import React, { useEffect, useState, useCallback, useMemo, useContext, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import {
    Page,
    Layout,
    Card,
    TextField,
    Select,
    Checkbox,
    Button,
    Text,
    Box,
    InlineStack,
    BlockStack,
    Banner,
    DropZone,
    Thumbnail,
    Badge,
    Tag,
    Modal,
    Toast,
    Spinner,
    Divider,
} from '@shopify/polaris';
import { PageAddIcon, DeleteIcon, PlusIcon, CalendarIcon, ClockIcon, ArrowLeftIcon } from '@shopify/polaris-icons';
import { useSafeAppBridge } from '../hooks/useSafeAppBridge';
import { useAppBridge } from '@shopify/app-bridge-react';
import { SaveBar } from '@shopify/app-bridge-react';
import createApp from '@shopify/app-bridge';
import { ResourcePicker } from '@shopify/app-bridge/actions';
import { AppBridgeConfigContext } from '../components/AppBridgeProvider';
import { SHOPIFY_API_KEY, buildNodeApiEntryUrl } from '../config/env';
import { campaignAPI } from '../services/campaignAPI';
import { createProductMetafield, updateProductMetafield, deleteProductMetafield, deleteCampaignCountMetafield, createOrUpdateCampaignCountMetafield } from '../helper/metafieldHelpers';
import { createOrUpdateRewardBundle, updateRewardVariantsInventoryTracking, updateMainProductVariantsInventoryTracking, uploadAndSetRewardImage, createMediaImageFromUrl, updateBundleProductImage, clearBundleCheckCache, updateBundleDescriptionOnly, updateBundlePriceOnly, updateBundleTitleOnly, updateBundleTitleAndDescription } from '../helper/bundleHelpers';
import { uploadImageToShopify } from '../services/shopifyGraphQL';
import { graphqlCall } from '../helper/Common';
import '../assets/index.css';
import PageTitleBar from '../components/PageTitleBar';
import { useSelector } from 'react-redux';
import { Redirect, History } from '@shopify/app-bridge/actions';
import { TIME_ZONE_OPTIONS, getInitialValues, INITIAL_VALUES } from '../helper/commonVariables';
import { decodeHtmlEntitiesDeep, decodeHtmlEntities, formatMoney } from '../helper/Common';

// Campaign type options - dynamic configuration
// To add new campaign types, simply add a new object to this array
const CAMPAIGN_TYPE_OPTIONS = [
    { value: '1', label: 'Crowdfunding', description: 'Raise funds from supporters who believe in your product before it launches. Set a funding goal and start collecting contributions.' },
    { value: '0', label: 'PreSales', description: 'Sell your product in advance and secure orders before production begins. Set your pre-sale limit and start taking early orders.' },
    { value: '2', label: 'Product Drop', description: 'Release a limited-edition product for a short window to create hype and fast sales. Set the drop start/end time and limit the available quantity.' }
];

// Helper function to get campaign type info dynamically
const getCampaignTypeInfo = (value) => {
    return CAMPAIGN_TYPE_OPTIONS.find(opt => opt.value === value) || null;
};

function CampaignEditContent() {
    const navigate = useNavigate();
    const location = useLocation();
    const { campaignId } = useParams();
    const formRef = useRef(null);
    const toastTimeoutRef = useRef(null);
    const isInitialLoadRef = useRef(true);
    const settingsLoadedRef = useRef(false); // Track if settings API has been called
    const campaignDataLoadedRef = useRef(null); // Track which campaignId was last loaded (null = not loaded yet)
    const config = useContext(AppBridgeConfigContext);
    const app = useSafeAppBridge();
    const shopify = useAppBridge(); // For SaveBar API access

    // Get install state from Redux
    const {
        checked: installChecked,
        success: installSuccess,
        loading: installLoading,
        installData,
        shop,
        host
    } = useSelector((state) => state.install);

    // UI State - All hooks must be called before any conditional returns
    const [loading, setLoading] = useState(true);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [productsChanged, setProductsChanged] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [originalData, setOriginalData] = useState(null); // Store original data for discard functionality
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track unsaved changes
    const [showNavigationModal, setShowNavigationModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const navigationBlockedRef = useRef(false);
    const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [showBanner, setShowBanner] = useState(false); // Show critical banner for validation errors
    const [bannerErrors, setBannerErrors] = useState([]); // Store validation errors for banner
    const [formikInitialValues, setFormikInitialValues] = useState(null); // Store initial values for Formik reinitialization
    const previousProductIdRef = useRef(null); // Track previous product ID for metafield management
    const previousMetafieldIdRef = useRef(null); // Track previous metafield ID for updates
    const previousCampaignTypeRef = useRef(null); // Track previous campaign type to detect type changes
    const [uploadingRewardImages, setUploadingRewardImages] = useState({}); // Track uploading state per reward tier
    const [uploadingBanner, setUploadingBanner] = useState(false); // Track uploading state for campaign banner
    // Save bar visibility is controlled by formik.dirty state via data-save-bar attribute
    const [saving, setSaving] = useState(false); // Track save operation loading state
    const [discarding, setDiscarding] = useState(false); // Track discard operation loading state
    const [apiSettings, setApiSettings] = useState(null); // Store settings from API
    const [storedCampaignId, setStoredCampaignId] = useState(null); // Store campaign ID after first save for new campaigns
    const [saveBarVisible, setSaveBarVisible] = useState(false); // Control save bar visibility explicitly
    const isNewCampaign = campaignId == null || campaignId === 'new';

    // App Bridge instance for ResourcePicker (Plain JavaScript API)
    // According to Shopify documentation: https://shopify.dev/docs/api/app-bridge/previous-versions/actions/resourcepicker#plain-javascript
    const appBridgeInstance = useMemo(() => {
        // Try to get config from context first
        let apiKey = config?.apiKey;
        let host = config?.host;

        // Fallback: Try to get from URL params if config is not available
        if (!host) {
            const urlParams = new URLSearchParams(window.location.search);
            host = urlParams.get('host');
        }

        // Fallback: Try to get from window.shopify if available
        if (!host && window.shopify?.config?.host) {
            host = window.shopify.config.host;
        }

        // Use API key from config or env
        if (!apiKey) {
            apiKey = SHOPIFY_API_KEY;
        }

        if (apiKey && host) {
            try {
                return createApp({
                    apiKey: apiKey,
                    host: host,
                });
            } catch (error) {
                console.error('Error creating App Bridge instance:', error);
                return null;
            }
        }

        return null;
    }, [config]);

    // Redirect on install failure (no HTML shown)
    useEffect(() => {
        if (installChecked && !installSuccess && !installData && shop) {
            const redirectUrl = buildNodeApiEntryUrl(shop, host);

            // Try App Bridge redirect first
            if (app) {
                try {
                    const redirect = Redirect.create(app);
                    redirect.dispatch(Redirect.Action.REMOTE, redirectUrl);
                    return;
                } catch (error) {
                    console.warn('[CampaignEdit] App Bridge redirect failed, trying fallback');
                }
            }

            // Fallback: window navigation
            try {
                if (window.top && window.top !== window) {
                    window.top.location.href = redirectUrl;
                } else {
                    window.location.href = redirectUrl;
                }
            } catch (error) {
                window.open(redirectUrl, '_blank');
            }
        }
    }, [installChecked, installSuccess, installData, shop, host, app]);


    // Formik form configuration
    // Use formikInitialValues state - starts as null, will be set when data loads
    // This ensures Formik only initializes with correct data (API data or defaults for new campaigns)
    const formik = useFormik({
        initialValues: formikInitialValues || getInitialValues(), // Use state if available, otherwise defaults (will be replaced)
        enableReinitialize: true, // Allow Formik to reinitialize when initialValues change - REQUIRED for proper dirty tracking
        validate: (values) => {
            const errors = {};

            if (!values.campaign_name.trim()) {
                errors.campaign_name = 'Campaign name is required';
            }

            if (!values.campaign_type) {
                errors.campaign_type = 'Campaign type is required';
            }

            if (values.campaign_type === '1') { // Crowdfunding
                if (!values.goal_type) {
                    errors.goal_type = 'Goal type is required';
                }
                if (values.goal_type === '0' && (!values.number_of_items || values.number_of_items <= 0)) {
                    errors.number_of_items = 'Number of items must be greater than 0';
                }
                if (values.goal_type === '1' && (!values.total_amount || values.total_amount <= 0)) {
                    errors.total_amount = 'Total amount must be greater than 0';
                }
            }

            // Validate all number fields are not negative
            if (values.number_of_items < 0) {
                errors.number_of_items = 'Number of items cannot be negative';
            }
            if (values.total_amount < 0) {
                errors.total_amount = 'Total amount cannot be negative';
            }
            if (values.initial_fund < 0) {
                errors.initial_fund = 'Initial fund cannot be negative';
            }
            if (values.initial_backers < 0) {
                errors.initial_backers = 'Initial backers cannot be negative';
            }
            if (values.initial_backers_count < 0) {
                errors.initial_backers_count = 'Initial backers count cannot be negative';
            }
            if (values.availableInventory !== null && values.availableInventory < 0) {
                errors.availableInventory = 'Available inventory cannot be negative';
            }
            if (values.enableProductLimitPerOrder && (values.productLimitPerOrder === null || values.productLimitPerOrder === undefined || values.productLimitPerOrder < 1)) {
                errors.productLimitPerOrder = 'Product limit per order must be at least 1';
            }
            if (values.perCustomerLimit && parseFloat(values.perCustomerLimit) < 0) {
                errors.perCustomerLimit = 'Per customer limit cannot be negative';
            }
            if (values.totalDropLimit && parseFloat(values.totalDropLimit) < 0) {
                errors.totalDropLimit = 'Total drop limit cannot be negative';
            }

            // Validate end date is not in the past based on selected timezone (for both Crowdfunding and Product Drop)
            if ((values.showEndDateCrowdfunding || values.showEndDate) && values.campaign_end_datetime && values.timeZone) {
                try {
                    // Parse end datetime
                    const [endDatePart, endTimePart] = values.campaign_end_datetime.split('T');
                    if (endDatePart && endTimePart) {
                        const [endYear, endMonth, endDay] = endDatePart.split('-').map(Number);
                        const [endHour, endMinute] = endTimePart.split(':').map(Number);
                        
                        // Get current time in the selected timezone
                        const now = new Date();
                        const formatter = new Intl.DateTimeFormat('en-US', {
                            timeZone: values.timeZone,
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: false
                        });
                        
                        const nowParts = formatter.formatToParts(now);
                        const nowYear = parseInt(nowParts.find(p => p.type === 'year').value);
                        const nowMonth = parseInt(nowParts.find(p => p.type === 'month').value);
                        const nowDay = parseInt(nowParts.find(p => p.type === 'day').value);
                        const nowHour = parseInt(nowParts.find(p => p.type === 'hour').value);
                        const nowMinute = parseInt(nowParts.find(p => p.type === 'minute').value);
                        
                        // Compare dates directly in the timezone
                        // Create date objects for comparison (using local time representation)
                        const endDateTime = new Date(endYear, endMonth - 1, endDay, endHour, endMinute);
                        const currentDateTime = new Date(nowYear, nowMonth - 1, nowDay, nowHour, nowMinute);
                        
                        // If end date is before or equal to current date/time in timezone, it's invalid
                        if (endDateTime.getTime() <= currentDateTime.getTime()) {
                            errors.campaign_end_datetime = 'End date and time must be in the future based on the selected timezone';
                        }
                    }
                } catch (error) {
                    console.error('Error validating end date:', error);
                    // Don't block save if validation fails, but log error
                }
            }

            // Validate start time is not greater than end time (for Product Drop only)
            if (values.campaign_type === '2' && values.campaign_start_datetime && values.campaign_end_datetime && values.timeZone) {
                try {
                    // Only validate if end date is actually set (checkbox is checked)
                    const isEndDateSet = values.showEndDate;
                    
                    if (isEndDateSet) {
                        // Helper function to convert datetime to UTC (inline version for validation)
                        const convertToUTCInline = (dateTimeString, timezone) => {
                            if (!dateTimeString || !timezone) return null;
                            try {
                                const [datePart, timePart] = dateTimeString.split('T');
                                if (!datePart || !timePart) return null;
                                const [year, month, day] = datePart.split('-').map(Number);
                                const [hours, minutes] = timePart.split(':').map(Number);
                                let testDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
                                const formatter = new Intl.DateTimeFormat('en-US', {
                                    timeZone: timezone,
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    hour12: false
                                });
                                let parts = formatter.formatToParts(testDate);
                                let tzYear = parseInt(parts.find(p => p.type === 'year').value);
                                let tzMonth = parseInt(parts.find(p => p.type === 'month').value) - 1;
                                let tzDay = parseInt(parts.find(p => p.type === 'day').value);
                                let tzHour = parseInt(parts.find(p => p.type === 'hour').value);
                                let tzMinute = parseInt(parts.find(p => p.type === 'minute').value);
                                let iterations = 0;
                                const maxIterations = 10;
                                while ((tzYear !== year || tzMonth !== month - 1 || tzDay !== day || tzHour !== hours || tzMinute !== minutes) && iterations < maxIterations) {
                                    const desiredTime = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
                                    const actualTime = new Date(Date.UTC(tzYear, tzMonth, tzDay, tzHour, tzMinute, 0));
                                    const diffMs = desiredTime.getTime() - actualTime.getTime();
                                    testDate = new Date(testDate.getTime() + diffMs);
                                    parts = formatter.formatToParts(testDate);
                                    tzYear = parseInt(parts.find(p => p.type === 'year').value);
                                    tzMonth = parseInt(parts.find(p => p.type === 'month').value) - 1;
                                    tzDay = parseInt(parts.find(p => p.type === 'day').value);
                                    tzHour = parseInt(parts.find(p => p.type === 'hour').value);
                                    tzMinute = parseInt(parts.find(p => p.type === 'minute').value);
                                    iterations++;
                                }
                                return testDate;
                            } catch (error) {
                                return null;
                            }
                        };

                        // Convert both start and end datetimes to UTC for comparison
                        const startDateTimeUTC = convertToUTCInline(values.campaign_start_datetime, values.timeZone);
                        const endDateTimeUTC = convertToUTCInline(values.campaign_end_datetime, values.timeZone);
                        
                        if (startDateTimeUTC && endDateTimeUTC) {
                            // If start time is greater than or equal to end time, it's invalid
                            if (startDateTimeUTC.getTime() >= endDateTimeUTC.getTime()) {
                                errors.campaign_end_datetime = 'End date and time must be after start date and time';
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error validating start/end time comparison:', error);
                    // Don't block save if validation fails, but log error
                }
            }

            return errors;
        },
        onSubmit: async (values, { setSubmitting }) => {
            await handleSave(values);
            setSubmitting(false);
        }
    });

    // Helper function to show toast messages using Polaris Toast
    const showToastMessage = useCallback((message, isError = false) => {
        // Clear any existing timeout
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }

        setToastMessage(message);
        setShowToast(true);

        // Auto-hide toast after 5 seconds
        toastTimeoutRef.current = setTimeout(() => {
            setShowToast(false);
            toastTimeoutRef.current = null;
        }, 5000);
    }, []);

    // Helper function to decode HTML entities for preview text
    const decodeForPreview = useCallback((text) => {
        if (typeof text !== 'string') return text;
        return decodeHtmlEntities(text);
    }, []);

    // Load settings from API for preview styling - matches Settings.jsx structure
    // OPTIMIZATION: Ensure settings are properly loaded and decoded for preview
    const previewSettings = useMemo(() => {
        // Priority: API settings > localStorage > defaults
        let settingsToUse = null;
        
        // Get defaults based on campaign type
        const getDefaults = () => {
            if (formik.values?.campaign_type === '1') return INITIAL_VALUES.crowdfunding;
            if (formik.values?.campaign_type === '0') return INITIAL_VALUES.presales;
            if (formik.values?.campaign_type === '2') return INITIAL_VALUES.productDrop;
            return INITIAL_VALUES.crowdfunding;
        };
        
        const defaults = getDefaults();
        
        // First, try API settings (already decoded when loaded from API)
        if (apiSettings) {
            try {
                // Settings are already decoded when loaded from API, so use directly
                // Return settings based on campaign type, merged with defaults
                if (formik.values?.campaign_type === '1') {
                    settingsToUse = { ...defaults, ...apiSettings.crowdfunding };
                } else if (formik.values?.campaign_type === '0') {
                    settingsToUse = { ...defaults, ...apiSettings.presales };
                } else if (formik.values?.campaign_type === '2') {
                    settingsToUse = { ...defaults, ...apiSettings.productDrop };
                }
                
            } catch (error) {
                console.error('Error processing API settings:', error);
            }
        }
        
        // Fallback to localStorage if API settings not available
        if (!settingsToUse) {
            try {
                const stored = localStorage.getItem('campaign-settings');
                if (stored) {
                    const loadedSettings = JSON.parse(stored);
                    const decodedLoaded = decodeHtmlEntitiesDeep(loadedSettings);
                    // Return settings based on campaign type, merged with defaults
                    if (formik.values?.campaign_type === '1') {
                        settingsToUse = { ...defaults, ...decodedLoaded.crowdfunding };
                    } else if (formik.values?.campaign_type === '0') {
                        settingsToUse = { ...defaults, ...decodedLoaded.presales };
                    } else if (formik.values?.campaign_type === '2') {
                        settingsToUse = { ...defaults, ...decodedLoaded.productDrop };
                    }
                    
                }
            } catch (error) {
                console.error('Error loading preview settings from localStorage:', error);
            }
        }
        
        // Final fallback to defaults
        return settingsToUse || defaults;
    }, [formik.values?.campaign_type, apiSettings]);

    // Helper function to normalize datetime format from API (MySQL format: YYYY-MM-DD HH:mm:ss) to form format (YYYY-MM-DDTHH:mm)
    const normalizeDateTimeFormat = useCallback((dateTimeString) => {
        if (!dateTimeString) return '';
        
        try {
            // Handle MySQL format: "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD HH:mm:ss.000000"
            if (dateTimeString.includes(' ')) {
                // Replace space with T and remove seconds/milliseconds
                const normalized = dateTimeString.split(' ').join('T').split('.')[0];
                // Remove seconds if present (format: YYYY-MM-DDTHH:mm:ss -> YYYY-MM-DDTHH:mm)
                // But ensure we have at least date and time parts
                const [datePart, timePart] = normalized.split('T');
                if (datePart && timePart) {
                    const timeParts = timePart.split(':');
                    if (timeParts.length >= 2) {
                        // Return format: YYYY-MM-DDTHH:mm (preserve hours and minutes)
                        // Ensure time is always in HH:mm format (pad if needed)
                        const hours = timeParts[0].padStart(2, '0');
                        const minutes = timeParts[1].padStart(2, '0');
                        return `${datePart}T${hours}:${minutes}`;
                    } else if (timeParts.length === 1) {
                        // Only hours, add minutes
                        const hours = timeParts[0].padStart(2, '0');
                        return `${datePart}T${hours}:00`;
                    }
                }
                // If no time part, add default time 00:00
                if (datePart && !timePart) {
                    return `${datePart}T00:00`;
                }
                return normalized;
            }
            
            // Handle ISO format: "YYYY-MM-DDTHH:mm:ss" or "YYYY-MM-DDTHH:mm:ss.000Z"
            if (dateTimeString.includes('T')) {
                // Remove seconds/milliseconds and timezone
                const withoutTz = dateTimeString.split('.')[0].split('Z')[0].split('+')[0];
                const [datePart, timePart] = withoutTz.split('T');
                if (datePart && timePart) {
                    const timeParts = timePart.split(':');
                    if (timeParts.length >= 2) {
                        // Return format: YYYY-MM-DDTHH:mm (preserve hours and minutes)
                        // Ensure time is always in HH:mm format (pad if needed)
                        const hours = timeParts[0].padStart(2, '0');
                        const minutes = timeParts[1].padStart(2, '0');
                        return `${datePart}T${hours}:${minutes}`;
                    } else if (timeParts.length === 1) {
                        // Only hours, add minutes
                        const hours = timeParts[0].padStart(2, '0');
                        return `${datePart}T${hours}:00`;
                    }
                }
                // If no time part, add default time 00:00
                if (datePart && !timePart) {
                    return `${datePart}T00:00`;
                }
                return withoutTz;
            }
            
            // If only date part (YYYY-MM-DD), add default time
            if (dateTimeString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                return `${dateTimeString}T00:00`;
            }
            
            // If already in correct format or unknown format, return as-is
            return dateTimeString;
        } catch (error) {
            console.error('Error normalizing datetime format:', error);
            return dateTimeString;
        }
    }, []);

    // Helper function to transform API response to form data - extracted for reuse
    const transformApiDataToFormData = useCallback((apiData) => {
        const campaignData = apiData.campaign;
        if (!campaignData) {
            throw new Error('Campaign data not found in response');
        }

        // Normalize datetime formats from API
        const normalizedStartDateTime = normalizeDateTimeFormat(campaignData.campaign_start_datetime);
        const normalizedEndDateTime = normalizeDateTimeFormat(campaignData.campaign_end_datetime);

        return {
            // Campaign data from API - exact field names
            id: campaignData.id,
            client_id: campaignData.client_id,
            campaign_name: campaignData.campaign_name,
            campaign_description: campaignData.campaign_description,
            campaign_banner_link: campaignData.campaign_banner_link,
            campaign_type: campaignData.campaign_type,
            campaign_status: campaignData.campaign_status,
            goal_type: campaignData.goal_type,
            number_of_items: campaignData.number_of_items,
            total_amount: parseFloat(campaignData.total_amount) || 0,
            is_auto_end_campaign: campaignData.is_auto_end_campaign || '0',
            is_add_start_point: campaignData.is_add_start_point,
            initial_fund: parseFloat(campaignData.initial_fund) || 0,
            initial_backers: parseInt(campaignData.initial_backers) || 0,
            duration_type: campaignData.duration_type,
            campaign_start_datetime: normalizedStartDateTime,
            campaign_end_datetime: normalizedEndDateTime,
            timezone: campaignData.timezone,
            is_limit_inventory: campaignData.is_limit_inventory,
            available_inventory: campaignData.available_inventory,
            is_show_presale_target: campaignData.is_show_presale_target,
            product_drop_per_customer_limit: campaignData.product_drop_per_customer_limit,
            product_drop_total_limit: campaignData.product_drop_total_limit,
            order_tags: campaignData.order_tags,
            customer_tags: campaignData.customer_tags,
            current_funding: parseFloat(campaignData.current_funding) || 0,
            current_backers: parseInt(campaignData.current_backers) || 0,
            current_items_sold: parseInt(campaignData.current_items_sold) || 0,
            initial_items_sold_preSale: parseInt(campaignData.initial_items_sold_preSale) || 0,
            initial_backers_count: campaignData.Initial_backers_count != null ? parseInt(campaignData.Initial_backers_count) || 0 : 0,
            is_deleted: campaignData.is_deleted,
            created_at: campaignData.created_at,
            updated_at: campaignData.updated_at,

            // Parse product_id from campaign data to create products array (only id, name, image)
            // Backend stores product_id as JSON string: {"id": 123, "name": "...", "image": "..."}
            products: (() => {
                try {
                    let productId = campaignData.product_id;

                    // Parse JSON string if needed
                    if (typeof productId === 'string') {
                        productId = JSON.parse(productId);
                    }

                    if (productId && productId.id) {
                        return [{
                            shopify_product_id: `gid://shopify/Product/${productId.id}`,
                            product_title: productId.name || '',
                            product_image: productId.image || '',
                            // Prioritize campaignData.product_handle, but also check productId.handle as fallback
                            product_handle: campaignData.product_handle || productId.handle || null,
                            is_primary_product: '1',
                            is_active: '1'
                        }];
                    }
                    return [];
                } catch (error) {
                    console.error('Error parsing product_id:', error);
                    return [];
                }
            })(),
            // Parse rewards from campaigns.rewards JSON field
            rewards: (() => {
                try {
                    // Rewards can come from:
                    // 1. apiData.rewards (if backend already parsed JSON and returned separately)
                    // 2. campaignData.rewards (if it's stored as JSON string in campaign object)
                    let rewardsData = apiData.rewards || campaignData.rewards || null;
                    
                    // If rewards is in campaign object as JSON string, parse it
                    if (!rewardsData && campaignData.rewards) {
                        rewardsData = campaignData.rewards;
                    }
                    
                    // Parse JSON string if needed
                    if (typeof rewardsData === 'string') {
                        rewardsData = JSON.parse(rewardsData);
                    }
                    
                    // Ensure it's an array
                    if (!Array.isArray(rewardsData)) {
                        rewardsData = [];
                    }
                    
                    // Helper function to convert numeric IDs to GID format
                    const toGid = (id, type = 'Product') => {
                        if (!id && id !== 0) return null;
                        // If already in GID format, return as is
                        if (typeof id === 'string' && id.includes('gid://')) return id;
                        // Convert numeric ID (number or string number) to GID format
                        const numericId = typeof id === 'number' ? id : parseInt(id);
                        if (isNaN(numericId)) return null;
                        return `gid://shopify/${type}/${numericId}`;
                    };
                    
                    return rewardsData.map(reward => {
                        // Process products array
                        const products = (reward.products || []).map(product => {
                            // Handle variant_ids - can be numeric array or GID array
                            let variantIds = product.variant_ids || [];
                            if (!Array.isArray(variantIds)) {
                                variantIds = [];
                            }
                            
                            // Convert variant_ids to GID format
                            const selectedVariants = variantIds
                                .map(id => toGid(id, 'ProductVariant'))
                                .filter(Boolean);
                            
                            // Handle variants array - convert IDs to GID format
                            let variants = product.variants || [];
                            if (!Array.isArray(variants)) {
                                variants = [];
                            }
                            
                            // Convert variant objects to GID format
                            variants = variants.map(variant => ({
                                ...variant,
                                id: toGid(variant.id, 'ProductVariant'), // Convert to GID format
                                // Keep other fields as is
                                title: variant.title || '',
                                price: variant.price || 0,
                                available: variant.available !== false
                            }));
                            
                            // If selectedVariants is empty but variants exist, use all variants
                            const finalSelectedVariants = selectedVariants.length > 0 
                                ? selectedVariants 
                                : variants.map(v => v.id).filter(Boolean);
                            
                            return {
                                ...product,
                                shopify_product_id: toGid(product.shopify_product_id, 'Product') || '',
                                shopify_variant_id: finalSelectedVariants.length > 0 ? finalSelectedVariants[0] : '',
                                variant_ids: variantIds, // Keep original numeric IDs for reference
                                variants: variants, // Variants with GID format IDs
                                selectedVariants: finalSelectedVariants, // Selected variant IDs in GID format
                                // IMPORTANT: Preserve product_handle - don't lose it during transformation
                                product_handle: product.product_handle || product.handle || null
                            };
                        });
                        
                        return {
                            id: reward.id || null,
                            reward_title: reward.reward_title || '',
                            reward_description: reward.reward_description || '',
                            reward_image: reward.reward_image || '',
                            quantity_limit: reward.quantity_limit || null,
                            reward_price: reward.reward_price || '',
                            is_active: reward.is_active || '1',
                            bundle_product_id: reward.bundle_product_id || null, // Can be GID format or null
                            bundle_product_handle: reward.bundle_product_handle || null, // Bundle product handle
                            products: products,
                            show_strikethrough_price: reward.show_strikethrough_price || false
                        };
                    });
                } catch (error) {
                    console.error('Error parsing rewards from JSON:', error);
                    return [];
                }
            })(),

            // Counts from API
            rewards_count: apiData.rewards_count || 0,

            // UI helper fields - dynamically determined from campaign data
            // Show end date checkbox for Crowdfunding if end datetime exists
            showEndDateCrowdfunding: campaignData.campaign_type === '1' && campaignData.campaign_end_datetime != null && campaignData.campaign_end_datetime !== '',
            // Show end date checkbox for Product Drop if end datetime exists
            showEndDate: campaignData.campaign_type === '2' && campaignData.campaign_end_datetime != null && campaignData.campaign_end_datetime !== '',
            // Add start point checkbox - convert '1'/'0' to boolean
            // If is_add_start_point is '1', set addStartPoint to true
            // Also check if initial_backers or initial_fund has a value (for backward compatibility)
            addStartPoint: campaignData.is_add_start_point === '1' || 
                          campaignData.is_add_start_point === 1 || 
                          campaignData.is_add_start_point === true ||
                          (campaignData.initial_backers && parseInt(campaignData.initial_backers) > 0) ||
                          (campaignData.initial_fund && parseFloat(campaignData.initial_fund) > 0),

            // UI state fields for form management
            orderTagInput: '',
            customerTagInput: '',
            timeZone: campaignData.timezone || 'UTC',
            perCustomerLimit: campaignData.product_drop_per_customer_limit?.toString() || '1',
            totalDropLimit: campaignData.product_drop_total_limit?.toString() || '',
            limitInventory: campaignData.is_limit_inventory === '1' || campaignData.is_limit_inventory === 1 || campaignData.is_limit_inventory === true, // Convert to boolean
            availableInventory: parseInt(campaignData.available_inventory),
            enableProductLimitPerOrder: campaignData.product_limit_per_order ? true : false,
            productLimitPerOrder: campaignData.product_limit_per_order ? parseInt(campaignData.product_limit_per_order) : null,
            showPresalesTarget: campaignData.is_show_presale_target === '1' || campaignData.is_show_presale_target === 1,
            orderTags: campaignData.order_tags ? campaignData.order_tags.split(',').filter(tag => tag.trim()) : [],
            customerTags: campaignData.customer_tags ? campaignData.customer_tags.split(',').filter(tag => tag.trim()) : []
        };
    }, [normalizeDateTimeFormat]);

    // Load campaign data when ID is provided
    const loadCampaignData = async () => {
        if (campaignId && campaignId !== 'new') {
            setLoading(true);
            try {
                // Use campaignAPI service which handles response structure
                const response = await campaignAPI.getCampaign(campaignId);

                // Backend returns { success: true, message: string, data: { campaign: object, rewards: array } }
                if (!response.success || !response.data) {
                    throw new Error(response.message || 'Campaign not found');
                }

                // Transform API data to form data
                const formData = transformApiDataToFormData(response.data);
                
                // Store original data for discard functionality
                setOriginalData(formData);
                
                // Track previous product ID for metafield management (only track, don't fetch on load)
                const primaryProduct = (formData.products || [])[0];
                if (primaryProduct?.shopify_product_id) {
                    previousProductIdRef.current = primaryProduct.shopify_product_id;
                    // Don't fetch metafields on load - use live data from API response
                    // Metafields will be created/updated on save
                }
                
                // Track previous campaign type to detect type changes
                if (formData.campaign_type) {
                    previousCampaignTypeRef.current = formData.campaign_type;
                }
                
                // Use live form values directly from API - no need to fetch metafields
                
                // Update Formik initial values state - this will trigger reinitialize due to enableReinitialize: true
                // This ensures the form renders with API data, not default values
                setFormikInitialValues(formData);
                
                // Also use resetForm to immediately update formik.values and reset dirty state
                // Create a deep copy to ensure Formik detects changes (immutable pattern)
                const immutableFormData = {
                    ...formData,
                    products: formData.products ? [...formData.products] : []
                };
                formik.resetForm({ 
                    values: immutableFormData,
                    initialValues: immutableFormData // Explicitly set initialValues so Formik knows these are the baseline
                });
                
                setDataLoaded(true);
                setHasUnsavedChanges(false);
                setProductsChanged(false);
                setSaveBarVisible(false); // Hide save bar on initial load
                // Reset initial load ref for existing campaigns
                isInitialLoadRef.current = true;
                
                // Allow change detection to resume after form is initialized
                setTimeout(() => {
                    isInitialLoadRef.current = false;
                }, 300);
            } catch (error) {
                console.error('Error loading campaign:', error);
                // Show error toast using optimized showToastMessage
                const errorMessage = error.message || 'Failed to load campaign data';
                showToastMessage(errorMessage, true);
                
                // Don't set default values on error - keep form in error state
                // Only mark as loaded so we can show error UI, but don't reset formik values
                setDataLoaded(true);
            } finally {
                setLoading(false);
            }
        } else {
            // For new campaigns ONLY, set default values and mark as loaded
            // This branch only executes when campaignId is null or 'new'
            const defaultValues = getInitialValues();
            setOriginalData(null); // No original data for new campaigns
            
            // Update Formik initial values state - this will trigger reinitialize
            setFormikInitialValues(defaultValues);
            
            // Also use resetForm to immediately update formik.values and reset dirty state
            // Create a deep copy to ensure Formik detects changes (immutable pattern)
            const immutableDefaultValues = {
                ...defaultValues,
                products: defaultValues.products ? [...defaultValues.products] : []
            };
            formik.resetForm({ 
                values: immutableDefaultValues,
                initialValues: immutableDefaultValues // Explicitly set initialValues so Formik knows these are the baseline
            });
            setDataLoaded(true);
            setLoading(false);
            setHasUnsavedChanges(true); // New campaigns always have changes
            setProductsChanged(false);
            setSaveBarVisible(false); // Hide save bar on initial load (will show when user makes changes)
            // Reset initial load ref for new campaigns
            isInitialLoadRef.current = true;
            
            // Allow change detection to resume after form is initialized
            setTimeout(() => {
                isInitialLoadRef.current = false;
            }, 300);
        }
    };

    // Handle browser navigation (back/forward/close) when there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    // Intercept browser back/forward buttons - always redirect to /app
    useEffect(() => {
        const handlePopState = (e) => {
            // Prevent navigation by immediately pushing state back
            // This cancels the navigation
            window.history.pushState(null, '', location.pathname + location.search);
            
            if (hasUnsavedChanges && !navigationBlockedRef.current) {
                // If there are unsaved changes, show confirmation modal
                setShowNavigationModal(true);
                setPendingNavigation(() => () => {
                    // Allow navigation after user confirms
                    navigationBlockedRef.current = true;
                    // Always navigate to /app instead of previous page
                    const path = '/app';
                    // Update parent window URL using App Bridge History
                    if (app) {
                        try {
                            const history = History.create(app);
                            history.dispatch(History.Action.PUSH, path);
                        } catch (error) {
                            console.warn('[CampaignEdit] App Bridge History failed, using react-router:', error);
                        }
                    }
                    // Navigate to /app
                    navigate(path);
                });
            } else {
                // No unsaved changes - directly navigate to /app
                navigationBlockedRef.current = false; // Reset flag
                const path = '/app';
                // Update parent window URL using App Bridge History
                if (app) {
                    try {
                        const history = History.create(app);
                        history.dispatch(History.Action.PUSH, path);
                    } catch (error) {
                        console.warn('[CampaignEdit] App Bridge History failed, using react-router:', error);
                    }
                }
                // Navigate to /app
                navigate(path);
            }
        };

        // Push a state to enable popstate detection
        // This creates an entry in history so we can detect back button
        const currentUrl = location.pathname + location.search;
        if (window.location.pathname + window.location.search !== currentUrl) {
            window.history.pushState(null, '', currentUrl);
        }

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [hasUnsavedChanges, location.pathname, location.search, navigate, app]);


    // Sync URL with App Bridge History when route changes
    useEffect(() => {
        if (!app || !installChecked || !installData) return;

        try {
            const history = History.create(app);
            // Update parent window URL to match current route
            history.dispatch(History.Action.REPLACE, location.pathname);
        } catch (error) {
            console.warn('[CampaignEdit] App Bridge History sync failed:', error);
        }
    }, [location.pathname, app, installChecked, installData]);

    // OPTIMIZATION: Load settings from API - only called during initial load
    const loadSettings = useCallback(async () => {
        if (!installChecked || !installData) return;
        
        // Prevent duplicate calls - check if already loaded
        if (settingsLoadedRef.current) return;
        settingsLoadedRef.current = true;
        
        try {
            const clientId = installData?.shop_info?.id || 1;
            const response = await campaignAPI.getSettings(clientId);
            
            if (response.success && response.data) {
                const apiData = response.data;
                
                // Structure settings similar to Settings.jsx format
                // Decode HTML entities when loading from API (same as Settings.jsx)
                const settingsData = {
                    crowdfunding: apiData.crowdfunding_settings ? decodeHtmlEntitiesDeep(apiData.crowdfunding_settings) : {},
                    presales: apiData.presales_settings ? decodeHtmlEntitiesDeep(apiData.presales_settings) : {},
                    productDrop: apiData.product_drop_settings ? decodeHtmlEntitiesDeep(apiData.product_drop_settings) : {}
                };
                
                setApiSettings(settingsData);
                
                // Also update localStorage as backup
                try {
                    localStorage.setItem('campaign-settings', JSON.stringify(settingsData));
                } catch (storageError) {
                    console.warn('Failed to save settings to localStorage:', storageError);
                }
            }
        } catch (error) {
            console.warn('Failed to load settings from API, will use localStorage/defaults:', error);
            // Don't throw - allow fallback to localStorage/defaults
            // Reset ref on error so it can be retried if needed
            settingsLoadedRef.current = false;
        }
    }, [installChecked, installData]);

    // Reset stored campaign ID when starting a truly new campaign
    useEffect(() => {
        if (campaignId == null || campaignId === 'new') {
            // Reset stored campaign ID when creating a new campaign
            setStoredCampaignId(null);
            // Reset campaign data loaded ref when switching to new campaign
            // This ensures new campaign data will be loaded
            campaignDataLoadedRef.current = null;
            // Reset previous campaign type ref for new campaigns
            previousCampaignTypeRef.current = null;
        }
    }, [campaignId]); // Only depend on campaignId to detect when switching to new campaign

    // OPTIMIZATION: Initial load - run Install (via useEnsureInstall), campaign details, and settings in parallel
    // Only runs once on page refresh - no GraphQL calls during load
    useEffect(() => {
        if (!installChecked || !installData) return;
        
        // Prevent duplicate calls for the same campaignId
        const currentCampaignId = campaignId || 'new';
        const lastLoadedCampaignId = campaignDataLoadedRef.current;
        
        // Only load if:
        // 1. We haven't loaded data yet (lastLoadedCampaignId is null), OR
        // 2. The campaignId has changed (user navigated to different campaign)
        if (lastLoadedCampaignId === null || lastLoadedCampaignId !== currentCampaignId) {
            campaignDataLoadedRef.current = currentCampaignId;
            
            // OPTIMIZATION: Run campaign data and settings in parallel for faster load
            const loadInitialData = async () => {
                const promises = [];
                
                // Load campaign data (only if not new campaign)
                if (currentCampaignId !== 'new') {
                    promises.push(loadCampaignData());
                } else {
                    // For new campaigns, just set default values immediately
                    const defaultValues = getInitialValues();
                    setOriginalData(null);
                    setFormikInitialValues(defaultValues);
                    const immutableDefaultValues = {
                        ...defaultValues,
                        products: defaultValues.products ? [...defaultValues.products] : []
                    };
                    formik.resetForm({ 
                        values: immutableDefaultValues,
                        initialValues: immutableDefaultValues
                    });
                    setDataLoaded(true);
                    setLoading(false);
                    setHasUnsavedChanges(true);
                    setProductsChanged(false);
                    setSaveBarVisible(false);
                    isInitialLoadRef.current = true;
                    setTimeout(() => {
                        isInitialLoadRef.current = false;
                    }, 300);
                }
                
                // Load settings in parallel (only if not already loaded)
                if (!settingsLoadedRef.current) {
                    promises.push(loadSettings());
                }
                
                // Wait for all initial APIs to complete
                await Promise.allSettled(promises);
            };
            
            loadInitialData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId, installChecked, installData?.shop_info?.id]); // Only depend on install check and clientId

    // Cleanup toast timeout on unmount
    useEffect(() => {
        return () => {
            if (toastTimeoutRef.current) {
                clearTimeout(toastTimeoutRef.current);
            }
        };
    }, []);

    // Track unsaved changes for navigation blocking - optimized
    // Use formik.dirty as primary indicator, only do deep comparison when needed
    const hasFormChanges = useMemo(() => {
        if (!dataLoaded || isInitialLoadRef.current || !formik.values) return false;
        if (!originalData) return true; // New campaigns always have changes
        // Only do expensive comparison if formik thinks form is dirty
        if (!formik.dirty) return false;
        // Deep comparison only when formik.dirty is true (optimization)
        return JSON.stringify(formik.values) !== JSON.stringify(originalData);
    }, [formik.values, formik.dirty, originalData, dataLoaded]);

    useEffect(() => {
        setHasUnsavedChanges(hasFormChanges);
    }, [hasFormChanges]);

    // Control save bar visibility - only show after initial load and when there are actual changes
    useEffect(() => {
        // Don't show save bar during initial load or if data not loaded
        if (!dataLoaded || isInitialLoadRef.current) {
            setSaveBarVisible(false);
            return;
        }

        // Check if there are actual changes (formik.dirty or productsChanged)
        const hasChanges = formik.dirty || productsChanged;
        
        // For new campaigns, only show if there are actual changes
        if (isNewCampaign) {
            // For new campaigns, show save bar only if user has made changes
            setSaveBarVisible(hasChanges);
            return;
        }

        // For existing campaigns, check if values actually differ from original
        if (hasChanges && originalData) {
            // Use the same logic as hasFormChanges but also check productsChanged
            const valuesChanged = formik.dirty && JSON.stringify(formik.values) !== JSON.stringify(originalData);
            setSaveBarVisible(valuesChanged || productsChanged);
        } else {
            setSaveBarVisible(false);
        }
    }, [formik.dirty, productsChanged, dataLoaded, originalData, isNewCampaign, formik.values]);

    // Reset initial values when goal type changes - optimized
    // Only reset when user actually changes goal_type, not on initial load
    useEffect(() => {
        if (!formik.values || formik.values.campaign_type !== '1') return;
        if (isInitialLoadRef.current || !dataLoaded) return; // Don't reset on initial load
        
        // When goal type changes, reset the opposite field
        if (formik.values.goal_type === '1') {
            // Total Amount goal type - reset initial_backers
            formik.setFieldValue('initial_backers', 0);
        } else {
            // Number of items goal type - reset initial_fund
            formik.setFieldValue('initial_fund', 0);
        }
    }, [formik.values?.goal_type, formik.values?.campaign_type, dataLoaded]);

    // Monitor products array changes and ensure Formik detects them for save bar
    // This useEffect ensures that when products change, Formik's dirty state is properly updated
    useEffect(() => {
        if (!dataLoaded || isInitialLoadRef.current) return;
        
        // Compare current products with initial products to ensure Formik recognizes changes
        const currentProducts = formik.values.products || [];
        const initialProducts = formik.initialValues.products || [];
        
        // Use JSON.stringify for deep comparison since Formik uses shallow equality
        const currentProductsStr = JSON.stringify(currentProducts);
        const initialProductsStr = JSON.stringify(initialProducts);
        
        // Update productsChanged state based on deep comparison
        if (currentProductsStr !== initialProductsStr) {
            // Products have changed - ensure productsChanged is true
            if (!productsChanged) {
                setProductsChanged(true);
            }
            // If products have changed but formik.dirty is false, force Formik to recognize the change
            if (!formik.dirty) {
                // Force Formik to recalculate dirty state by setting a temporary field
                formik.setFieldValue('_productsChanged', Date.now(), false);
                // Clean up the temporary field immediately
                setTimeout(() => {
                    formik.setFieldValue('_productsChanged', undefined, false);
                }, 0);
            }
        } else {
            // Products match initial values - ensure productsChanged is false
            if (productsChanged) {
                setProductsChanged(false);
            }
        }
    }, [formik.values.products, formik.initialValues.products, formik.dirty, dataLoaded, formik, productsChanged]);

    // Note: Time remaining is calculated on each render via calculateTimeRemaining()
    // No need for interval since it's recalculated when formik values change

    // Reset tags when campaign type changes - optimized
    // Only reset when user actually changes campaign type, not on initial load
    useEffect(() => {
        if (!formik.values?.campaign_type) return;
        if (isInitialLoadRef.current || !dataLoaded) return; // Don't reset on initial load
        
        // Only reset if campaign type actually changed (not just set for first time)
        const currentCampaignType = formik.values.campaign_type;
        const previousCampaignType = previousCampaignTypeRef.current;
        
        // Only reset if there was a previous campaign type and it's different
        // This ensures tags loaded from API are preserved when editing existing campaigns
        if (previousCampaignType !== null && previousCampaignType !== currentCampaignType) {
            formik.setFieldValue('orderTags', []);
            formik.setFieldValue('customerTags', []);
            formik.setFieldValue('orderTagInput', '');
            formik.setFieldValue('customerTagInput', '');
        }
    }, [formik.values?.campaign_type]);


    // Product selection handlers - using ResourcePicker Plain JavaScript API
    // According to Shopify documentation: https://shopify.dev/docs/api/app-bridge/previous-versions/actions/resourcepicker#plain-javascript
    const handleProductSelect = useCallback(async () => {
        if (!appBridgeInstance) {
            console.error('App Bridge instance not available');
            // Show user-friendly error message
            showToastMessage('Unable to open product picker. Please ensure you are accessing this app from within Shopify Admin.', true);
            return;
        }

        try {
            // Create ResourcePicker instance
            const productPicker = ResourcePicker.create(appBridgeInstance, {
                resourceType: ResourcePicker.ResourceType.Product,
                options: {
                    selectMultiple: false,
                    showVariants: false,
                    showArchived: false,
                    initialSelectionIds: (formik.values.products?.length || 0) > 0
                        ? [{
                            id: formik.values.products[0].shopify_product_id
                        }]
                        : []
                }
            });

            // Subscribe to selection
            productPicker.subscribe(ResourcePicker.Action.SELECT, (selectPayload) => {
                const selection = selectPayload.selection || [];

                if (selection.length > 0) {
                    const product = selection[0]; // Take only the first selected product
                    const newProduct = {
                        shopify_product_id: product.id,
                        product_title: product.title,
                        product_handle: product.handle,
                        product_image: product.images?.[0]?.originalSrc || '',
                        is_primary_product: '1',
                        is_active: '1'
                    };

                    // Replace all selected products with the new single selection
                    // Create a NEW array reference (immutable update) so Formik detects the change
                    // This ensures Formik's shallow equality check will detect the difference
                    const newProductsArray = [newProduct];
                    
                    // Use setFieldValue to update - Formik will compare new array reference with initialValues
                    formik.setFieldValue('products', newProductsArray, false);
                    formik.setFieldTouched('products', true, false);
                    
                    // Mark products as changed to ensure save bar appears
                    setProductsChanged(true);
                }

                // Cleanup
                productPicker.unsubscribe();
            });

            // Subscribe to cancellation
            productPicker.subscribe(ResourcePicker.Action.CANCEL, () => {
                // Cleanup
                productPicker.unsubscribe();
            });

            // Open the picker
            productPicker.dispatch(ResourcePicker.Action.OPEN);
        } catch (error) {
            console.error('Error opening product picker:', error);
            showToastMessage('Failed to open product picker. Please try again.', true);
        }
    }, [appBridgeInstance, formik, showToastMessage]);

    const handleRewardTierProductSelect = useCallback(async (tierId) => {
        if (!appBridgeInstance) {
            console.error('App Bridge instance not available');
            showToastMessage('Unable to open product picker. Please ensure you are accessing this app from within Shopify Admin.', true);
            return;
        }

        try {
            // Get current products for this tier to show as initial selection
            const currentTier = (formik.values.rewards || []).find(tier => tier.id === tierId);
            
            // Build initial selection according to Shopify ResourcePicker format
            // Format: [{ id: 'gid://shopify/Product/123', variants: [{ id: 'gid://shopify/ProductVariant/1' }] }]
            // If no variants specified, all variants will be selected
            const initialSelectionIds = [];
            if (currentTier?.products?.length > 0) {
                currentTier.products.forEach(product => {
                    const productId = product.shopify_product_id;
                    if (!productId) return;
                    
                    // Get selected variants for this product
                    const selectedVariants = product.selectedVariants || [];
                    
                    // Build product selection object according to Shopify format
                    if (selectedVariants.length > 0) {
                        // Product with specific variants selected
                        initialSelectionIds.push({
                            id: productId,
                            variants: selectedVariants.map(variantId => ({ id: variantId }))
                        });
                    } else if (product.shopify_variant_id) {
                        // Fallback: single variant selected (legacy format)
                        initialSelectionIds.push({
                            id: productId,
                            variants: [{ id: product.shopify_variant_id }]
                        });
                    } else {
                        // Product with all variants selected (no variants array means all variants)
                        initialSelectionIds.push({
                            id: productId
                        });
                    }
                });
            }

            // Create ResourcePicker instance
            const productPicker = ResourcePicker.create(appBridgeInstance, {
                resourceType: ResourcePicker.ResourceType.Product,
                options: {
                    selectMultiple: true,
                    showVariants: true,
                    initialSelectionIds: initialSelectionIds
                }
            });

            // Subscribe to selection
            productPicker.subscribe(ResourcePicker.Action.SELECT, (selectPayload) => {
                const selection = selectPayload.selection || [];

                if (selection.length > 0) {
                    // Process selection to group variants by product
                    const productsMap = new Map();

                    // First pass: collect all products
                    selection.forEach(item => {
                        const isVariant = item.id && item.id.includes('ProductVariant');
                        if (!isVariant) {
                            const productId = item.id;
                            if (!productsMap.has(productId)) {
                                productsMap.set(productId, {
                                    id: productId,
                                    shopify_product_id: productId,
                                    product_title: item.title || 'Untitled Product',
                                    product_image: item.images?.[0]?.originalSrc || '',
                                    product_handle: item.handle || null, // IMPORTANT: Preserve product handle from ResourcePicker
                                    selectedVariants: [],
                                    variants: []
                                });
                            }
                        }
                    });

                    // Second pass: collect variants and link to products
                    selection.forEach(item => {
                        const isVariant = item.id && item.id.includes('ProductVariant');
                        if (isVariant) {
                            // Extract product ID from variant (variant ID format: gid://shopify/ProductVariant/123)
                            // Product ID should be in parentId or we need to extract it
                            let productId = item.parentId;
                            
                            // If no parentId, try to find product by matching variant in product variants
                            if (!productId) {
                                for (const [pid, product] of productsMap.entries()) {
                                    // Check if this variant belongs to this product
                                    // We'll match it later when we have product details
                                    productId = pid;
                                    break; // For now, assign to first product (will be refined)
                                }
                            }

                            if (productId && productsMap.has(productId)) {
                                const productEntry = productsMap.get(productId);
                                const variantId = item.id;
                                
                                if (!productEntry.selectedVariants.includes(variantId)) {
                                    productEntry.selectedVariants.push(variantId);
                                }
                                
                                productEntry.variants.push({
                                    id: variantId,
                                    title: item.title || 'Default',
                                    price: parseFloat(item.price || 0),
                                    available: item.available !== false
                                });
                            }
                        } else {
                            // Product selected - if it has variants, add all selected variants
                            const productEntry = productsMap.get(item.id);
                            if (productEntry && item.variants && item.variants.length > 0) {
                                item.variants.forEach(variant => {
                                    const variantId = variant.id;
                                    if (!productEntry.selectedVariants.includes(variantId)) {
                                        productEntry.selectedVariants.push(variantId);
                                    }
                                    productEntry.variants.push({
                                        id: variantId,
                                        title: variant.title || 'Default',
                                        price: parseFloat(variant.price || 0),
                                        available: variant.availableForSale !== false
                                    });
                                });
                            }
                        }
                    });

                    // Convert map to array and set default values
                    const products = Array.from(productsMap.values()).map(product => ({
                        ...product,
                        product_price: product.variants.length > 0 
                            ? parseFloat(product.variants[0].price || 0) 
                            : 0,
                        variant_title: product.variants.length > 0 
                            ? product.variants[0].title 
                            : 'Default',
                        shopify_variant_id: product.selectedVariants.length > 0 
                            ? product.selectedVariants[0] 
                            : '',
                        // IMPORTANT: Ensure product_handle is preserved
                        product_handle: product.product_handle || product.handle || null
                    }));

                    // Update reward tier products
                    const updatedRewards = (formik.values.rewards || []).map(tier =>
                        tier.id === tierId ? { ...tier, products: products } : tier
                    );
                    // Mark field as touched to ensure Formik tracks the change properly
                    formik.setFieldValue('rewards', updatedRewards, false);
                    formik.setFieldTouched('rewards', true, false);
                }

                // Cleanup
                productPicker.unsubscribe();
            });

            // Subscribe to cancellation
            productPicker.subscribe(ResourcePicker.Action.CANCEL, () => {
                productPicker.unsubscribe();
            });

            // Open the picker
            productPicker.dispatch(ResourcePicker.Action.OPEN);
        } catch (error) {
            console.error('Error opening reward tier product picker:', error);
            showToastMessage('Failed to open product picker. Please try again.', true);
        }
    }, [appBridgeInstance, formik.values.rewards, formik, showToastMessage]);

    // Define updateRewardTier before it's used in handleRewardTierProductSelect
    const updateRewardTier = useCallback((id, field, value) => {
        const updatedRewards = (formik.values.rewards || []).map(tier =>
            tier.id === id ? { ...tier, [field]: value } : tier
        );
        // Mark field as touched to ensure Formik tracks the change properly
        formik.setFieldValue('rewards', updatedRewards, false);
        formik.setFieldTouched('rewards', true, false);
    }, [formik]);

    const handleRemoveProduct = useCallback((productId) => {
        // Create a NEW array reference (immutable update) so Formik detects the change
        // Using filter() creates a new array, ensuring Formik's shallow equality check detects the difference
        const currentProducts = formik.values.products || [];
        const updatedProducts = currentProducts.filter(product => product.shopify_product_id !== productId);
        
        // Only update if the array actually changed (avoid unnecessary updates)
        if (updatedProducts.length !== currentProducts.length) {
            // Use setFieldValue to update - Formik will compare new array reference with initialValues
            formik.setFieldValue('products', updatedProducts, false);
            formik.setFieldTouched('products', true, false);
            
            // Mark products as changed to ensure save bar appears
            setProductsChanged(true);
        }
    }, [formik]);

    const handleRemoveRewardTierProduct = useCallback((tierId, productId, variantId = null) => {
        const updatedRewards = (formik.values.rewards || []).map(reward => {
            if (reward.id !== tierId) return reward;
            
            // If variantId is provided, remove only that variant from the product
            if (variantId) {
                const updatedProducts = (reward.products || []).map(product => {
                    // Check if this is the product we want to modify
                    const isTargetProduct = product.shopify_product_id === productId || product.id === productId;
                    
                    if (isTargetProduct) {
                        // Remove the specific variant from selectedVariants
                        const updatedSelectedVariants = (product.selectedVariants || []).filter(selectedVariantId => {
                            // Compare variant IDs (handle both GID format and numeric)
                            const selectedIdStr = String(selectedVariantId);
                            const variantIdStr = String(variantId);
                            
                            // Check if they match (handle GID format: gid://shopify/ProductVariant/123)
                            return selectedIdStr !== variantIdStr && 
                                   !selectedIdStr.endsWith(`/${variantIdStr}`) &&
                                   !variantIdStr.endsWith(`/${selectedIdStr.split('/').pop()}`);
                        });
                        
                        // Also remove from variants array
                        const updatedVariants = (product.variants || []).filter(variant => {
                            const variantIdStr = String(variant.id);
                            const targetVariantIdStr = String(variantId);
                            
                            return variantIdStr !== targetVariantIdStr &&
                                   !variantIdStr.endsWith(`/${targetVariantIdStr}`) &&
                                   !targetVariantIdStr.endsWith(`/${variantIdStr.split('/').pop()}`);
                        });
                        
                        // If no variants remain, remove the entire product
                        if (updatedSelectedVariants.length === 0) {
                            return null; // Mark for removal
                        }
                        
                        // Update product with remaining variants
                        return {
                            ...product,
                            selectedVariants: updatedSelectedVariants,
                            variants: updatedVariants,
                            shopify_variant_id: updatedSelectedVariants.length > 0 ? updatedSelectedVariants[0] : ''
                        };
                    }
                    
                    return product;
                }).filter(product => product !== null); // Remove products marked for deletion
                
                return {
                    ...reward,
                    products: updatedProducts
                };
            } else {
                // If no variantId, remove the entire product (backward compatibility)
                return {
                    ...reward,
                    products: (reward.products || []).filter(product =>
                        (product.shopify_product_id !== productId) && (product.id !== productId)
                    )
                };
            }
        });
        
        // Mark field as touched to ensure Formik tracks the change properly
        formik.setFieldValue('rewards', updatedRewards, false);
        formik.setFieldTouched('rewards', true, false);
    }, [formik]);

    const handleBannerUpload = useCallback(async (files) => {
        if (!files || files.length === 0) {
            return;
        }

        const file = files[0];
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
            showToastMessage('Please upload a valid image file', true);
            return;
        }

        // Check if App Bridge is available (window.shopify exists when app is embedded)
        if (!window.shopify) {
            showToastMessage('App Bridge is not available. Please ensure you are accessing the app through Shopify Admin.', true);
            return;
        }

        // Set uploading state
        setUploadingBanner(true);

        try {
            // Upload image to Shopify (using graphqlCall pattern, no appBridge parameter needed)
            const uploadedFile = await uploadImageToShopify(file, 'Campaign banner image');
            // Update campaign banner with Shopify image URL
            formik.setFieldValue('campaign_banner_link', uploadedFile.url, false);
            formik.setFieldTouched('campaign_banner_link', true, false);
            
            showToastMessage('Campaign banner uploaded successfully', false);
        } catch (error) {
            console.error('Error uploading campaign banner:', error);
            // Provide more helpful error messages
            let errorMessage = 'Failed to upload banner image';
            if (error.message) {
                if (error.message.includes('wrong domain') || error.message.includes('not properly embedded')) {
                    errorMessage = 'Please ensure you are accessing the app through Shopify Admin. The app must be embedded with shop and host parameters.';
                } else if (error.message.includes('App Bridge')) {
                    errorMessage = 'App Bridge is not available. Please refresh the page and try again.';
                } else {
                    errorMessage = error.message;
                }
            }
            showToastMessage(errorMessage, true);
        } finally {
            // Clear uploading state
            setUploadingBanner(false);
        }
    }, [formik, showToastMessage]);

    const handleRewardImageUpload = useCallback(async (tierId, files) => {
        if (!files || files.length === 0) {
            return;
        }

        const file = files[0];
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
            showToastMessage('Please upload a valid image file', true);
            return;
        }

        // Check if App Bridge is available (window.shopify exists when app is embedded)
        if (!window.shopify) {
            showToastMessage('App Bridge is not available. Please ensure you are accessing the app through Shopify Admin.', true);
            return;
        }

        // Set uploading state
        setUploadingRewardImages(prev => ({ ...prev, [tierId]: true }));

        try {
            // Get current reward tier to check if bundle exists
            const currentTier = (formik.values.rewards || []).find(tier => tier.id === tierId);
            const bundleProductId = currentTier?.bundle_product_id;

            // Upload image and update bundle product if bundle exists
            if (bundleProductId) {
                // Use the new function that uploads and sets image on bundle product
                const result = await uploadAndSetRewardImage(file, bundleProductId, `Reward image for tier ${tierId}`);
                
                if (result.success && result.url) {
                    // Update reward tier with Shopify image URL
                    updateRewardTier(tierId, 'reward_image', result.url);
                    
                    if (result.productUpdated) {
                        showToastMessage('Reward image uploaded and bundle product updated successfully', false);
                    } else {
                        showToastMessage('Reward image uploaded successfully (bundle product update may have failed)', false);
                    }
                } else {
                    throw new Error(result.error || 'Failed to upload and set reward image');
                }
            } else {
                // No bundle exists yet, just upload the image
                // Bundle product will be updated when bundle is created
                const uploadedFile = await uploadImageToShopify(file, `Reward image for tier ${tierId}`);
                // Update reward tier with Shopify image URL
                updateRewardTier(tierId, 'reward_image', uploadedFile.url);
                
                showToastMessage('Reward image uploaded successfully. It will be set on the bundle product when you save the campaign.', false);
            }
        } catch (error) {
            console.error('Error uploading reward image:', error);
            // Provide more helpful error messages
            let errorMessage = 'Failed to upload image';
            if (error.message) {
                if (error.message.includes('wrong domain') || error.message.includes('not properly embedded')) {
                    errorMessage = 'Please ensure you are accessing the app through Shopify Admin. The app must be embedded with shop and host parameters.';
                } else if (error.message.includes('App Bridge')) {
                    errorMessage = 'App Bridge is not available. Please refresh the page and try again.';
                } else {
                    errorMessage = error.message;
                }
            }
            showToastMessage(errorMessage, true);
        } finally {
            // Clear uploading state
            setUploadingRewardImages(prev => {
                const updated = { ...prev };
                delete updated[tierId];
                return updated;
            });
        }
    }, [updateRewardTier, showToastMessage]);

    const addRewardTier = useCallback(() => {
        const newTier = {
            id: Date.now(),
            reward_title: '',
            reward_description: '',
            reward_image: '',
            quantity_limit: '',
            reward_price: '',
            products: [],
            is_active: '1',
            show_strikethrough_price: false
        };
        const updatedRewards = [newTier, ...(formik.values.rewards || [])];
        // Mark field as touched to ensure Formik tracks the change properly
        formik.setFieldValue('rewards', updatedRewards, false);
        formik.setFieldTouched('rewards', true, false);
    }, [formik]);

    const removeRewardTier = useCallback((id) => {
        const updatedRewards = (formik.values.rewards || []).filter(tier => tier.id !== id);
        // Mark field as touched to ensure Formik tracks the change properly
        formik.setFieldValue('rewards', updatedRewards, false);
        formik.setFieldTouched('rewards', true, false);
    }, [formik]);

    const handleTagAdd = useCallback((type, value) => {
        if (value.trim() && !(type === 'order' ? (formik.values.orderTags || []) : (formik.values.customerTags || [])).includes(value.trim())) {
            if (type === 'order') {
                const updatedOrderTags = [...(formik.values.orderTags || []), value.trim()];
                // Mark field as touched to ensure Formik tracks the change properly
                formik.setFieldValue('orderTags', updatedOrderTags, false);
                formik.setFieldTouched('orderTags', true, false);
            } else {
                const updatedCustomerTags = [...(formik.values.customerTags || []), value.trim()];
                // Mark field as touched to ensure Formik tracks the change properly
                formik.setFieldValue('customerTags', updatedCustomerTags, false);
                formik.setFieldTouched('customerTags', true, false);
            }
        }
    }, [formik]);

    const handleTagRemove = useCallback((type, tag) => {
        if (type === 'order') {
            const updatedOrderTags = (formik.values.orderTags || []).filter(t => t !== tag);
            // Mark field as touched to ensure Formik tracks the change properly
            formik.setFieldValue('orderTags', updatedOrderTags, false);
            formik.setFieldTouched('orderTags', true, false);
        } else {
            const updatedCustomerTags = (formik.values.customerTags || []).filter(t => t !== tag);
            // Mark field as touched to ensure Formik tracks the change properly
            formik.setFieldValue('customerTags', updatedCustomerTags, false);
            formik.setFieldTouched('customerTags', true, false);
        }
    }, [formik]);

    const handleOrderTagInputChange = useCallback((value) => {
        formik.setFieldValue('orderTagInput', value);
    }, []);

    const handleCustomerTagInputChange = useCallback((value) => {
        formik.setFieldValue('customerTagInput', value);
    }, []);

    const handleOrderTagSubmit = useCallback(() => {
        if (formik.values.orderTagInput.trim()) {
            handleTagAdd('order', formik.values.orderTagInput.trim());
            formik.setFieldValue('orderTagInput', '');
            // Mark orderTags field as touched to ensure Formik tracks the change
            formik.setFieldTouched('orderTags', true, false);
        }
    }, [formik.values.orderTagInput, handleTagAdd, formik]);

    const handleCustomerTagSubmit = useCallback(() => {
        if (formik.values.customerTagInput.trim()) {
            handleTagAdd('customer', formik.values.customerTagInput.trim());
            formik.setFieldValue('customerTagInput', '');
            // Mark customerTags field as touched to ensure Formik tracks the change
            formik.setFieldTouched('customerTags', true, false);
        }
    }, [formik.values.customerTagInput, handleTagAdd, formik]);

    const handleOrderTagKeyPress = useCallback((event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation(); // Prevent form submission
            handleOrderTagSubmit();
        }
    }, [handleOrderTagSubmit]);

    const handleCustomerTagKeyPress = useCallback((event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation(); // Prevent form submission
            handleCustomerTagSubmit();
        }
    }, [handleCustomerTagSubmit]);

    const calculateProgress = useCallback(() => {
        // Use live form values directly from campaign API response
        if (formik.values.goal_type === '0') {
            // Number of items goal type - use initial_backers only if addStartPoint is checked
            const initialBackers = formik.values.addStartPoint ? (formik.values.initial_backers || 0) : 0;
            const numberOfItems = formik.values.number_of_items || 0;
            if (numberOfItems <= 0) return 0;
            const progress = (initialBackers / numberOfItems) * 100;
            return isNaN(progress) || !isFinite(progress) ? 0 : Math.min(Math.max(progress, 0), 100);
        } else {
            // Total Amount goal type - use initial_fund only if addStartPoint is checked
            const initialFund = formik.values.addStartPoint ? (formik.values.initial_fund || 0) : 0;
            const totalAmount = formik.values.total_amount || 0;
            if (totalAmount <= 0) return 0;
            const progress = (initialFund / totalAmount) * 100;
            return isNaN(progress) || !isFinite(progress) ? 0 : Math.min(Math.max(progress, 0), 100);
        }
    }, [formik.values.goal_type, formik.values.initial_fund, formik.values.initial_backers, formik.values.number_of_items, formik.values.total_amount, formik.values.addStartPoint]);

    const calculateBackers = useCallback(() => {
        // Use live form values directly from campaign API response
        return formik.values.initial_backers_count || 0;
    }, [formik.values.initial_backers_count]);

    // Memoized progress calculation - calculateProgress is already memoized with useCallback
    const progressValue = calculateProgress();

    // Memoized backers calculation - calculateBackers is already memoized with useCallback
    const backersValue = calculateBackers();

    // Helper function to get current date/time in a specific timezone
    const getCurrentDateTimeInTimezone = useCallback((timezone) => {
        if (!timezone) {
            const now = new Date();
            return {
                date: now.toISOString().split('T')[0],
                time: now.toTimeString().slice(0, 5),
                datetime: now.toISOString().slice(0, 16),
                dateObject: now
            };
        }
        
        try {
            const now = new Date();
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });
            
            const parts = formatter.formatToParts(now);
            const year = parseInt(parts.find(p => p.type === 'year').value);
            const month = parseInt(parts.find(p => p.type === 'month').value) - 1;
            const day = parseInt(parts.find(p => p.type === 'day').value);
            const hour = parseInt(parts.find(p => p.type === 'hour').value);
            const minute = parseInt(parts.find(p => p.type === 'minute').value);
            const second = parseInt(parts.find(p => p.type === 'second').value);
            
            // Create date string in format YYYY-MM-DDTHH:mm
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
            
            return {
                date: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
                time: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
                datetime: dateStr,
                dateObject: new Date(year, month, day, hour, minute, second)
            };
        } catch (error) {
            console.error('Error getting current datetime in timezone:', error);
            const now = new Date();
            return {
                date: now.toISOString().split('T')[0],
                time: now.toTimeString().slice(0, 5),
                datetime: now.toISOString().slice(0, 16),
                dateObject: now
            };
        }
    }, []);

    // Helper function to convert datetime string to UTC based on selected timezone
    const convertToUTC = useCallback((dateTimeString, timezone) => {
        if (!dateTimeString || !timezone) return null;

        try {
            // Parse the datetime string (format: "YYYY-MM-DDTHH:mm")
            const [datePart, timePart] = dateTimeString.split('T');
            if (!datePart || !timePart) return null;

            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes] = timePart.split(':').map(Number);

            // Create a date object treating the input as UTC (starting point for iteration)
            let testDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));

            // Use Intl API to format this date in the selected timezone
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });

            // Iteratively adjust until we get the correct UTC time
            // Format the test date in the selected timezone
            let parts = formatter.formatToParts(testDate);
            let tzYear = parseInt(parts.find(p => p.type === 'year').value);
            let tzMonth = parseInt(parts.find(p => p.type === 'month').value) - 1;
            let tzDay = parseInt(parts.find(p => p.type === 'day').value);
            let tzHour = parseInt(parts.find(p => p.type === 'hour').value);
            let tzMinute = parseInt(parts.find(p => p.type === 'minute').value);

            // Check if the formatted time matches our input
            let iterations = 0;
            const maxIterations = 10; // Prevent infinite loops

            while ((tzYear !== year || tzMonth !== month - 1 || tzDay !== day || tzHour !== hours || tzMinute !== minutes) && iterations < maxIterations) {
                // Calculate the difference in milliseconds
                const desiredTime = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
                const actualTime = new Date(Date.UTC(tzYear, tzMonth, tzDay, tzHour, tzMinute, 0));
                const diffMs = desiredTime.getTime() - actualTime.getTime();

                // Adjust the test date
                testDate = new Date(testDate.getTime() + diffMs);

                // Format again to check
                parts = formatter.formatToParts(testDate);
                tzYear = parseInt(parts.find(p => p.type === 'year').value);
                tzMonth = parseInt(parts.find(p => p.type === 'month').value) - 1;
                tzDay = parseInt(parts.find(p => p.type === 'day').value);
                tzHour = parseInt(parts.find(p => p.type === 'hour').value);
                tzMinute = parseInt(parts.find(p => p.type === 'minute').value);

                iterations++;
            }

            return testDate;
        } catch (error) {
            console.error('Error converting datetime to UTC:', error);
            // Fallback: treat as local time
            try {
                return new Date(dateTimeString);
            } catch {
                return null;
            }
        }
    }, []);

    // Helper function to format datetime string for display in selected timezone (for date picker)
    const formatDateForTimezone = useCallback((dateTimeString, timezone) => {
        if (!dateTimeString || !timezone) return '';
        
        try {
            // Parse the datetime string (format: "YYYY-MM-DDTHH:mm")
            const [datePart, timePart] = dateTimeString.split('T');
            if (!datePart || !timePart) return datePart || '';
            
            // Convert to UTC first, then format in the selected timezone
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes] = timePart.split(':').map(Number);
            
            // Create a UTC date object representing this datetime in the timezone
            const utcDate = convertToUTC(dateTimeString, timezone);
            if (!utcDate) return datePart;
            
            // Format the UTC date in the selected timezone to get the date part
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
            
            const parts = formatter.formatToParts(utcDate);
            const displayYear = parts.find(p => p.type === 'year').value;
            const displayMonth = parts.find(p => p.type === 'month').value;
            const displayDay = parts.find(p => p.type === 'day').value;
            
            return `${displayYear}-${displayMonth}-${displayDay}`;
        } catch (error) {
            console.error('Error formatting date for timezone:', error);
            // Fallback: return the date part as-is
            const [datePart] = dateTimeString.split('T');
            return datePart || '';
        }
    }, [convertToUTC]);

    // Helper function to format time for display in selected timezone
    const formatTimeForTimezone = useCallback((dateTimeString, timezone) => {
        if (!dateTimeString || !timezone) return '';
        
        try {
            const [datePart, timePart] = dateTimeString.split('T');
            if (!datePart || !timePart) return '';
            
            // Convert to UTC first, then format in the selected timezone
            const utcDate = convertToUTC(dateTimeString, timezone);
            if (!utcDate) return timePart;
            
            // Format the UTC date in the selected timezone to get the time part
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            
            const parts = formatter.formatToParts(utcDate);
            const hour = parts.find(p => p.type === 'hour').value;
            const minute = parts.find(p => p.type === 'minute').value;
            
            return `${hour}:${minute}`;
        } catch (error) {
            console.error('Error formatting time for timezone:', error);
            // Fallback: return the time part as-is
            const [, timePart] = dateTimeString.split('T');
            return timePart || '';
        }
    }, [convertToUTC]);

    // Get minimum date for date picker based on selected timezone
    const minDateForTimezone = useMemo(() => {
        if (!formik.values.timeZone) {
            return new Date().toISOString().split('T')[0];
        }
        const currentTZ = getCurrentDateTimeInTimezone(formik.values.timeZone);
        return currentTZ.date;
    }, [formik.values.timeZone, getCurrentDateTimeInTimezone]);

    // Calculate time remaining as object with days, hours, minutes
    const calculateTimeRemainingObject = useCallback(() => {
        if (!formik.values.campaign_end_datetime || !formik.values.timeZone) {
            return { days: 0, hours: 0, minutes: 0 };
        }

        try {
            // Parse the end datetime - normalize format first
            let endDateStr = formik.values.campaign_end_datetime;
            
            // Normalize the datetime format if needed (handle MySQL format: YYYY-MM-DD HH:mm:ss)
            if (endDateStr.includes(' ') && !endDateStr.includes('T')) {
                endDateStr = normalizeDateTimeFormat(endDateStr);
            }
            
            const [endDatePart, endTimePart] = endDateStr.split('T');
            
            let endDateTimeUTC = null;
            
            if (!endDatePart || !endTimePart) {
                // Try to parse as MySQL format if T format fails
                if (endDateStr.includes(' ')) {
                    const mysqlParts = endDateStr.split(' ');
                    if (mysqlParts.length >= 2) {
                        const datePart = mysqlParts[0];
                        const timePart = mysqlParts[1].split(':').slice(0, 2).join(':');
                        endDateStr = `${datePart}T${timePart}`;
                        endDateTimeUTC = convertToUTC(endDateStr, formik.values.timeZone);
                    }
                }
            } else {
                // Convert end datetime to UTC based on selected timezone
                endDateTimeUTC = convertToUTC(endDateStr, formik.values.timeZone);
            }
            
            if (!endDateTimeUTC) {
                return { days: 0, hours: 0, minutes: 0 };
            }
            
            // Get current UTC time
            const nowUTC = new Date();
            
            // Calculate difference
            const timeDiff = endDateTimeUTC.getTime() - nowUTC.getTime();

            if (timeDiff <= 0) {
                return { days: 0, hours: 0, minutes: 0 };
            }

            const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

            return { days, hours, minutes };
        } catch (error) {
            console.error('Error calculating time remaining:', error);
            return { days: 0, hours: 0, minutes: 0 };
        }
    }, [formik.values.campaign_end_datetime, formik.values.timeZone, convertToUTC, normalizeDateTimeFormat]);
    
    // Format time remaining text with placeholders
    const formatTimeRemainingText = useCallback((text) => {
        if (!text) return '';
        const timeRemaining = calculateTimeRemainingObject();

        const DAY_MARKER = '__LIFT_DAY__';
        const HOUR_MARKER = '__LIFT_HOUR__';
        const MINUTE_MARKER = '__LIFT_MINUTE__';

        let replacedText = text
            .replace(/{days}|{day}/gi, DAY_MARKER)
            .replace(/{hours}|{hour}/gi, HOUR_MARKER)
            .replace(/{minutes}|{minute}/gi, MINUTE_MARKER);

        const pluralizeTokenUnit = (inputText, marker, unit, value) => {
            return inputText
                .replace(new RegExp(`${marker}\\s+(${unit}s?)\\b`, 'gi'), (match, rawUnit) => {
                    const singularUnit = rawUnit.replace(/s$/i, '');
                    const pluralSuffix = /^[A-Z]+$/.test(singularUnit) ? 'S' : 's';
                    return `${value} ${singularUnit}${value <= 1 ? '' : pluralSuffix}`;
                })
                .replace(new RegExp(marker, 'g'), String(value));
        };

        replacedText = pluralizeTokenUnit(replacedText, DAY_MARKER, 'day', timeRemaining.days);
        replacedText = pluralizeTokenUnit(replacedText, HOUR_MARKER, 'hour', timeRemaining.hours);
        replacedText = pluralizeTokenUnit(replacedText, MINUTE_MARKER, 'minute', timeRemaining.minutes);

        return replacedText;
    }, [calculateTimeRemainingObject]);

    // Calculate countdown for Product Drop start time based on selected timezone
    const calculateDropCountdown = useCallback(() => {
        if (!formik.values.campaign_start_datetime || !formik.values.timeZone) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }

        try {
            // Normalize the datetime format if needed (handle MySQL format: YYYY-MM-DD HH:mm:ss)
            let startDateStr = formik.values.campaign_start_datetime;
            if (startDateStr.includes(' ') && !startDateStr.includes('T')) {
                startDateStr = normalizeDateTimeFormat(startDateStr);
            }
            
            // Convert the start datetime to UTC based on selected timezone
            const startDateTimeUTC = convertToUTC(startDateStr, formik.values.timeZone);
            if (!startDateTimeUTC) {
                return { days: 0, hours: 0, minutes: 0, seconds: 0 };
            }

            // Get current UTC time
            const nowUTC = new Date();

            // Calculate difference
            const timeDiff = startDateTimeUTC.getTime() - nowUTC.getTime();

            if (timeDiff <= 0) {
                return { days: 0, hours: 0, minutes: 0, seconds: 0 };
            }

            const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

            return { days, hours, minutes, seconds };
        } catch (error) {
            console.error('Error calculating countdown:', error);
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
    }, [formik.values.campaign_start_datetime, formik.values.timeZone, convertToUTC, normalizeDateTimeFormat]);

    // Update countdown every second for Product Drop preview - optimized
    useEffect(() => {
        if (!formik.values || formik.values.campaign_type !== '2') return;

        // Calculate initial countdown
        setCountdown(calculateDropCountdown());

        // Update countdown every second
        const intervalId = setInterval(() => {
            setCountdown(calculateDropCountdown());
        }, 1000);

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [formik.values?.campaign_type, formik.values?.campaign_start_datetime, formik.values?.timeZone, calculateDropCountdown]);

    // Get campaign status using same logic as campaign list
    const getCampaignStatus = useCallback((campaign_status) => {
        switch (campaign_status) {
            case '0': return 'Inactive';
            case '1': return 'Active';
            case '2': return 'Paused';
            case '3': return 'Completed';
            case '4': return 'Cancelled';
            default: return 'Inactive';
        }
    }, []);

    // Get campaign type using same logic as campaign list
    const getCampaignType = useCallback((campaign_type) => {
        switch (campaign_type) {
            case '0': return 'PreSales';
            case '1': return 'Crowdfunding';
            case '2': return 'Product Drop';
            default: return 'PreSales';
        }
    }, []);

    // Helper function to format datetime to MySQL format (YYYY-MM-DD HH:mm:ss)
    const formatDateTimeForMySQL = useCallback((dateTimeValue) => {
        if (!dateTimeValue) return null;

        try {
            // Handle datetime string in format "YYYY-MM-DDTHH:mm" (from formik)
            if (typeof dateTimeValue === 'string' && dateTimeValue.includes('T')) {
                const [datePart, timePart] = dateTimeValue.split('T');
                if (datePart && timePart) {
                    // Ensure time part has seconds (add :00 if missing)
                    const timeWithSeconds = timePart.includes(':') && timePart.split(':').length === 2 
                        ? `${timePart}:00` 
                        : timePart;
                    return `${datePart} ${timeWithSeconds}`;
                }
            }
            
            // Fallback: try to parse as Date object or ISO string
            const date = new Date(dateTimeValue);
            // Check if date is valid
            if (isNaN(date.getTime())) return null;

            // Format to MySQL datetime format: YYYY-MM-DD HH:mm:ss
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        } catch (error) {
            console.error('Error formatting datetime:', error);
            return null;
        }
    }, []);

    // Memoize primary product calculation
    const primaryProduct = useMemo(() => {
        if (!formik.values?.products || formik.values.products.length === 0) return null;
        return formik.values.products.find(p => p.is_primary_product === '1' || p.is_primary_product === 1 || p.is_primary_product === true) || formik.values.products[0];
    }, [formik.values?.products]);

    // Validate required fields and collect errors for banner
    const validateRequiredFields = useCallback((values) => {
        const errors = [];
        
        // Campaign name is always required
        if (!values.campaign_name || !values.campaign_name.trim()) {
            errors.push('Campaign Name');
        }
        
        // Campaign type is always required
        if (!values.campaign_type) {
            errors.push('Campaign Type');
        }
        
        // Product is required for new campaigns
        if (isNewCampaign && (!values.products || values.products.length === 0)) {
            errors.push('Product');
        }
        
        // Crowdfunding specific validations
        if (values.campaign_type === '1') {
            if (!values.goal_type) {
                errors.push('Goal Type');
            }
            if (values.goal_type === '0' && (!values.number_of_items || values.number_of_items <= 0)) {
                errors.push('Number of Items');
            }
            if (values.goal_type === '1' && (!values.total_amount || values.total_amount <= 0)) {
                errors.push('Total Amount');
            }

            // If end date checkbox is checked, end date must be filled
            if (values.showEndDateCrowdfunding && (!values.campaign_end_datetime || !values.campaign_end_datetime.trim())) {
                errors.push('End Date and Time');
            }

            // Validate rewards if they exist
            if (values.rewards && Array.isArray(values.rewards) && values.rewards.length > 0) {
                values.rewards.forEach((reward, index) => {
                    // Reward Title is required
                    if (!reward.reward_title || !reward.reward_title.trim()) {
                        errors.push(`Reward ${index + 1} - Title`);
                    }
                    
                    // Bundle Final Price is required
                    if (!reward.reward_price || reward.reward_price === '' || parseFloat(reward.reward_price) <= 0) {
                        errors.push(`Reward ${index + 1} - Bundle Final Price`);
                    }
                    
                    // At least one product is required
                    if (!reward.products || !Array.isArray(reward.products) || reward.products.length === 0) {
                        errors.push(`Reward ${index + 1} - Products`);
                    }
                });
            }
        }
        
        // PreSales specific validations
        if (values.campaign_type === '0') {
            // If "Limit inventory for Preorder" is checked, available inventory must not be empty or 0
            if (values.limitInventory && (values.availableInventory === null || values.availableInventory === undefined || values.availableInventory <= 0)) {
                errors.push('Available Inventory');
            }
        }
        
        // Product Drop specific validations
        if (values.campaign_type === '2') {
            // Start date is required
            if (!values.campaign_start_datetime || !values.campaign_start_datetime.trim()) {
                errors.push('Start Date and Time');
            }
            
            // Drop limit (Total Drop Limit) is required
            if (!values.totalDropLimit || values.totalDropLimit === '' || parseInt(values.totalDropLimit) <= 0) {
                errors.push('Total Drop Limit');
            }
            
            // If end date checkbox is checked, end date must be filled
            if (values.showEndDate && (!values.campaign_end_datetime || !values.campaign_end_datetime.trim())) {
                errors.push('End Date and Time');
            }
        }

        // Validate start time is not greater than end time (for Product Drop only)
        if (values.campaign_type === '2' && values.campaign_start_datetime && values.campaign_end_datetime && values.timeZone) {
            try {
                // Only validate if end date is actually set (checkbox is checked)
                const isEndDateSet = values.showEndDate;
                
                if (isEndDateSet) {
                    // Helper function to convert datetime to UTC (inline version for validation)
                    const convertToUTCInline = (dateTimeString, timezone) => {
                        if (!dateTimeString || !timezone) return null;
                        try {
                            const [datePart, timePart] = dateTimeString.split('T');
                            if (!datePart || !timePart) return null;
                            const [year, month, day] = datePart.split('-').map(Number);
                            const [hours, minutes] = timePart.split(':').map(Number);
                            let testDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
                            const formatter = new Intl.DateTimeFormat('en-US', {
                                timeZone: timezone,
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false
                            });
                            let parts = formatter.formatToParts(testDate);
                            let tzYear = parseInt(parts.find(p => p.type === 'year').value);
                            let tzMonth = parseInt(parts.find(p => p.type === 'month').value) - 1;
                            let tzDay = parseInt(parts.find(p => p.type === 'day').value);
                            let tzHour = parseInt(parts.find(p => p.type === 'hour').value);
                            let tzMinute = parseInt(parts.find(p => p.type === 'minute').value);
                            let iterations = 0;
                            const maxIterations = 10;
                            while ((tzYear !== year || tzMonth !== month - 1 || tzDay !== day || tzHour !== hours || tzMinute !== minutes) && iterations < maxIterations) {
                                const desiredTime = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
                                const actualTime = new Date(Date.UTC(tzYear, tzMonth, tzDay, tzHour, tzMinute, 0));
                                const diffMs = desiredTime.getTime() - actualTime.getTime();
                                testDate = new Date(testDate.getTime() + diffMs);
                                parts = formatter.formatToParts(testDate);
                                tzYear = parseInt(parts.find(p => p.type === 'year').value);
                                tzMonth = parseInt(parts.find(p => p.type === 'month').value) - 1;
                                tzDay = parseInt(parts.find(p => p.type === 'day').value);
                                tzHour = parseInt(parts.find(p => p.type === 'hour').value);
                                tzMinute = parseInt(parts.find(p => p.type === 'minute').value);
                                iterations++;
                            }
                            return testDate;
                        } catch (error) {
                            return null;
                        }
                    };

                    // Convert both start and end datetimes to UTC for comparison
                    const startDateTimeUTC = convertToUTCInline(values.campaign_start_datetime, values.timeZone);
                    const endDateTimeUTC = convertToUTCInline(values.campaign_end_datetime, values.timeZone);
                    
                    if (startDateTimeUTC && endDateTimeUTC) {
                        // If start time is greater than or equal to end time, it's invalid
                        if (startDateTimeUTC.getTime() >= endDateTimeUTC.getTime()) {
                            errors.push('End Date and Time must be after Start Date and Time');
                        }
                    }
                }
            } catch (error) {
                console.error('Error validating start/end time comparison:', error);
                // Don't block save if validation fails, but log error
            }
        }
        
        return errors;
    }, [isNewCampaign]);

    // Inventory Management Functions using GraphQL via App Bridge
    /**
     * Get inventory item ID and location ID from variant
     */
    const getVariantInventoryInfo = useCallback(async (variantId) => {
        if (!variantId) {
            console.warn('[Inventory] Missing variantId');
            return { inventoryItemId: null, locationId: null, locationName: null };
        }

        try {
            // Convert numeric ID to GID format if needed
            let variantGid = variantId;
            if (typeof variantId === 'number' || /^\d+$/.test(String(variantId))) {
                variantGid = `gid://shopify/ProductVariant/${variantId}`;
            }

            const query = {
                query: `
                    query getProductInventory($variantId: ID!) {
                        productVariant(id: $variantId) {
                            inventoryItem {
                                id
                                inventoryLevels(first: 10) {
                                    edges {
                                        node {
                                            location {
                                                id
                                                name
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                `,
                variables: {
                    variantId: variantGid
                }
            };
            const response = await graphqlCall(query);

            if (response.errors && Array.isArray(response.errors) && response.errors.length > 0) {
                console.error('[Inventory] GraphQL errors getting variant inventory:', JSON.stringify(response.errors, null, 2));
                return { inventoryItemId: null, locationId: null, locationName: null };
            }

            const variant = response.data?.productVariant;
            if (!variant || !variant.inventoryItem) {
                console.warn('[Inventory] No inventory item found for variant:', variantGid);
                return { inventoryItemId: null, locationId: null, locationName: null };
            }

            const inventoryItemId = variant.inventoryItem.id;
            const inventoryLevels = variant.inventoryItem.inventoryLevels?.edges || [];
            
            // Get first location (or primary location)
            let locationId = null;
            let locationName = null;
            if (inventoryLevels.length > 0) {
                locationId = inventoryLevels[0].node.location.id;
                locationName = inventoryLevels[0].node.location.name;
            }

            return { inventoryItemId, locationId, locationName };
        } catch (error) {
            console.error('[Inventory] Error getting variant inventory info:', error);
            return { inventoryItemId: null, locationId: null, locationName: null };
        }
    }, []);

    /**
     * Enable inventory tracking for an inventory item
     */
    const enableInventoryTracking = useCallback(async (inventoryItemId) => {
        if (!inventoryItemId) {
            console.warn('[Inventory] Missing inventoryItemId');
            return { success: false, error: 'Missing required parameters' };
        }

        try {
            const mutation = {
                query: `
                    mutation inventoryItemUpdate($id: ID!, $input: InventoryItemInput!) {
                        inventoryItemUpdate(id: $id, input: $input) {
                            inventoryItem {
                                id
                                tracked
                            }
                            userErrors {
                                field
                                message
                            }
                        }
                    }
                `,
                variables: {
                    id: inventoryItemId,
                    input: {
                        tracked: true
                    }
                }
            };

            const response = await graphqlCall(mutation);

            if (response.errors && Array.isArray(response.errors) && response.errors.length > 0) {
                console.error('[Inventory] GraphQL errors enabling inventory tracking:', JSON.stringify(response.errors, null, 2));
                return { success: false, error: response.errors[0]?.message || 'GraphQL error' };
            }

            const result = response.data?.inventoryItemUpdate;
            if (result?.userErrors && result.userErrors.length > 0) {
                console.error('[Inventory] User errors enabling inventory tracking:', JSON.stringify(result.userErrors, null, 2));
                return { success: false, error: result.userErrors[0]?.message || 'User error' };
            }

            const tracked = result?.inventoryItem?.tracked;
            return { success: true, tracked: tracked };
        } catch (error) {
            console.error('[Inventory] Error enabling inventory tracking:', error);
            return { success: false, error: error.message };
        }
    }, []);

    /**
     * Set inventory quantity for a variant
     * @param {string} inventoryItemId - Inventory item ID
     * @param {string} locationId - Location ID
     * @param {number} quantity - Quantity to set
     * @param {boolean} enableTracking - Whether to enable inventory tracking (default: true)
     * @returns {Promise<Object>} Result object with success status
     */
    const setInventoryQuantity = useCallback(async (inventoryItemId, locationId, quantity, enableTracking = true) => {
        if (!inventoryItemId || !locationId || quantity === null || quantity === undefined) {
            console.warn('[Inventory] Missing required parameters for setting inventory');
            return { success: false, error: 'Missing required parameters' };
        }

        try {
            const mutation = {
                query: `
                    mutation inventorySetQuantities($input: InventorySetQuantitiesInput!) {
                        inventorySetQuantities(input: $input) {
                            inventoryAdjustmentGroup {
                                createdAt
                                reason
                            }
                            userErrors {
                                field
                                message
                            }
                        }
                    }
                `,
                variables: {
                    input: {
                        reason: 'correction',
                        name: 'available',
                        ignoreCompareQuantity: true,
                        quantities: [{
                            inventoryItemId: inventoryItemId,
                            locationId: locationId,
                            quantity: parseInt(quantity) || 0
                        }]
                    }
                }
            };

            const response = await graphqlCall(mutation);

            if (response.errors && Array.isArray(response.errors) && response.errors.length > 0) {
                console.error('[Inventory] GraphQL errors setting inventory:', JSON.stringify(response.errors, null, 2));
                return { success: false, error: response.errors[0]?.message || 'GraphQL error' };
            }

            const result = response.data?.inventorySetQuantities;
            if (result?.userErrors && result.userErrors.length > 0) {
                console.error('[Inventory] User errors setting inventory:', JSON.stringify(result.userErrors, null, 2));
                return { success: false, error: result.userErrors[0]?.message || 'User error' };
            }

            
            // Enable inventory tracking after setting quantity (only if enableTracking is true)
            if (enableTracking) {
                const trackingResult = await enableInventoryTracking(inventoryItemId);
                
                if (trackingResult.success) {
                } else {
                    console.warn('[Inventory] ⚠️ Failed to enable inventory tracking:', trackingResult.error);
                    // Don't fail the whole operation if tracking enable fails
                }
                
                return { success: true, trackingEnabled: trackingResult.success };
            } else {
                return { success: true, trackingEnabled: false };
            }
        } catch (error) {
            console.error('[Inventory] Error setting inventory quantity:', error);
            return { success: false, error: error.message };
        }
    }, [enableInventoryTracking]);

    /**
     * Update main product inventory for pre-sale campaigns only
     * Note: Product drop campaigns no longer update inventory
     */
    const updateMainProductInventory = useCallback(async (productId, variantId, campaignType, limitInventory, availableInventory, totalDropLimit) => {
        if (!productId || !variantId) {
            console.warn('[Inventory] Missing required parameters for main product inventory update');
            return { success: false, error: 'Missing required parameters' };
        }

        let quantityToSet = null;

        // Pre-sale (campaign_type = '0'): Check is_limit_inventory and available_inventory
        if (campaignType === '0') {
            if (limitInventory && availableInventory !== null && availableInventory !== undefined && availableInventory > 0) {
                quantityToSet = parseInt(availableInventory);
            } else {
                return { success: true, message: 'No inventory limit set for pre-sale campaign' };
            }
        }
        // Product Drop (campaign_type = '2'): Check product_drop_total_limit
        else if (campaignType === '2') {
            if (totalDropLimit && totalDropLimit !== '' && parseInt(totalDropLimit) > 0) {
                quantityToSet = parseInt(totalDropLimit);
            } else {
                return { success: true, message: 'No total drop limit set for product drop campaign' };
            }
        }
        // Crowdfunding (campaign_type = '1'): Don't set inventory on main product
        else {
            return { success: true, message: 'Crowdfunding campaigns do not set inventory on main product' };
        }

        // Get inventory info
        const inventoryInfo = await getVariantInventoryInfo(variantId);
        if (!inventoryInfo.inventoryItemId || !inventoryInfo.locationId) {
            console.warn('[Inventory] Could not get inventory info for variant:', variantId);
            return { success: false, error: 'Could not get inventory info' };
        }

        // Set inventory (disable tracking for product drop and pre-order campaigns)
        // These campaign types should not track inventory
        const shouldEnableTracking = false; // Disable tracking for product drop and pre-order campaigns
        const result = await setInventoryQuantity(
            inventoryInfo.inventoryItemId,
            inventoryInfo.locationId,
            quantityToSet,
            shouldEnableTracking
        );

        return result;
    }, [getVariantInventoryInfo, setInventoryQuantity]);

    /**
     * Update variant inventory policy (disable "continue selling when out of stock")
     * for pre-sale campaigns when limits are set
     * Note: Product drop campaigns no longer update inventory policy
     * @param {string} productId - Product GID
     * @param {string} campaignType - Campaign type ('0' for pre-sale)
     * @param {boolean} limitInventory - Whether inventory is limited (for pre-sale)
     * @param {number} availableInventory - Available inventory (for pre-sale)
     * @param {string|number} totalDropLimit - Total drop limit (deprecated, no longer used)
     * @returns {Promise<Object>} Result object with success status
     */
    const updateVariantInventoryPolicy = useCallback(async (productId, campaignType, limitInventory, availableInventory, totalDropLimit) => {
        if (!productId) {
            console.warn('[Inventory Policy] Missing product ID');
            return { success: false, error: 'Missing product ID' };
        }

        // Check if we should disable "continue selling when out of stock"
        let shouldDisableContinueSelling = false;

        // Pre-sale (campaign_type = '0'): Check is_limit_inventory and available_inventory
        if (campaignType === '0') {
            if (limitInventory && availableInventory !== null && availableInventory !== undefined && availableInventory > 0) {
                shouldDisableContinueSelling = true;
            } else {
                return { success: true, message: 'No inventory limit set for pre-sale campaign' };
            }
        }
        // Product Drop (campaign_type = '2'): Check product_drop_total_limit
        else if (campaignType === '2') {
            if (totalDropLimit && totalDropLimit !== '' && parseInt(totalDropLimit) > 0) {
                shouldDisableContinueSelling = true;
            } else {
                return { success: true, message: 'No total drop limit set for product drop campaign' };
            }
        }
        // Crowdfunding (campaign_type = '1'): Don't update inventory policy
        else {
            return { success: true, message: 'Crowdfunding campaigns do not update inventory policy' };
        }

        if (!shouldDisableContinueSelling) {
            return { success: true, message: 'No need to update inventory policy' };
        }

        try {
            // Convert product ID to GID format if needed
            let productGid = productId;
            if (typeof productId === 'number' || /^\d+$/.test(String(productId))) {
                productGid = `gid://shopify/Product/${productId}`;
            }

            // Step 1: Get all variants for the product
            const variantsQuery = {
                query: `
                    query GetProductVariants($productId: ID!) {
                        product(id: $productId) {
                            id
                            variants(first: 250) {
                                edges {
                                    node {
                                        id
                                        inventoryPolicy
                                    }
                                }
                            }
                        }
                    }
                `,
                variables: {
                    productId: productGid
                }
            };

            const variantsResponse = await graphqlCall(variantsQuery);

            if (variantsResponse.errors && Array.isArray(variantsResponse.errors) && variantsResponse.errors.length > 0) {
                console.error('[Inventory Policy] GraphQL errors getting variants:', variantsResponse.errors);
                return { success: false, error: 'GraphQL error getting product variants' };
            }

            const variants = variantsResponse.data?.product?.variants?.edges || [];
            if (variants.length === 0) {
                console.warn('[Inventory Policy] No variants found for product:', productGid);
                return { success: false, error: 'No variants found for product' };
            }

            // Extract variant IDs
            const variantIds = variants.map(edge => edge.node.id);

            // Step 2: Update all variants to DENY (disable continue selling when out of stock)
            // Use productVariantsBulkUpdate mutation
            // Build variants array with DENY inventory policy
            const variantsToUpdate = variantIds.map(variantId => ({
                id: variantId,
                inventoryPolicy: 'DENY' // DENY = don't continue selling when out of stock (enum value as string)
            }));

            const updateMutation = {
                query: `
                    mutation SetVariantsInventoryPolicy($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
                        productVariantsBulkUpdate(productId: $productId, variants: $variants) {
                            productVariants {
                                id
                                inventoryPolicy
                            }
                            userErrors {
                                field
                                message
                            }
                        }
                    }
                `,
                variables: {
                    productId: productGid,
                    variants: variantsToUpdate
                }
            };

            const updateResponse = await graphqlCall(updateMutation);

            if (updateResponse.errors && Array.isArray(updateResponse.errors) && updateResponse.errors.length > 0) {
                console.error('[Inventory Policy] GraphQL errors updating variants:', updateResponse.errors);
                return { success: false, error: 'GraphQL error updating variant inventory policy' };
            }

            // Check for user errors
            const userErrors = updateResponse.data?.productVariantsBulkUpdate?.userErrors || [];
            if (userErrors.length > 0) {
                console.error('[Inventory Policy] User errors updating variants:', userErrors);
                const errorMessages = userErrors.map(err => err.message || JSON.stringify(err)).join(', ');
                return { success: false, error: `User errors: ${errorMessages}` };
            }

            const updatedVariants = updateResponse.data?.productVariantsBulkUpdate?.productVariants || [];

            return {
                success: true,
                message: `Successfully updated ${updatedVariants.length} variant(s) to disable continue selling when out of stock`,
                updatedCount: updatedVariants.length
            };
        } catch (error) {
            console.error('[Inventory Policy] Error updating variant inventory policy:', error);
            return { success: false, error: error.message || 'Unknown error updating inventory policy' };
        }
    }, []);

    /**
     * Update bundle product inventory for reward campaigns
     */
    const updateBundleProductInventory = useCallback(async (bundleProductId, quantityLimit) => {
        if (!bundleProductId) {
            console.warn('[Inventory] Missing required parameters for bundle product inventory update');
            return { success: false, error: 'Missing required parameters' };
        }

        // Only set inventory if limit is set
        if (!quantityLimit || quantityLimit === '' || parseInt(quantityLimit) <= 0) {
            return { success: true, message: 'No quantity limit set for reward' };
        }

        const limit = parseInt(quantityLimit);

        // Convert bundle product ID to GID format if needed
        let bundleProductGid = bundleProductId;
        if (typeof bundleProductId === 'number' || /^\d+$/.test(String(bundleProductId))) {
            bundleProductGid = `gid://shopify/Product/${bundleProductId}`;
        }

        // Query to get first variant of bundle product
        const productQuery = {
            query: `
                query getProductVariants($productId: ID!) {
                    product(id: $productId) {
                        variants(first: 1) {
                            edges {
                                node {
                                    id
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                productId: bundleProductGid
            }
        };

        try {
            const productResponse = await graphqlCall(productQuery);

            if (productResponse.errors && Array.isArray(productResponse.errors) && productResponse.errors.length > 0) {
                console.error('[Inventory] GraphQL errors getting bundle product:', JSON.stringify(productResponse.errors, null, 2));
                return { success: false, error: 'GraphQL error getting bundle product' };
            }

            const variants = productResponse.data?.product?.variants?.edges || [];
            if (variants.length === 0) {
                console.warn('[Inventory] No variants found for bundle product:', bundleProductGid);
                return { success: false, error: 'No variants found for bundle product' };
            }

            const variantGid = variants[0].node.id;

            // Get inventory info for bundle variant
            const inventoryInfo = await getVariantInventoryInfo(variantGid);
            if (!inventoryInfo.inventoryItemId || !inventoryInfo.locationId) {
                console.warn('[Inventory] Could not get inventory info for bundle variant:', variantGid);
                return { success: false, error: 'Could not get inventory info for bundle variant' };
            }

            // Set inventory
            const result = await setInventoryQuantity(
                inventoryInfo.inventoryItemId,
                inventoryInfo.locationId,
                limit
            );

            return result;
        } catch (error) {
            console.error('[Inventory] Error updating bundle product inventory:', error);
            return { success: false, error: error.message };
        }
    }, [getVariantInventoryInfo, setInventoryQuantity]);

    const handleSave = useCallback(async (values) => {
        const latestFormValues = formik.values;
        // First, validate using Formik's validation with latest values
        const formikErrors = await formik.validateForm();
        
        // Also validate required fields using custom validation with latest values
        const validationErrors = validateRequiredFields(latestFormValues);
        
        // Combine both validation errors - convert Formik field names to display names
        const allErrors = [];
        
        // Map Formik field names to display names
        const fieldNameMap = {
            'campaign_name': 'Campaign Name',
            'campaign_type': 'Campaign Type',
            'goal_type': 'Goal Type',
            'number_of_items': 'Number of Items',
            'total_amount': 'Total Amount',
            'initial_fund': 'Initial Fund',
            'initial_backers': 'Initial Backers',
            'availableInventory': 'Available Inventory',
            'perCustomerLimit': 'Per Customer Limit',
            'totalDropLimit': 'Total Drop Limit',
            'campaign_end_datetime': 'End Date and Time'
        };
        
        // Add Formik validation errors
        if (formikErrors && Object.keys(formikErrors).length > 0) {
            Object.keys(formikErrors).forEach(field => {
                const displayName = fieldNameMap[field] || field;
                if (!allErrors.includes(displayName)) {
                    allErrors.push(displayName);
                }
            });
            // Mark all error fields as touched so errors show
            Object.keys(formikErrors).forEach(field => {
                formik.setFieldTouched(field, true);
            });
        }
        
        // Add custom validation errors
        validationErrors.forEach(error => {
            if (!allErrors.includes(error)) {
                allErrors.push(error);
            }
        });
        
        if (allErrors.length > 0) {
            setBannerErrors(allErrors);
            setShowBanner(true);
            // Scroll to top to show banner
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // Don't set saving state if validation fails
            return;
        }
        
        // Hide banner if validation passes
        setShowBanner(false);
        setBannerErrors([]);

        // Set saving state to show loader
        setSaving(true);
        // setLoading(true);
        try {
            // OPTIMIZATION: Clear bundle check cache at start of save operation
            clearBundleCheckCache();
            
            // Structure products data - only primary product stored in JSON fields
            // Get primary product (first one or marked as primary)
            // Use latestFormValues to ensure we have the most current form state
            const currentPrimaryProduct = (latestFormValues.products || []).find(p => 
                p.is_primary_product === '1' || 
                p.is_primary_product === 1 || 
                p.is_primary_product === true
            ) || (latestFormValues.products || [])[0];

            // OPTIMIZATION: Helper function to detect what changed in a reward
            const detectRewardChanges = (newReward, oldReward) => {
                if (!oldReward) {
                    // New reward - update everything
                    return {
                        products: true,
                        title: true,
                        description: true,
                        price: true,
                        quantity: true
                    };
                }

                // Compare products (check IDs and selected variants)
                const productsChanged = (() => {
                    const oldProducts = oldReward.products || [];
                    const newProducts = newReward.products || [];
                    if (oldProducts.length !== newProducts.length) return true;
                    
                    const normalizeProduct = (p) => ({
                        id: p.shopify_product_id,
                        variants: (p.selectedVariants || []).sort().join(',')
                    });
                    
                    const oldNormalized = oldProducts.map(normalizeProduct).sort((a, b) => 
                        (a.id || '').localeCompare(b.id || '')
                    );
                    const newNormalized = newProducts.map(normalizeProduct).sort((a, b) => 
                        (a.id || '').localeCompare(b.id || '')
                    );
                    
                    return JSON.stringify(oldNormalized) !== JSON.stringify(newNormalized);
                })();

                // Compare title
                const titleChanged = (oldReward.reward_title || '') !== (newReward.reward_title || '');

                // Compare description
                const descriptionChanged = (oldReward.reward_description || '') !== (newReward.reward_description || '');

                // Compare price
                const priceChanged = (oldReward.reward_price || '') !== (newReward.reward_price || '');

                // Compare quantity limit
                const quantityChanged = (oldReward.quantity_limit || '') !== (newReward.quantity_limit || '');

                return {
                    products: productsChanged,
                    title: titleChanged,
                    description: descriptionChanged,
                    price: priceChanged,
                    quantity: quantityChanged
                };
            };

            // Get original rewards for comparison
            const originalRewards = originalData?.rewards || [];

            // Create/update bundles for reward tiers with products BEFORE saving campaign
            // Each reward tier gets its own separate bundle
            let updatedRewardsWithBundles = latestFormValues.rewards || [];
            if (latestFormValues.rewards && latestFormValues.rewards.length > 0) {
                try {
                    // OPTIMIZATION: Process all rewards in parallel instead of sequentially
                    const rewardPromises = latestFormValues.rewards.map(async (reward, i) => {
                        // Only create bundles for rewards with products
                        if (!reward.products || reward.products.length === 0) {
                            return { reward, updatedReward: reward, bundleInfo: null };
                        }
                        
                        try {
                            // Find original reward for comparison
                            const oldReward = originalRewards.find(r => 
                                (r.id && r.id === reward.id) || 
                                (r.reward_id && r.reward_id === reward.reward_id) ||
                                (r.reward_id && r.reward_id === reward.id)
                            );

                            // Detect what changed
                            const changes = detectRewardChanges(reward, oldReward);

                            // Check if reward already has a bundle ID stored (from previous save)
                            const existingBundleId = reward.bundle_product_id;
                            
                            // OPTIMIZATION: Only update what changed
                            // If nothing changed and bundle exists, skip GraphQL calls
                            if (existingBundleId && !changes.products && !changes.title && !changes.description && !changes.price && !changes.quantity) {
                                // Nothing changed, return existing bundle info
                                return {
                                    reward,
                                    updatedReward: reward,
                                    bundleInfo: {
                                        bundle_product_id: existingBundleId,
                                        bundle_product_handle: reward.bundle_product_handle
                                    }
                                };
                            }

                            // OPTIMIZATION: Handle specific update cases
                            let bundleInfo = null;

                            if (existingBundleId) {
                                // Bundle exists - check what needs updating
                                if (changes.products) {
                                    // Products changed - need to update bundle components
                                    const updateOptions = {
                                        updateProducts: true,
                                        updateTitle: changes.title,
                                        updateDescription: changes.description,
                                        updatePrice: changes.price
                                    };
                                    bundleInfo = await createOrUpdateRewardBundle(
                                        reward, 
                                        existingBundleId,
                                        updateOptions,
                                        oldReward
                                    );
                                } else {
                                    // Products didn't change - can update individual fields
                                    // OPTIMIZATION: If both title and description changed, update them in a single GraphQL call
                                    const updatePromises = [];
                                    
                                    if (changes.title && changes.description && reward.reward_title && reward.reward_description) {
                                        // Both title and description changed - update in single GraphQL call
                                        updatePromises.push(
                                            updateBundleTitleAndDescription(existingBundleId, reward.reward_title, reward.reward_description)
                                                .then(success => {
                                                    if (!success) {
                                                        console.warn(`Failed to update title and description for bundle ${existingBundleId}`);
                                                    }
                                                })
                                                .catch(err => console.warn('Error updating title and description:', err))
                                        );
                                    } else {
                                        // Update title and description separately if only one changed
                                        if (changes.title && reward.reward_title) {
                                            updatePromises.push(
                                                updateBundleTitleOnly(existingBundleId, reward.reward_title)
                                                    .then(success => {
                                                        if (!success) {
                                                            console.warn(`Failed to update title for bundle ${existingBundleId}`);
                                                        }
                                                    })
                                                    .catch(err => console.warn('Error updating title:', err))
                                            );
                                        }
                                        
                                        if (changes.description && reward.reward_description) {
                                            updatePromises.push(
                                                updateBundleDescriptionOnly(existingBundleId, reward.reward_description)
                                                    .then(success => {
                                                        if (!success) {
                                                            console.warn(`Failed to update description for bundle ${existingBundleId}`);
                                                        }
                                                    })
                                                    .catch(err => console.warn('Error updating description:', err))
                                            );
                                        }
                                    }
                                    
                                    if (changes.price && reward.reward_price) {
                                        updatePromises.push(
                                            updateBundlePriceOnly(existingBundleId, reward.reward_price, reward.products)
                                                .then(success => {
                                                    if (!success) {
                                                        console.warn(`Failed to update price for bundle ${existingBundleId}`);
                                                    }
                                                })
                                                .catch(err => console.warn('Error updating price:', err))
                                        );
                                    }
                                    
                                    // Wait for updates to complete
                                    if (updatePromises.length > 0) {
                                        await Promise.allSettled(updatePromises);
                                    }
                                    
                                    // Return existing bundle info (no need to recreate)
                                    bundleInfo = {
                                        bundle_product_id: existingBundleId,
                                        bundle_product_handle: reward.bundle_product_handle
                                    };
                                }
                            } else {
                                // No existing bundle - create new one
                                const updateOptions = {
                                    updateProducts: true,
                                    updateDescription: true,
                                    updatePrice: true
                                };
                                bundleInfo = await createOrUpdateRewardBundle(
                                    reward, 
                                    null,
                                    updateOptions,
                                    oldReward
                                );
                            }
                            
                            if (bundleInfo && bundleInfo.bundle_product_id) {
                                    // Ensure we have both bundle_product_id and bundle_product_handle
                                    const finalBundleId = bundleInfo.bundle_product_id;
                                    const finalBundleHandle = bundleInfo.bundle_product_handle || null;
                                    
                                    if (!finalBundleHandle) {
                                        console.warn(`[CampaignEdit] Bundle created but handle not returned for reward "${reward.reward_title || `Reward ${i + 1}`}". Bundle ID: ${finalBundleId}`);
                                    }
                                    
                                    // Build updated reward with bundle info
                                    const updatedReward = { 
                                        ...reward, 
                                        bundle_product_id: finalBundleId,
                                        bundle_product_handle: finalBundleHandle
                                    };
                                    
                                    // Also store bundle info in each product object within the reward
                                    if (updatedReward.products && updatedReward.products.length > 0) {
                                        updatedReward.products = updatedReward.products.map(product => ({
                                            ...product,
                                            bundle_product_id: finalBundleId,
                                            bundle_product_handle: finalBundleHandle
                                        }));
                                    }

                                    // OPTIMIZATION: Only update inventory tracking if quantity changed
                                    if (bundleInfo.bundle_product_id && changes.quantity) {
                                        try {
                                            const hasQuantityLimit = reward.quantity_limit && 
                                                reward.quantity_limit !== '' && 
                                                parseInt(reward.quantity_limit) > 0;
                                            
                                            if (!hasQuantityLimit) {
                                                // When quantity_limit is empty (unlimited), ensure bundle product variants do NOT track inventory
                                                const bundleInventoryResult = await updateMainProductVariantsInventoryTracking(bundleInfo.bundle_product_id);
                                            } else {
                                                // When quantity_limit is set, update reward product variants (not bundle variants)
                                                const inventoryResult = await updateRewardVariantsInventoryTracking(reward);
                                                
                                            }
                                        } catch (inventoryError) {
                                            console.error(`Error updating inventory tracking for reward "${reward.reward_title || `Reward ${i + 1}`}":`, inventoryError);
                                            // Don't fail the save operation if inventory update fails
                                            // Log error but continue
                                        }
                                    }

                                    // OPTIMIZATION: Only update image if it changed
                                    const imageChanged = oldReward && (oldReward.reward_image || '') !== (reward.reward_image || '');
                                    if (reward.reward_image && bundleInfo.bundle_product_id && (imageChanged || !oldReward)) {
                                        try {
                                            // Check if reward_image is a URL (already uploaded) or needs to be uploaded
                                            // If it's a URL starting with http/https, it's already uploaded
                                            if (reward.reward_image.startsWith('http://') || reward.reward_image.startsWith('https://')) {
                                                // Image is already uploaded, create MediaImage and update product
                                                const mediaImageId = await createMediaImageFromUrl(reward.reward_image, reward.reward_title || `Reward ${i + 1}`);
                                                
                                                if (mediaImageId) {
                                                    const productUpdated = await updateBundleProductImage(bundleInfo.bundle_product_id, mediaImageId);
                                                } else {
                                                    console.warn(`Could not create MediaImage from URL for reward "${reward.reward_title || `Reward ${i + 1}`}"`);
                                                }
                                            }
                                        } catch (imageError) {
                                            console.error(`Error updating bundle product image for reward "${reward.reward_title || `Reward ${i + 1}`}":`, imageError);
                                            // Don't fail the save operation if image update fails
                                            // Log error but continue
                                        }
                                    }

                                    // Store reward information on bundle product for validation
                                    // This allows validation even when main product is not in cart
                                    if (bundleInfo.bundle_product_id) {
                                        try {
                                            const rewardMetafieldData = {
                                                campaign_id: latestFormValues.campaign_id || campaignId || storedCampaignId,
                                                reward_id: reward.id || reward.reward_id,
                                                quantity_limit: reward.quantity_limit || null,
                                                main_product_id: currentPrimaryProduct?.shopify_product_id || null,
                                                campaign_type: '1' // Crowdfunding
                                            };
                                            
                                            await createProductMetafield(
                                                bundleInfo.bundle_product_id,
                                                rewardMetafieldData,
                                                'reward_data' // Use different key for reward bundles
                                            );
                                        } catch (rewardMetafieldError) {
                                            console.error(`Error storing reward metafield on bundle product for reward "${reward.reward_title || `Reward ${i + 1}`}":`, rewardMetafieldError);
                                            // Don't fail the save operation if metafield update fails
                                        }
                                    }

                                    // Note: Bundle product inventory is no longer updated.
                                    // Reward quantity limits are now handled via product validation function (similar to pre-orders).
                                    // Inventory tracking is disabled for reward bundles to keep inventory unlimited.
                                    
                                    return { reward, updatedReward, bundleInfo };
                                } else {
                                    console.warn(`No bundle info returned for reward "${reward.reward_title || `Reward ${i + 1}`}"`);
                                    return { reward, updatedReward: reward, bundleInfo: null };
                                }
                            } catch (bundleError) {
                                console.error(`Could not create/update bundle for reward "${reward.reward_title || `Reward ${i + 1}`}":`, bundleError);
                                
                                // Check if error is about bundle products being used as components
                                const errorMessage = bundleError.message || bundleError.toString();
                                if (errorMessage.includes('bundle') && errorMessage.includes('component')) {
                                    // Show user-friendly error message about bundle products
                                    showToastMessage(
                                        `Cannot create bundle for reward "${reward.reward_title || `Reward ${i + 1}`}": Bundle products cannot be used as components. Please select regular products instead.`,
                                        true
                                    );
                                } else if (errorMessage.includes('No valid components')) {
                                    // Show user-friendly error message about no valid components
                                    showToastMessage(
                                        `Cannot create bundle for reward "${reward.reward_title || `Reward ${i + 1}`}": No valid products found. Please ensure you have selected regular products (not bundles) with valid variants.`,
                                        true
                                    );
                                } else {
                                    // Generic error message
                                    showToastMessage(
                                        `Could not create bundle for reward "${reward.reward_title || `Reward ${i + 1}`}": ${errorMessage}`,
                                        true
                                    );
                                }
                                
                                // Continue with other rewards even if one fails
                                // Keep the existing bundle_product_id if it exists, so we don't lose it
                                if (reward.bundle_product_id) {
                                    console.warn(`Keeping existing bundle_product_id for reward "${reward.reward_title || `Reward ${i + 1}`}": ${reward.bundle_product_id}`);
                                }
                                
                                return { reward, updatedReward: reward, bundleInfo: null };
                            }
                        });
                    
                    // Wait for all rewards to be processed in parallel
                    const rewardResults = await Promise.allSettled(rewardPromises);
                    
                    // Update rewards array with results
                    rewardResults.forEach((result, i) => {
                        if (result.status === 'fulfilled' && result.value) {
                            const { reward, updatedReward } = result.value;
                            updatedRewardsWithBundles = updatedRewardsWithBundles.map(r => 
                                r.id === reward.id ? updatedReward : r
                            );
                        }
                    });
                } catch (error) {
                    console.error('Error creating reward bundles:', error);
                    // Show warning but continue with save
                    showToastMessage('Some bundles could not be created, but campaign will still be saved', true);
                }
            }

            // Structure products data - only primary product stored in JSON fields
            // Backend expects product_handle in campaign_products array
            const campaignProducts = currentPrimaryProduct ? [{
                shopify_product_id: currentPrimaryProduct.shopify_product_id || '',
                product_title: currentPrimaryProduct.product_title || '',
                product_image: currentPrimaryProduct.product_image || '',
                product_handle: currentPrimaryProduct.product_handle || null, // Include product handle
                is_primary_product: '1',
                is_active: '1'
            }] : [];

            // Structure rewards data as JSON object for campaigns.rewards field
            // Format: Reward { all fields } + Product { bundle_product_id, variant_ids, image, name }
            // Only selected variants are saved in products
            const campaignRewards = updatedRewardsWithBundles.map(reward => {
                console.log('reward', reward);
                // Build products array with only selected variants
                const products = (reward.products || []).map(product => {
                    // Extract numeric IDs from Shopify GID format
                    const extractShopifyId = (gid) => {
                        if (!gid) return null;
                        if (typeof gid === 'number') return gid;
                        if (typeof gid === 'string' && /^\d+$/.test(gid)) return parseInt(gid);
                        const match = gid.match(/\/(\d+)$/);
                        return match ? parseInt(match[1]) : null;
                    };

                    // Get selected variant IDs (only these will be saved)
                    const selectedVariants = product.selectedVariants || [];
                    
                    // Extract variant IDs (numeric) from selected variants
                    const variantIds = selectedVariants
                        .map(variantId => extractShopifyId(variantId))
                        .filter(id => id !== null);

                    // Build product object similar to main product but with multi-variant support
                    // Product contains: variant_ids (selected only), image, name, bundle info
                    // IMPORTANT: Preserve bundle_product_id and bundle_product_handle from product or reward level
                    return {
                        shopify_product_id: extractShopifyId(product.shopify_product_id), // Product ID (numeric)
                        product_title: product.product_title || '',
                        product_image: product.product_image || '',
                        product_handle: product.product_handle || null, // Product handle
                        variant_ids: variantIds, // Array of selected variant IDs (numeric) - only selected variants
                        // Preserve bundle information - check both product and reward level
                        bundle_product_id: product.bundle_product_id || reward.bundle_product_id || null,
                        bundle_product_handle: product.bundle_product_handle || reward.bundle_product_handle || null,
                        // Store variant details for selected variants only
                        variants: (product.variants || [])
                            .filter(variant => {
                                // Only include variants that are in selectedVariants
                                if (selectedVariants.length === 0) return false;
                                const variantId = variant.id;
                                return selectedVariants.some(selectedId => {
                                    const selectedIdStr = String(selectedId);
                                    return variantId === selectedIdStr || 
                                           variantId.endsWith(`/${selectedIdStr}`) ||
                                           selectedIdStr.endsWith(`/${variantId.split('/').pop()}`);
                                });
                            })
                            .map(variant => ({
                                id: extractShopifyId(variant.id), // Numeric variant ID
                                title: variant.title || '',
                                price: variant.price || '',
                                available: variant.available !== false
                            })),
                        // Preserve selectedVariants in GID format for frontend compatibility
                        selectedVariants: selectedVariants
                    };
                }).filter(product => product.shopify_product_id !== null); // Filter out invalid products

                // Calculate item_count (total number of products in reward)
                const item_count = products.length;
                
                // Calculate selected_variant_count (total number of selected variants across all products)
                const selected_variant_count = products.reduce((total, product) => {
                    return total + (product.variant_ids ? product.variant_ids.length : 0);
                }, 0);

                // Build reward object with all fields
                // Ensure bundle_product_id and bundle_product_handle are included for proper bundle management
                // IMPORTANT: Include ALL fields needed for blocks/liquid templates
                return {
                    id: reward.id || null, // Keep ID for updates (temporary ID for new rewards)
                    reward_id: reward.reward_id || reward.id || null, // Alternative ID field
                    reward_title: reward.reward_title || '',
                    reward_description: reward.reward_description || '',
                    reward_image: reward.reward_image || '',
                    quantity_limit: reward.quantity_limit || null, // null means unlimited
                    reward_price: reward.reward_price || '',
                    reward_price_numeric: reward.reward_price || 0, // Numeric price for calculations
                    is_active: reward.is_active || '1',
                    bundle_product_id: reward.bundle_product_id || null, // Bundle product ID (GID format) - used to update existing bundle
                    bundle_product_handle: reward.bundle_product_handle || null, // Bundle product handle - CRITICAL for blocks/liquid templates
                    item_count: item_count, // Total number of products in this reward
                    selected_variant_count: selected_variant_count, // Total number of selected variants across all products
                    products: products, // Array of products with selected variants only (includes bundle info)
                    show_strikethrough_price: reward.show_strikethrough_price || false
                };
            });

            // Structure data according to API expectations - ensure all dynamic values are included
            // Use latestFormValues to get the latest form state - this captures all current field values
            const campaignData = {
                // Campaign ID (for updates)
                ...(latestFormValues.id && !isNewCampaign ? { id: latestFormValues.id } : {}),
                
                // Basic campaign fields - use latestFormValues to ensure latest state
                client_id: latestFormValues.client_id || installData?.shop_info?.id || 1,
                campaign_name: (latestFormValues.campaign_name || '').trim(),
                campaign_description: latestFormValues.campaign_description || '',
                campaign_banner_link: latestFormValues.campaign_banner_link || '',
                campaign_type: latestFormValues.campaign_type || '',
                campaign_status: latestFormValues.campaign_status || '0',
                
                // Crowdfunding specific fields
                goal_type: latestFormValues.goal_type || null,
                number_of_items: latestFormValues.number_of_items || null,
                total_amount: latestFormValues.total_amount || null,
                is_auto_end_campaign: latestFormValues.is_auto_end_campaign || '0',
                is_add_start_point: latestFormValues.addStartPoint ? '1' : '0',
                initial_fund: latestFormValues.initial_fund || 0,
                initial_backers: latestFormValues.initial_backers || 0,
                current_backers: latestFormValues.current_backers || 0,
                initial_backers_count: latestFormValues.initial_backers_count || 0,
                
                // Date and time fields
                duration_type: latestFormValues.duration_type || '0',
                campaign_start_datetime: latestFormValues.campaign_start_datetime
                    ? formatDateTimeForMySQL(latestFormValues.campaign_start_datetime)
                    : formatDateTimeForMySQL(new Date().toISOString()),
                campaign_end_datetime: (latestFormValues.campaign_end_datetime && latestFormValues.campaign_end_datetime.trim() !== '')
                    ? formatDateTimeForMySQL(latestFormValues.campaign_end_datetime)
                    : null,
                // Timezone only for Crowdfunding and Product Drop, not PreSales
                timezone: (latestFormValues.campaign_type === '1' || latestFormValues.campaign_type === '2') 
                    ? (latestFormValues.timeZone || 'UTC') 
                    : null,
                
                // PreSales specific fields
                is_limit_inventory: latestFormValues.limitInventory ? '1' : '0',
                available_inventory: latestFormValues.availableInventory,
                product_limit_per_order: (latestFormValues.enableProductLimitPerOrder && latestFormValues.productLimitPerOrder) ? parseInt(latestFormValues.productLimitPerOrder) : null,
                is_show_presale_target: latestFormValues.showPresalesTarget ? '1' : '0',
                initial_items_sold_preSale: parseInt(latestFormValues.initial_items_sold_preSale) || 0,
                
                // Product Drop specific fields
                product_drop_per_customer_limit: parseInt(latestFormValues.perCustomerLimit) || 1,
                product_drop_total_limit: latestFormValues.totalDropLimit ? parseInt(latestFormValues.totalDropLimit) : null,
                
                // Tags - ensure arrays are properly converted to comma-separated strings
                order_tags: Array.isArray(latestFormValues.orderTags) 
                    ? latestFormValues.orderTags.filter(tag => tag && tag.trim()).join(',')
                    : (latestFormValues.orderTags || ''),
                customer_tags: Array.isArray(latestFormValues.customerTags)
                    ? latestFormValues.customerTags.filter(tag => tag && tag.trim()).join(',')
                    : (latestFormValues.customerTags || ''),
                
                // Properly structured products and rewards
                campaign_products: campaignProducts,
                rewards: campaignRewards // Store as JSON in campaigns.rewards field
            };

            let response;
            // For new campaigns: if we have stored ID from previous save, use update; otherwise create
            // For existing campaigns: always use update
            if (isNewCampaign) {
                if (storedCampaignId) {
                    // Campaign was already created, use update API
                    response = await campaignAPI.updateCampaign(storedCampaignId, campaignData);
                } else {
                    // First time saving new campaign, use create API
                response = await campaignAPI.createCampaign(campaignData);
                }
            } else {
                response = await campaignAPI.updateCampaign(campaignId, campaignData);
            }

            if (response.success) {
                // Get campaign ID from response - prioritize response data for new campaigns
                // Response format: { success: true, message: "...", data: { id: 95 } }
                let finalCampaignId = null;
                
                // For new campaigns, prioritize ID from create response
                if (isNewCampaign && !storedCampaignId) {
                    // First time creating campaign - get ID from create response
                    finalCampaignId = response.data?.id || response.data?.campaign?.id || null;
                } else {
                    // For updates or subsequent saves, use response ID or existing ID
                    finalCampaignId = response.data?.id || response.data?.campaign?.id || latestFormValues.id || campaignId || storedCampaignId;
                }
                
                // Ensure we have a valid campaign ID
                if (!finalCampaignId) {
                    console.warn('[CampaignEdit] No campaign ID found in response or existing data:', {
                        responseData: response.data,
                        latestFormValuesId: latestFormValues.id,
                        campaignId: campaignId,
                        storedCampaignId: storedCampaignId
                    });
                }
                
                // Manage product metafields - store entire campaign object using helper functions
                if (currentPrimaryProduct?.shopify_product_id) {
                    const currentProductId = currentPrimaryProduct.shopify_product_id;
                    const previousProductId = previousProductIdRef.current;
                    const previousMetafieldId = previousMetafieldIdRef.current;
                    
                    // Prepare metafield data - format reward_price with currency symbol for metafield storage
                    // Create a deep copy of formValues and format reward prices
                    // IMPORTANT: Preserve ALL reward data including bundle_product_handle and bundle_product_id
                    // Use updatedRewardsWithBundles to ensure bundle handles are included in metafield
                    const formValuesForMetafield = {
                        ...latestFormValues,
                        campaign_id: finalCampaignId || null,
                        rewards: (updatedRewardsWithBundles || latestFormValues.rewards || []).map(reward => {
                            // Calculate item_count and selected_variant_count for metafield
                            const products = reward.products || [];
                            const item_count = products.length;
                            const selected_variant_count = products.reduce((total, product) => {
                                const selectedVariants = product.selectedVariants || product.variants || [];
                                return total + (Array.isArray(selectedVariants) ? selectedVariants.length : 0);
                            }, 0);
                            
                            // Get bundle info from reward (should be set during bundle creation)
                            const bundleProductId = reward.bundle_product_id || null;
                            const bundleProductHandle = reward.bundle_product_handle || null;
                            
                            // Preserve all reward fields including bundle information
                            // Map products to ensure all bundle data is preserved
                            const productsWithBundleData = products.map(product => ({
                                ...product,
                                // Ensure bundle_product_id and bundle_product_handle are preserved from product or reward level
                                // Priority: product level > reward level
                                bundle_product_id: product.bundle_product_id || bundleProductId || null,
                                bundle_product_handle: product.bundle_product_handle || bundleProductHandle || null
                            }));
                            
                            return {
                                ...reward,
                                // Explicitly preserve bundle fields - use values from updatedRewardsWithBundles
                                bundle_product_id: bundleProductId,
                                bundle_product_handle: bundleProductHandle,
                                reward_price: reward.reward_price 
                                    ? formatCurrency(parseFloat(reward.reward_price)) 
                                    : formatCurrency(0),
                                // Keep numeric value for calculations
                                reward_price_numeric: reward.reward_price || 0,
                                // Add item count fields
                                item_count: item_count,
                                selected_variant_count: selected_variant_count,
                                // Preserve products with all bundle data
                                products: productsWithBundleData
                            };
                        })
                    };
                    
                    // Prepare metafield data - only store formValues (no duplication)
                    // Include campaign ID in metafield JSON - ALWAYS use finalCampaignId from response
                    const completeCampaignData = {
                        formValues: formValuesForMetafield,
                        savedAt: new Date().toISOString(),
                        campaignId: finalCampaignId || null, // Always use the ID from response for new campaigns
                    };
                    
                    try {
                        // Get current campaign type for tracking
                        const currentCampaignType = latestFormValues.campaign_type;
                        
                        // OPTIMIZATION: Run metafield operations in parallel where possible
                        // Check if product changed
                        if (previousProductId && previousProductId !== currentProductId) {
                            
                            // OPTIMIZATION: Delete old metafields in parallel
                            const deletePromises = [];
                            
                            if (previousMetafieldId && previousProductId) {
                                deletePromises.push(
                                    deleteProductMetafield(previousMetafieldId, previousProductId)
                                        .catch(error => console.warn('[CampaignEdit] Could not delete campaign_data metafield from old product:', error))
                                );
                            }
                            
                            deletePromises.push(
                                deleteCampaignCountMetafield(previousProductId)
                                    .catch(error => console.warn('[CampaignEdit] Could not delete Campign_count metafield from old product:', error))
                            );
                            
                            // Wait for deletions to complete
                            await Promise.allSettled(deletePromises);
                            
                            // Clear refs for old product
                            previousMetafieldIdRef.current = null;
                            
                            // Create campaign_data metafield on new product
                            const newMetafield = await createProductMetafield(currentProductId, completeCampaignData);
                            if (newMetafield?.id) {
                                previousMetafieldIdRef.current = newMetafield.id;
                            }
                            
                            // Only create/reset campaign count metafield on FIRST save of NEW campaign
                            // Do NOT reset when product changes - preserve existing count data
                            if (isNewCampaign && !storedCampaignId) {
                                const initialCampaignCountData = {
                                    Crowdfunding: {
                                        rewardCount: 0,
                                        rewardCounts: {}
                                    },
                                    PreSales: {
                                        itemSold: 0
                                    },
                                    'Product Drop': {
                                        totalLimit: 0,
                                        customerLimit: {}
                                    }
                                };
                                
                                try {
                                    await createOrUpdateCampaignCountMetafield(currentProductId, initialCampaignCountData);
                                } catch (error) {
                                    console.warn('[CampaignEdit] Could not create/reset Campign_count metafield on new product:', error);
                                }
                            }
                        } else if (currentProductId) {
                            // Product same: update existing metafield or create new one
                            if (previousMetafieldId) {
                                // Update existing metafield
                                const updatedMetafield = await updateProductMetafield(currentProductId, completeCampaignData);
                                if (updatedMetafield?.id) {
                                    previousMetafieldIdRef.current = updatedMetafield.id;
                                }
                                
                                // Do NOT reset campaign count metafield on updates
                                // Campaign count metafield is only reset on FIRST save of NEW campaign
                                // Campaign count metafield is updated by webhooks when orders are created
                            } else {
                                // Create new metafield (first time saving)
                                const newMetafield = await createProductMetafield(currentProductId, completeCampaignData);
                                if (newMetafield?.id) {
                                    previousMetafieldIdRef.current = newMetafield.id;
                                }
                                
                                // For new campaigns, also create/reset campaign count metafield with initial empty structure
                                if (isNewCampaign && !storedCampaignId) {
                                    const initialCampaignCountData = {
                                        Crowdfunding: {
                                            rewardCount: 0,
                                            rewardCounts: {}
                                        },
                                        Presales: {
                                            rewardCount: 0,
                                            rewardCounts: {}
                                        },
                                        'Product-drop': {
                                            rewardCount: 0,
                                            rewardCounts: {}
                                        }
                                    };
                                    
                                    try {
                                        await createOrUpdateCampaignCountMetafield(currentProductId, initialCampaignCountData);
                                    } catch (error) {
                                        console.warn('[CampaignEdit] Could not create/reset Campign_count metafield for new campaign:', error);
                                    }
                                }
                            }
                        }
                        
                        // Update tracked product ID and campaign type
                        previousProductIdRef.current = currentProductId;
                        previousCampaignTypeRef.current = currentCampaignType;

                        // Campaign count metafield is reset ONLY when:
                        // 1. New campaign is created (first save only - when isNewCampaign && !storedCampaignId)
                        // For all other scenarios (product changes, campaign type changes, setting updates),
                        // campaign count metafield is NOT reset
                        // Campaign count metafield is updated by webhooks when orders are created
                        // This ensures campaign counts reflect actual order data, not form values
                    } catch (metafieldError) {
                        console.error('Error managing product metafield:', metafieldError);
                        // Don't fail the save operation if metafield update fails
                        // Show warning but continue
                        showToastMessage('Campaign saved, but metafield update failed', true);
                    }
                } else if (previousProductIdRef.current) {
                    // OPTIMIZATION: Product was removed - delete ALL app-related metafields from old product in parallel
                    const previousMetafieldId = previousMetafieldIdRef.current;
                    const previousProductId = previousProductIdRef.current;
                    if (previousProductId) {
                        try {
                            
                            // OPTIMIZATION: Delete metafields in parallel for faster execution
                            const deletePromises = [];
                            
                            if (previousMetafieldId) {
                                deletePromises.push(
                                    deleteProductMetafield(previousMetafieldId, previousProductId)
                                        .catch(error => console.warn('[CampaignEdit] Could not delete campaign_data metafield from removed product:', error))
                                );
                            }
                            
                            deletePromises.push(
                                deleteCampaignCountMetafield(previousProductId)
                                    .catch(error => console.warn('[CampaignEdit] Could not delete Campign_count metafield from removed product:', error))
                            );
                            
                            // Wait for all deletions to complete
                            await Promise.allSettled(deletePromises);
                        } catch (deleteError) {
                            console.warn('Could not delete metafields from removed product:', deleteError);
                        } finally {
                            // Clear refs regardless of success/failure
                            previousProductIdRef.current = null;
                            previousMetafieldIdRef.current = null;
                        }
                    }
                }

                // OPTIMIZATION: For PreSales campaigns, only update inventory tracking (turn off tracking)
                // No need to update inventory quantity or policy separately - just turn off tracking
                // This is done in a single optimized GraphQL call
                if (latestFormValues.campaign_type === '0' && currentPrimaryProduct?.shopify_product_id) {
                    // PreSales: Only turn off inventory tracking, no quantity updates needed
                    const inventoryTrackingPromise = (async () => {
                        try {
                            const inventoryResult = await updateMainProductVariantsInventoryTracking(currentPrimaryProduct.shopify_product_id);
                            
                            if (inventoryResult.failed > 0) {
                                console.warn(`[CampaignEdit] Failed to update inventory tracking for ${inventoryResult.failed} variant(s) of main product:`, inventoryResult.errors);
                            }
                        } catch (inventoryError) {
                            console.error('[CampaignEdit] Error updating inventory tracking for main product:', inventoryError);
                            // Don't fail the save operation if inventory update fails
                        }
                    })();
                    
                    // Don't await - let it run in background (non-blocking)
                    inventoryTrackingPromise.catch(error => {
                        console.error('[CampaignEdit] Inventory tracking promise error (non-critical):', error);
                    });
                }
                
                // OPTIMIZATION: For Product Drop campaigns, only update inventory tracking
                if (latestFormValues.campaign_type === '2' && currentPrimaryProduct?.shopify_product_id) {
                    const inventoryTrackingPromise = (async () => {
                        try {
                            const inventoryResult = await updateMainProductVariantsInventoryTracking(currentPrimaryProduct.shopify_product_id);
                            
                            if (inventoryResult.failed > 0) {
                                console.warn(`[CampaignEdit] Failed to update inventory tracking for ${inventoryResult.failed} variant(s) of main product:`, inventoryResult.errors);
                            }
                        } catch (inventoryError) {
                            console.error('[CampaignEdit] Error updating inventory tracking for main product:', inventoryError);
                        }
                    })();
                    
                    inventoryTrackingPromise.catch(error => {
                        console.error('[CampaignEdit] Inventory tracking promise error (non-critical):', error);
                    });
                }

                // Show success toast - following Shopify guidelines: short, noun + verb pattern
                const successMessage = response.message || 'Campaign saved';
                showToastMessage(successMessage, false);

                // Hide banner on successful save
                setShowBanner(false);
                setBannerErrors([]);

                // Reset unsaved changes flag immediately
                setHasUnsavedChanges(false);
                setProductsChanged(false);
                
                // Hide save bar after successful save
                setSaveBarVisible(false);
                
                // Hide SaveBar after successful save (if using Shopify SaveBar API)
                if (shopify && shopify.saveBar) {
                    shopify.saveBar.hide('campaign-edit-save-bar');
                }
                
                // Mark as initial load to prevent immediate change detection
                isInitialLoadRef.current = true;

                // For new campaigns: store the campaign ID in state after first save and redirect to home
                const isFirstSave = isNewCampaign && finalCampaignId && !storedCampaignId;
                if (isFirstSave) {
                    // First time saving new campaign - store the ID in state
                    setStoredCampaignId(finalCampaignId);
                }

                // Prepare saved values for immediate reset (to hide save bar immediately)
                // Include updated rewards with bundle information so formik knows the latest state
                // Create immutable copy of products array to ensure Formik detects future changes
                const savedValues = { 
                    ...latestFormValues,
                    products: latestFormValues.products ? [...latestFormValues.products] : [],
                    id: finalCampaignId || storedCampaignId,
                    rewards: updatedRewardsWithBundles // Include rewards with updated bundle_product_id and bundle_product_handle
                };

                // IMMEDIATELY reset Formik to hide save bar - don't wait for API reload
                // This ensures the save bar hides right away, providing instant feedback
                setFormikInitialValues(savedValues);
                
                // Reset formik form - this will set dirty to false
                // Create immutable copy to ensure Formik properly tracks future changes
                const immutableSavedValues = {
                    ...savedValues,
                    products: savedValues.products ? [...savedValues.products] : []
                };
                formik.resetForm({ 
                    values: immutableSavedValues,
                    initialValues: immutableSavedValues,
                    resetErrors: true,
                    resetTouched: true,
                    resetStatus: true
                });

                // Update originalData immediately with saved values (no API reload)
                setOriginalData(savedValues);
                
                // Update metafield refs based on saved values (no API call needed)
                // Metafield was already created/updated during save process above
                const primaryProduct = (savedValues.products || [])[0];
                if (primaryProduct?.shopify_product_id) {
                    previousProductIdRef.current = primaryProduct.shopify_product_id;
                    // Metafield ID was already set during save process, so we keep it
                    // If metafield was just created, the ID is already in previousMetafieldIdRef
                }
                
                // Allow change detection to resume after a brief delay
                setTimeout(() => {
                    isInitialLoadRef.current = false;
                }, 200);

                // Redirect to home page after successfully creating a new campaign (first save only)
                if (isFirstSave) {
                    // Small delay to ensure toast is visible before redirect
                    setTimeout(() => {
                        const path = '/app';
                        // Update parent window URL using App Bridge History
                        if (app) {
                            try {
                                const history = History.create(app);
                                history.dispatch(History.Action.PUSH, path);
                            } catch (error) {
                                console.warn('[CampaignEdit] App Bridge History failed, using react-router:', error);
                            }
                        }
                        // Navigate to home page
                        navigate(path);
                    }, 1000); // 1 second delay to show success toast
                }
            } else {
                // Show error toast - following Shopify guidelines: short messages
                const errorMessage = response.message || 'Save failed';
                showToastMessage(errorMessage, true);
            }
        } catch (error) {
            console.error('Error saving campaign:', error);
            // Show error toast - following Shopify guidelines: short messages
            const errorMessage = error.message || 'Save failed';
            showToastMessage(errorMessage, true);
        } finally {
            // setLoading(false);
            setSaving(false);
        }
    }, [campaignId, isNewCampaign, navigate, formik, showToastMessage, installData, app, formatDateTimeForMySQL, validateRequiredFields, transformApiDataToFormData, storedCampaignId, updateMainProductInventory, updateBundleProductInventory, updateVariantInventoryPolicy]);

    const handleDiscard = useCallback(async () => {
        // Hide banner when discarding changes
        setShowBanner(false);
        setBannerErrors([]);
        
        // Set discarding state to show loader
        setDiscarding(true);
        
        // Mark as initial load to prevent immediate change detection
        isInitialLoadRef.current = true;

        try {
            let resetData;
            
            if (campaignId && campaignId !== 'new') {
                // For existing campaigns, fetch fresh data from API to ensure we reset to latest DB state
                try {
                    const response = await campaignAPI.getCampaign(campaignId);
                    
                    if (!response.success || !response.data) {
                        throw new Error(response.message || 'Failed to fetch campaign data');
                    }

                    // Transform API response data to form data format
                    resetData = transformApiDataToFormData(response.data);
                } catch (error) {
                    console.error('Error fetching campaign data on discard:', error);
                    // Fallback to originalData if API call fails
                    if (originalData) {
                        resetData = originalData;
                    } else {
                        // No fallback data available
                        showToastMessage('Failed to discard changes. Please try again.', true);
                        isInitialLoadRef.current = false;
                        setDiscarding(false);
                        return;
                    }
                }
            } else {
                // For new campaigns, reset to default values
                resetData = getInitialValues();
                setOriginalData(null);
            }
            
            // Update originalData with reset data (for existing campaigns)
            if (campaignId && campaignId !== 'new') {
                setOriginalData(resetData);
            }
            
            // Reset previous campaign type ref to match reset data
            if (resetData?.campaign_type) {
                previousCampaignTypeRef.current = resetData.campaign_type;
            }
            
            // Update Formik initial values
            setFormikInitialValues(resetData);
            
            // Reset Formik form - this ensures Formik recognizes the form is clean
            // Create immutable copy to ensure Formik properly tracks future changes
            const immutableResetData = {
                ...resetData,
                products: resetData.products ? [...resetData.products] : []
            };
            formik.resetForm({ 
                values: immutableResetData,
                initialValues: immutableResetData,
                resetErrors: true,
                resetTouched: true,
                resetStatus: true
            });
            
            // Reset unsaved changes flag immediately
            setHasUnsavedChanges(campaignId === 'new' || campaignId === null);
            setProductsChanged(false); // Reset productsChanged flag after discard
            
            // Hide save bar after discard
            setSaveBarVisible(false);
            
            // Small delay to ensure formik state updates before allowing change detection
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Show success toast
            showToastMessage('Changes discarded', false);
        } catch (error) {
            console.error('Error during discard:', error);
            showToastMessage('Failed to discard changes. Please try again.', true);
        } finally {
            // Clear discarding state
            setDiscarding(false);
            // Allow change detection to resume
            isInitialLoadRef.current = false;
            
            // Note: Don't hide SaveBar here - let it be controlled by the `open` prop
            // The SaveBar will automatically hide when open={false} (when formik.dirty and productsChanged are both false)
        }
    }, [campaignId, originalData, formik, showToastMessage, transformApiDataToFormData]);

    // Get money format from install data
    const moneyFormat = useMemo(() => {
        return installData?.shop_info?.money_format || '${{amount}}';
    }, [installData]);

    // Extract currency prefix from money_format (e.g., "$" from "${{amount}}")
    const currencyPrefix = useMemo(() => {
        if (!moneyFormat) return '$';
        // Extract everything before {{amount}}
        const match = moneyFormat.match(/^([^{]*)/);
        return match ? match[1] : '$';
    }, [moneyFormat]);

    const formatCurrency = useCallback((amount, decimals = 0) => {
        return formatMoney(amount, moneyFormat, { decimals });
    }, [moneyFormat]);

    // Navigation handler for breadcrumbs and back button - MUST be before any early returns
    const handleNavigateToHome = useCallback(() => {
        // Check if there are unsaved changes
        if (hasUnsavedChanges) {
            setShowNavigationModal(true);
            setPendingNavigation(() => () => {
                const path = '/app';
                // Update parent window URL using App Bridge History
                if (app) {
                    try {
                        const history = History.create(app);
                        history.dispatch(History.Action.PUSH, path);
                    } catch (error) {
                        console.warn('[CampaignEdit] App Bridge History failed, using react-router:', error);
                    }
                }
                // Update iframe URL using react-router
                navigate(path);
            });
            return;
        }

        const path = '/app';
        // Update parent window URL using App Bridge History
        if (app) {
            try {
                const history = History.create(app);
                history.dispatch(History.Action.PUSH, path);
            } catch (error) {
                console.warn('[CampaignEdit] App Bridge History failed, using react-router:', error);
            }
        }
        // Update iframe URL using react-router
        navigate(path);
    }, [navigate, app, hasUnsavedChanges]);

    // Handle navigation confirmation modal - discard changes then navigate
    const handleConfirmNavigation = useCallback(async () => {
        setShowNavigationModal(false);
        navigationBlockedRef.current = false;
        
        // Hide save bar immediately when user confirms navigation
        setSaveBarVisible(false);
        
        // Discard changes first (this will also hide save bar)
        await handleDiscard();
        
        // Small delay to ensure save bar is hidden before navigation
        await new Promise(resolve => setTimeout(resolve, 150));
        
        // Then navigate after discard completes and save bar is hidden
        if (pendingNavigation) {
            pendingNavigation();
            setPendingNavigation(null);
        }
    }, [pendingNavigation, handleDiscard]);

    const handleCancelNavigation = useCallback(() => {
        setShowNavigationModal(false);
        setPendingNavigation(null);
        navigationBlockedRef.current = false;
    }, []);

    // Save bar visibility is controlled by formik.dirty via data-save-bar attribute
    // With enableReinitialize: true, formik properly tracks all field changes
    // The save bar will automatically show when formik.dirty becomes true after any field change

    // Show loading spinner while data is being loaded
    if (loading || !dataLoaded) {
        return (
            <>
            <PageTitleBar title={isNewCampaign ? "Create Campaign" : "Edit Campaign"} />
            <Page>
                <Layout>
                    <Layout.Section>
                        <Card>
                            <Box padding="800" style={{ textAlign: 'center' }}>
                                <Spinner size="large" />
                                <Text variant="bodyMd" tone="subdued" as="p">
                                    {isNewCampaign ? 'Preparing new campaign...' : 'Loading campaign data...'}
                                </Text>
                            </Box>
                        </Card>
                    </Layout.Section>
                </Layout>
            </Page>
            </>
        );
    }

    return (
        <>
            <PageTitleBar title={isNewCampaign ? "Create Campaign" : "Edit Campaign"} />
            <PageTitleBar
                title="Home"
                subtitle="Campaign Management Dashboard"
            />
            {/* Loading overlay for save/discard operations */}
            {(saving || discarding) && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                    <Spinner size="large" />
                    <Text variant="bodyMd" fontWeight="semibold" as="p">
                        {saving ? 'Saving campaign...' : 'Discarding changes...'}
                    </Text>
                </div>
            )}
            <Page>
                {showBanner && bannerErrors.length > 0 && (
                    <Box paddingBlockEnd="400">
                        <Banner
                            tone="critical"
                            title="Please fill in all required fields"
                            onDismiss={() => setShowBanner(false)}
                        >
                            <p>The following fields are required and cannot be empty:</p>
                            <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
                                {bannerErrors.map((error, index) => (
                                    <li key={index}>
                                        <strong>{error}</strong>
                                    </li>
                                ))}
                            </ul>
                        </Banner>
                    </Box>
                )}
                {/* 
                    Save Bar Control using SaveBar Component:
                    - Shows when: dataLoaded === true AND (formik.dirty === true OR productsChanged === true)
                    - formik.dirty becomes true automatically when ANY field value changes from initialValues
                    - All field changes (campaign_type, products, text fields, etc.) trigger formik.dirty
                    - Form re-renders automatically when formik.values change (React detects state change)
                */}
                <form
                    ref={formRef}
                    onSubmit={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        await handleSave(formik.values);
                    }}
                    onReset={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDiscard();
                    }}
                    onKeyDown={(e) => {
                        // Prevent form submission on Enter key in text fields
                        // Only allow Enter in textareas (for multiline input)
                        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                            // Check if this is a tag input field by checking placeholder
                            // Tag inputs have onKeyPress handlers that handle Enter key
                            const placeholder = e.target.placeholder || '';
                            const isTagInput = placeholder.toLowerCase().includes('tag');

                            if (!isTagInput) {
                                // Prevent form submission for regular text inputs
                                // This ensures form only submits when save button is explicitly clicked
                                e.preventDefault();
                            }
                            // Tag inputs will handle Enter via their onKeyPress handlers
                        }
                    }}
                >
                    <BlockStack gap="500">
                        {/* 60/40 Split Layout */}
                        <InlineStack gap="500" align="start" wrap={false}>
                            {/* Left 60% - Settings (Scrollable) */}
                            <Box style={{ flex: '0 0 60%', minWidth: 0 }}>
                                <BlockStack gap="500">
                                    <div>
                                        <Card className='campaign-create-page-card'>
                                            <BlockStack gap="400">
                                                <BlockStack gap="400">
                                                    <Text variant="headingMd" as="h3">
                                                        Basic Campaign Settings
                                                    </Text>
                                                </BlockStack>

                                                <Box>
                                                    <div style={{ marginBottom: '6px' }}>
                                                        <Text variant="bodyMd" fontWeight="regular" as="p" style={{ marginBottom: '12px' }}>
                                                            Campaign Type
                                                        </Text>
                                                    </div>
                                                    {/* Dynamic Radio Button Group - Optimized */}
                                                    <div 
                                                        role="radiogroup" 
                                                        aria-label="Campaign Type"
                                                        style={{ display: 'flex', gap: '0', width: '100%', marginBottom: '6px' }}
                                                    >
                                                        {CAMPAIGN_TYPE_OPTIONS.map((option, index) => {
                                                            const isSelected = formik.values.campaign_type === option.value;
                                                            const isFirst = index === 0;
                                                            const isLast = index === CAMPAIGN_TYPE_OPTIONS.length - 1;
                                                            const isDisabled = !isNewCampaign; // Disable when editing existing campaign
                                                            
                                                            return (
                                                                <label
                                                                    key={option.value}
                                                            style={{
                                                                flex: '1',
                                                                        position: 'relative',
                                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                                        marginLeft: isFirst ? '0' : '10px',
                                                                        opacity: isDisabled ? 0.6 : 1
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name="campaign_type"
                                                                        value={option.value}
                                                                        checked={isSelected}
                                                                        disabled={isDisabled}
                                                                        onChange={(e) => {
                                                                            if (!isDisabled) {
                                                                                formik.setFieldValue('campaign_type', e.target.value);
                                                                                formik.setFieldTouched('campaign_type', true);
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            opacity: 0,
                                                                            width: 0,
                                                                            height: 0,
                                                                            margin: 0,
                                                                            padding: 0
                                                                        }}
                                                                    />
                                                                    <span
                                                            style={{
                                                                            display: 'block',
                                                                padding: '8px 16px',
                                                                            border: isSelected
                                                                    ? '2px solid #0066cc'
                                                                    : '1px solid #000000',
                                                                            backgroundColor: isSelected
                                                                    ? '#e6f2ff'
                                                                    : 'transparent',
                                                                borderRadius: '6px',
                                                                fontSize: '14px',
                                                                            fontWeight: isSelected ? '600' : '400',
                                                                            color: isSelected ? '#0066cc' : '#000000',
                                                                transition: 'all 0.2s ease',
                                                                            textAlign: 'center',
                                                                            userSelect: 'none',
                                                                            pointerEvents: isDisabled ? 'none' : 'auto'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                            if (!isDisabled && !isSelected) {
                                                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                            if (!isDisabled && !isSelected) {
                                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                                }
                                                            }}
                                                        >
                                                                        {option.label}
                                                                    </span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                    {!isNewCampaign && (
                                                        <Text variant="bodySm" tone="subdued" as="p" style={{ marginTop: '4px' }}>
                                                            Campaign type cannot be changed after creation
                                                        </Text>
                                                    )}
                                                    {formik.touched.campaign_type && formik.errors.campaign_type && (
                                                        <Text variant="bodySm" tone="critical" as="p" style={{ marginTop: '4px' }}>
                                                            {formik.errors.campaign_type}
                                                        </Text>
                                                    )}
                                                    {/* Dynamic Description text based on selected campaign type - Optimized */}
                                                    {(() => {
                                                        const selectedOption = getCampaignTypeInfo(formik.values.campaign_type);
                                                        return selectedOption ? (
                                                        <Text variant="bodyMd" tone="subdued" as="p" style={{ marginTop: '12px' }}>
                                                                {selectedOption.description}
                                                        </Text>
                                                        ) : null;
                                                    })()}
                                                </Box>

                                                <Box>
                                                    {/* Hidden input to ensure Formik tracks products field changes */}
                                                    <input
                                                        type="hidden"
                                                        name="products"
                                                        value={JSON.stringify(formik.values.products || [])}
                                                        readOnly
                                                    />
                                                    
                                                    <div className='campaign-product-selection'>
                                                        <InlineStack gap="200" align="space-between">
                                                            <Text variant="bodyMd" fontWeight="regular" as="p">
                                                                Product Details
                                                            </Text>
                                                            <Button
                                                                variant="primary"
                                                                icon={PlusIcon}
                                                                onClick={handleProductSelect}
                                                            >
                                                                {(formik.values.products?.length || 0) > 0 ? 'Change Product' : 'Select Product'}
                                                            </Button>
                                                        </InlineStack>
                                                    </div>

                                                    {(formik.values.products?.length || 0) > 0 ? (
                                                        <BlockStack gap="200">
                                                            {(formik.values.products || []).map(product => (
                                                                <Card key={product.shopify_product_id} sectioned>
                                                                    <BlockStack gap="300">
                                                                        <InlineStack gap="300" align="space-between">
                                                                            <InlineStack gap="200" align="start">
                                                                                {product.product_image ? (
                                                                                    <Thumbnail
                                                                                        source={product.product_image}
                                                                                        alt={product.product_title}
                                                                                        size="small"
                                                                                    />
                                                                                ) : (
                                                                                    <Thumbnail
                                                                                        source=""
                                                                                        alt="No image available"
                                                                                        size="small"
                                                                                    />
                                                                                )}
                                                                                {/* <BlockStack gap="100"> */}
                                                                                <div style={{ margin: 'auto 5px' }}>
                                                                                    <Text variant="bodyMd" fontWeight="semibold">
                                                                                        {product.product_title}
                                                                                    </Text>
                                                                                </div>
                                                                                {/* </BlockStack> */}
                                                                            </InlineStack>
                                                                            <Button
                                                                                variant="tertiary"
                                                                                icon={DeleteIcon}
                                                                                onClick={() => handleRemoveProduct(product.shopify_product_id)}
                                                                            >
                                                                                Remove
                                                                            </Button>
                                                                        </InlineStack>
                                                                    </BlockStack>
                                                                </Card>
                                                            ))}
                                                        </BlockStack>
                                                    ) : (
                                                        <Box
                                                            padding="400"
                                                            background="bg-surface-secondary"
                                                            borderRadius="200"
                                                        >
                                                            <Text variant="bodyMd" tone="subdued" alignment="center">
                                                                No product selected. Click "Select Product" to choose a product for your campaign.
                                                            </Text>
                                                        </Box>
                                                    )}
                                                </Box>

                                                <TextField
                                                    label="Campaign Name"
                                                    value={formik.values.campaign_name || ''}
                                                    onChange={(value) => formik.setFieldValue('campaign_name', value)}
                                                    placeholder="Enter campaign name"
                                                    autoComplete="off"
                                                    error={formik.touched.campaign_name && formik.errors.campaign_name}
                                                />

                                                <Box>
                                                    <div style={{ marginBottom: '6px' }}>
                                                        <Text variant="bodyMd" fontWeight="regular" as="p">
                                                            Campaign Image (optional)
                                                        </Text>
                                                    </div>
                                                    {formik.values.campaign_banner_link ? (
                                                        <InlineStack gap="300" align="start">
                                                            <Thumbnail
                                                                source={formik.values.campaign_banner_link}
                                                                alt="Campaign banner"
                                                                size="large"
                                                            />
                                                            <Button
                                                                variant="tertiary"
                                                                onClick={() => formik.setFieldValue('campaign_banner_link', '')}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </InlineStack>
                                                    ) : (
                                                        <Box>
                                                            {uploadingBanner ? (
                                                                <Box padding="400">
                                                                    <InlineStack gap="200" align="center">
                                                                        <Spinner size="small" />
                                                                        <Text variant="bodySm" tone="subdued">Uploading banner to Shopify...</Text>
                                                                    </InlineStack>
                                                                </Box>
                                                            ) : (
                                                                <DropZone
                                                                    onDrop={handleBannerUpload}
                                                                    accept="image/*"
                                                                    type="image"
                                                                >
                                                                    <DropZone.FileUpload actionTitle="Upload Banner" />
                                                                </DropZone>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </BlockStack>
                                        </Card>
                                    </div>
                                    <div >
                                        {/* Crowdfunding Settings */}
                                        {formik.values.campaign_type === '1' && (
                                            <Card>
                                                <BlockStack gap="400">
                                                    <BlockStack gap="400">
                                                        <Text variant="headingMd" as="h3">
                                                            Crowdfunding Settings
                                                        </Text>
                                                    </BlockStack>

                                                    <Select
                                                        label="Goal Type"
                                                        options={[
                                                            { label: 'Total Items Sold', value: '0' },
                                                            { label: 'Funding Amount', value: '1' }
                                                        ]}
                                                        value={formik.values.goal_type}
                                                        onChange={(value) => formik.setFieldValue('goal_type', value)}
                                                        helpText={formik.values.goal_type === '0' ? 'A goal in terms of number of items sold from inventory ' : formik.values.goal_type === '1' ? 'A goal in terms of total money raised through order' : 'Choose your campaign goal type'}
                                                        error={formik.touched.goal_type && formik.errors.goal_type}
                                                        disabled={!isNewCampaign && formik.values.campaign_status === '1'}
                                                    />

                                                    {formik.values.goal_type === '0' && (
                                                        <div>
                                                            <TextField
                                                                label="Number of items"
                                                                type="number"
                                                                min="0"
                                                                value={(formik.values.number_of_items ?? 0).toString()}
                                                                onChange={(value) => formik.setFieldValue('number_of_items', Math.max(0, parseInt(value) || 0))}
                                                                placeholder="200"
                                                                error={formik.touched.number_of_items && formik.errors.number_of_items}
                                                            />
                                                            <Text variant="bodyMd" tone="subdued">
                                                                Target for number of products to sell during this campaign?
                                                            </Text>
                                                        </div>
                                                    )}

                                                    {formik.values.goal_type === '1' && (
                                                        <div>
                                                            <TextField
                                                                label="Total Amount"
                                                                type="number"
                                                                min="0"
                                                                value={(formik.values.total_amount ?? 0).toString()}
                                                                onChange={(value) => formik.setFieldValue('total_amount', Math.max(0, parseFloat(value) || 0))}
                                                                placeholder="10000"
                                                                prefix={currencyPrefix}
                                                                error={formik.touched.total_amount && formik.errors.total_amount}
                                                            />
                                                            <Text variant="bodyMd" tone="subdued">
                                                                Target for total amount to raise during this campaign.
                                                            </Text>
                                                        </div>
                                                    )}

                                                    <Box>
                                                        <InlineStack gap="200" align="space-between">
                                                            <Checkbox
                                                                label="Add Start Point (optional)"
                                                                checked={formik.values.addStartPoint}
                                                                onChange={(checked) => {
                                                                    formik.setFieldValue('addStartPoint', checked);
                                                                    // Don't reset values when unchecked - preserve them for when user checks again
                                                                    // Values will be ignored in calculations if addStartPoint is false
                                                                }}
                                                            />
                                                            <Button
                                                                variant="tertiary"
                                                                onClick={() => {
                                                                    if (formik.values.goal_type === '1') {
                                                                        formik.setFieldValue('initial_fund', 0);
                                                                    } else {
                                                                        formik.setFieldValue('initial_backers', 0);
                                                                    }
                                                                }}
                                                                size="slim"
                                                            >
                                                                Reset
                                                            </Button>
                                                        </InlineStack>
                                                        <Text variant="bodyMd" tone="subdued">
                                                            Enter an initial {formik.values.goal_type === '1' ? 'amount' : 'backers count'} to show progress from day one. This will count toward your goal and be
                                                            visible to customers. Having starting {formik.values.goal_type === '1' ? 'funding amount' : 'backers'} can help add trust.
                                                        </Text>

                                                        {formik.values.addStartPoint && (
                                                            <Box paddingBlockStart="300">
                                                                {formik.values.goal_type === '1' ? (
                                                                    <TextField
                                                                        label="What is the initial funding of your campaign?"
                                                                        type="number"
                                                                        min="0"
                                                                        value={(formik.values.initial_fund ?? 0).toString()}
                                                                        onChange={(value) => formik.setFieldValue('initial_fund', Math.max(0, parseFloat(value) || 0))}
                                                                        placeholder="1000"
                                                                        prefix={currencyPrefix}
                                                                        error={formik.touched.initial_fund && formik.errors.initial_fund}
                                                                    />
                                                                ) : (
                                                                    <TextField
                                                                        label="What is the initial backers count of your campaign?"
                                                                        type="number"
                                                                        min="0"
                                                                        value={(formik.values.initial_backers ?? 0).toString()}
                                                                        onChange={(value) => formik.setFieldValue('initial_backers', Math.max(0, parseInt(value) || 0))}
                                                                        placeholder="10"
                                                                        error={formik.touched.initial_backers && formik.errors.initial_backers}
                                                                    />
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    <Box >
                                                        <InlineStack gap="200" align="start">
                                                            <Checkbox
                                                                label="Auto-end campaign when goal is reached"
                                                                checked={formik.values.is_auto_end_campaign === '1'}
                                                                onChange={(checked) => {
                                                                    formik.setFieldValue('is_auto_end_campaign', checked ? '1' : '0');
                                                                }}
                                                            />
                                                        </InlineStack>
                                                        <Text variant="bodyMd" tone="subdued">
                                                            Campaign will automatically end when the funding goal is achieved
                                                        </Text>
                                                    </Box>

                                                    <InlineStack gap="300" align="start">
                                                        <Box minWidth="0" style={{ flex: 1 }}>
                                                            <Text variant="bodyMd" tone="subdued">Funded percentage</Text>
                                                            <Text variant="bodyLg" fontWeight="semibold">
                                                                {progressValue.toFixed(2)}%
                                                            </Text>
                                                        </Box>
                                                        <Box minWidth="0" style={{ flex: 1 }}>
                                                            <TextField
                                                                label="Initial backers"
                                                                type="number"
                                                                min="0"
                                                                value={(formik.values.initial_backers_count ?? 0).toString()}
                                                                onChange={(value) => formik.setFieldValue('initial_backers_count', Math.max(0, parseInt(value) || 0))}
                                                                placeholder="0"
                                                                error={formik.touched.initial_backers_count && formik.errors.initial_backers_count}
                                                            />
                                                        </Box>
                                                    </InlineStack>

                                                    <Banner tone="warning" title="">
                                                        <Text variant="bodyMd">
                                                            Starting boosts affect your campaign progress. Only include legitimate pre-secured funds or prior sales you want reflected in your total.
                                                        </Text>
                                                    </Banner>

                                                    <Checkbox
                                                        label="Set end date (optional)"
                                                        checked={formik.values.showEndDateCrowdfunding}
                                                        onChange={(checked) => {
                                                            formik.setFieldValue('showEndDateCrowdfunding', checked);
                                                            // Reset end date and time when unchecked
                                                            if (!checked) {
                                                                formik.setFieldValue('campaign_end_datetime', '');
                                                            }
                                                        }}
                                                    />

                                                    {formik.values.showEndDateCrowdfunding && (
                                                        <BlockStack gap="300">
                                                            <InlineStack gap="300" align="start">
                                                                <Box minWidth="0" style={{ flex: 1 }}>
                                                                    <TextField
                                                                        label="End date"
                                                                        type="date"
                                                                        min={minDateForTimezone}
                                                                        value={formik.values.campaign_end_datetime && formik.values.timeZone ? formatDateForTimezone(formik.values.campaign_end_datetime, formik.values.timeZone) : (formik.values.campaign_end_datetime ? formik.values.campaign_end_datetime.split('T')[0] : '')}
                                                                        onChange={(value) => {
                                                                            if (!value) {
                                                                                formik.setFieldValue('campaign_end_datetime', '');
                                                                                return;
                                                                            }
                                                                            // Get the current time part, or default to 23:59
                                                                            const currentTime = formik.values.campaign_end_datetime && formik.values.timeZone 
                                                                                ? formatTimeForTimezone(formik.values.campaign_end_datetime, formik.values.timeZone)
                                                                                : (formik.values.campaign_end_datetime ? formik.values.campaign_end_datetime.split('T')[1] : '23:59');
                                                                            // Store the date as-is in the selected timezone (YYYY-MM-DDTHH:mm format)
                                                                            const dateTime = `${value}T${currentTime}`;
                                                                            formik.setFieldValue('campaign_end_datetime', dateTime);
                                                                        }}
                                                                        placeholder="Select end date"
                                                                        suffix={<CalendarIcon />}
                                                                        error={formik.touched.campaign_end_datetime && formik.errors.campaign_end_datetime}
                                                                    />
                                                                </Box>
                                                                <Box minWidth="0" style={{ flex: 1 }}>
                                                                    <TextField
                                                                        label="End time"
                                                                        type="time"
                                                                        value={formik.values.campaign_end_datetime && formik.values.timeZone ? formatTimeForTimezone(formik.values.campaign_end_datetime, formik.values.timeZone) : (formik.values.campaign_end_datetime ? formik.values.campaign_end_datetime.split('T')[1] : '')}
                                                                        onChange={(value) => {
                                                                            if (!value) {
                                                                                const datePart = formik.values.campaign_end_datetime ? formik.values.campaign_end_datetime.split('T')[0] : '';
                                                                                formik.setFieldValue('campaign_end_datetime', datePart ? `${datePart}T23:59` : '');
                                                                                return;
                                                                            }
                                                                            // Get the current date part
                                                                            const datePart = formik.values.campaign_end_datetime && formik.values.timeZone
                                                                                ? formatDateForTimezone(formik.values.campaign_end_datetime, formik.values.timeZone)
                                                                                : (formik.values.campaign_end_datetime ? formik.values.campaign_end_datetime.split('T')[0] : minDateForTimezone);
                                                                            // Store the time as-is in the selected timezone
                                                                            const dateTime = datePart ? `${datePart}T${value}` : '';
                                                                            formik.setFieldValue('campaign_end_datetime', dateTime);
                                                                        }}
                                                                        suffix={<ClockIcon />}
                                                                    />
                                                                </Box>
                                                            </InlineStack>

                                                            {/* Timezone for Crowdfunding */}
                                                            <Box minWidth="200px">
                                                                <Select
                                                                    label="Campaign Timezone"
                                                                    options={TIME_ZONE_OPTIONS}
                                                                    value={formik.values.timeZone}
                                                                    onChange={(value) => formik.setFieldValue('timeZone', value)}
                                                                    helpText="Select the timezone for your campaign dates and times"
                                                                />
                                                            </Box>
                                                        </BlockStack>
                                                    )}
                                                </BlockStack>
                                            </Card>
                                        )}

                                        {/* Reward Tier Card - Separate Card for Crowdfunding */}
                                        {formik.values.campaign_type === '1' && (
                                            <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                                                <Card>
                                                    <BlockStack>
                                                        <BlockStack gap="400">
                                                            <InlineStack gap="200" align="space-between">
                                                                <div style={{ margin: 'auto 0px' }}>
                                                                    <Text variant="headingMd" as="h3">
                                                                        Reward tier (optional)
                                                                    </Text>
                                                                </div>
                                                                <Button
                                                                    variant="tertiary"
                                                                    icon={PageAddIcon}
                                                                    onClick={addRewardTier}
                                                                >
                                                                    Add Reward
                                                                </Button>
                                                            </InlineStack>
                                                        </BlockStack>

                                                        <Box>
                                                            {(formik.values.rewards?.length || 0) > 0 && (
                                                                <div style={{ marginTop: '1rem' }}>
                                                                    <BlockStack gap="300" paddingBlockStart="400">
                                                                        {(formik.values.rewards || []).map(tier => (
                                                                            <Card key={tier.id} sectioned>
                                                                                <BlockStack gap="300">
                                                                                    <InlineStack gap="200" align="space-between">
                                                                                        <Text variant="bodyMd" fontWeight="semibold">
                                                                                            Reward Tier
                                                                                        </Text>
                                                                                        <Button
                                                                                            variant="tertiary"
                                                                                            icon={DeleteIcon}
                                                                                            onClick={() => removeRewardTier(tier.id)}
                                                                                        >
                                                                                            Remove
                                                                                        </Button>
                                                                                    </InlineStack>

                                                                                    <TextField
                                                                                        label="Reward Title"
                                                                                        value={tier.reward_title || ''}
                                                                                        onChange={(value) => updateRewardTier(tier.id, 'reward_title', value)}
                                                                                        placeholder="Enter reward title"
                                                                                    />

                                                                                    <TextField
                                                                                        label="Description"
                                                                                        value={tier.reward_description || ''}
                                                                                        onChange={(value) => updateRewardTier(tier.id, 'reward_description', value)}
                                                                                        placeholder="Enter reward description"
                                                                                        multiline={2}
                                                                                    />

                                                                                    <Box>
                                                                                        <Text variant="bodyMd" fontWeight="semibold" as="p">
                                                                                            Image
                                                                                        </Text>
                                                                                        {tier.reward_image ? (
                                                                                            <InlineStack gap="300" align="start">
                                                                                                <Thumbnail
                                                                                                    source={tier.reward_image}
                                                                                                    alt={tier.reward_title || 'Reward image'}
                                                                                                    size="medium"
                                                                                                />
                                                                                                <Button
                                                                                                    variant="tertiary"
                                                                                                    onClick={() => updateRewardTier(tier.id, 'reward_image', '')}
                                                                                                >
                                                                                                    Remove
                                                                                                </Button>
                                                                                            </InlineStack>
                                                                                        ) : (
                                                                                            <Box>
                                                                                                {uploadingRewardImages[tier.id] ? (
                                                                                                    <Box padding="400">
                                                                                                        <InlineStack gap="200" align="center">
                                                                                                            <Spinner size="small" />
                                                                                                            <Text variant="bodySm" tone="subdued">Uploading image...</Text>
                                                                                                        </InlineStack>
                                                                                                    </Box>
                                                                                                ) : (
                                                                                                    <DropZone
                                                                                                        onDrop={(files) => handleRewardImageUpload(tier.id, files)}
                                                                                                        accept="image/*"
                                                                                                        type="image"
                                                                                                    >
                                                                                                        <DropZone.FileUpload actionTitle="Upload Image" />
                                                                                                    </DropZone>
                                                                                                )}
                                                                                            </Box>
                                                                                        )}
                                                                                    </Box>

                                                                                    <Box>
                                                                                        <div style={{ paddingBottom: '1rem' }}>
                                                                                            <InlineStack gap="200" align="space-between">
                                                                                                <div style={{ margin: 'auto 0px' }}>
                                                                                                    <Text variant="bodyMd" fontWeight="semibold" as="p">
                                                                                                        List of product / variants (in bundle)
                                                                                                    </Text>
                                                                                                </div>
                                                                                                <Button
                                                                                                    variant="primary"
                                                                                                    icon={PlusIcon}
                                                                                                    onClick={() => handleRewardTierProductSelect(tier.id)}
                                                                                                >
                                                                                                    {tier.products && tier.products.length > 0 ? 'Change Products' : 'Select Products'}
                                                                                                </Button>
                                                                                            </InlineStack>
                                                                                        </div>

                                                                                        {tier.products && tier.products.length > 0 ? (
                                                                                            <BlockStack gap="200">
                                                                                                <Text variant="bodySm" fontWeight="semibold" tone="subdued">
                                                                                                    Bundle ({tier.products.reduce((total, product) => {
                                                                                                        const selectedCount = product.selectedVariants?.length || product.variants?.length || 1;
                                                                                                        return total + selectedCount;
                                                                                                    }, 0)} selected variants)
                                                                                                </Text>
                                                                                                <BlockStack gap="200">
                                                                                                    {tier.products.map((product, productIndex) => {
                                                                                                        if (!product || !product.product_title) {
                                                                                                            return null;
                                                                                                        }

                                                                                                        // Get selected variants (only show these)
                                                                                                        const selectedVariants = product.selectedVariants || [];
                                                                                                        const displayVariants = product.variants?.filter(variant => {
                                                                                                            if (selectedVariants.length === 0) {
                                                                                                                // Backward compatibility: show all if no selectedVariants
                                                                                                                return true;
                                                                                                            }
                                                                                                            // Only show variants that are in selectedVariants
                                                                                                            return selectedVariants.some(selectedId => {
                                                                                                                const variantId = variant.id;
                                                                                                                const selectedIdStr = String(selectedId);
                                                                                                                return variantId === selectedIdStr || 
                                                                                                                       variantId.endsWith(`/${selectedIdStr}`) ||
                                                                                                                       selectedIdStr.endsWith(`/${variantId.split('/').pop()}`);
                                                                                                            });
                                                                                                        }) || [];

                                                                                                        return displayVariants.length > 0 ? (
                                                                                                            displayVariants.map((variant, variantIndex) => {
                                                                                                                const variantId = typeof variant.id === 'string' && variant.id.includes('/')
                                                                                                                    ? variant.id.match(/\/(\d+)$/)?.[1] || variant.id
                                                                                                                    : variant.id;

                                                                                                                return (
                                                                                                                    <Card key={`${product.shopify_product_id || product.id}-${variantId || variantIndex}`} sectioned>
                                                                                                                        <InlineStack gap="200" align="space-between">
                                                                                                                            <InlineStack gap="200" align="start">
                                                                                                                                {product.product_image ? (
                                                                                                                                    <Thumbnail
                                                                                                                                        source={product.product_image}
                                                                                                                                        alt={product.product_title || 'Product'}
                                                                                                                                        size="small"
                                                                                                                                    />
                                                                                                                                ) : (
                                                                                                                                    <Thumbnail
                                                                                                                                        source=""
                                                                                                                                        alt="No image available"
                                                                                                                                        size="small"
                                                                                                                                    />
                                                                                                                                )}
                                                                                                                                <BlockStack gap="100">
                                                                                                                                    <Text variant="bodyMd" fontWeight="semibold">
                                                                                                                                        {product.product_title || 'Untitled Product'}
                                                                                                                                    </Text>
                                                                                                                                    <Text variant="bodySm" tone="subdued">
                                                                                                                                        {variant.title || 'Default'} - {formatCurrency(parseFloat(variant.price || 0))}
                                                                                                                                    </Text>
                                                                                                                                </BlockStack>
                                                                                                                            </InlineStack>
                                                                                                                            <Button
                                                                                                                                variant="tertiary"
                                                                                                                                icon={DeleteIcon}
                                                                                                                                onClick={() => handleRemoveRewardTierProduct(tier.id, product.shopify_product_id || product.id, variant.id)}
                                                                                                                            >
                                                                                                                                Remove
                                                                                                                            </Button>
                                                                                                                        </InlineStack>
                                                                                                                    </Card>
                                                                                                                );
                                                                                                            })
                                                                                                        ) : (
                                                                                                            <Card key={`${product.shopify_product_id || product.id}-${productIndex}`} sectioned>
                                                                                                                <BlockStack gap="200">
                                                                                                                    <InlineStack gap="200" align="space-between">
                                                                                                                        <InlineStack gap="200" align="start">
                                                                                                                            {product.product_image ? (
                                                                                                                                <Thumbnail
                                                                                                                                    source={product.product_image}
                                                                                                                                    alt={product.product_title || 'Product'}
                                                                                                                                    size="small"
                                                                                                                                />
                                                                                                                            ) : (
                                                                                                                                <Thumbnail
                                                                                                                                    source=""
                                                                                                                                    alt="No image available"
                                                                                                                                    size="small"
                                                                                                                                />
                                                                                                                            )}
                                                                                                                            <BlockStack gap="100">
                                                                                                                                <Text variant="bodyMd" fontWeight="semibold">
                                                                                                                                    {product.product_title || 'Untitled Product'}
                                                                                                                                </Text>
                                                                                                                                <Text variant="bodySm" tone="subdued">
                                                                                                                                    {product.variant_title || 'Default'} - {formatCurrency(parseFloat(product.product_price || 0))}
                                                                                                                                </Text>
                                                                                                                            </BlockStack>
                                                                                                                        </InlineStack>
                                                                                                                        <Button
                                                                                                                            variant="tertiary"
                                                                                                                            icon={DeleteIcon}
                                                                                                                            onClick={() => handleRemoveRewardTierProduct(tier.id, product.shopify_product_id || product.id)}
                                                                                                                        >
                                                                                                                            Remove
                                                                                                                        </Button>
                                                                                                                    </InlineStack>
                                                                                                                    {displayVariants.length > 1 && (
                                                                                                                        <Box>
                                                                                                                            <Text variant="bodySm" fontWeight="semibold" tone="subdued">
                                                                                                                                Selected Variants ({displayVariants.length}):
                                                                                                                            </Text>
                                                                                                                            <InlineStack gap="100" wrap>
                                                                                                                                {displayVariants.map((variant, index) => (
                                                                                                                                    <Badge
                                                                                                                                        key={index}
                                                                                                                                        tone="info"
                                                                                                                                        size="small"
                                                                                                                                    >
                                                                                                                                        {variant.title || 'Default'} - {formatCurrency(parseFloat(variant.price || 0))}
                                                                                                                                    </Badge>
                                                                                                                                ))}
                                                                                                                            </InlineStack>
                                                                                                                        </Box>
                                                                                                                    )}
                                                                                                                </BlockStack>
                                                                                                            </Card>
                                                                                                        );
                                                                                                    }).filter(Boolean)}
                                                                                                </BlockStack>
                                                                                            </BlockStack>
                                                                                        ) : (
                                                                                            <Box
                                                                                                padding="400"
                                                                                                background="bg-surface-secondary"
                                                                                                borderRadius="200"
                                                                                            >
                                                                                                <Text variant="bodyMd" tone="subdued" alignment="center">
                                                                                                    No products selected. Click "Select Products" to choose products for this reward tier.
                                                                                                </Text>
                                                                                            </Box>
                                                                                        )}
                                                                                    </Box>

                                                                                    {/* Quantity and Price fields in 50-50 layout */}
                                                                                    <InlineStack gap="300" align="start">
                                                                                        <Box minWidth="0" style={{ flex: 1 }}>
                                                                                            <TextField
                                                                                                label="Purchase Limit (empty for unlimited)"
                                                                                                type="number"
                                                                                                min="0"
                                                                                                value={tier.quantity_limit || ''}
                                                                                                onChange={(value) => {
                                                                                                    const numValue = value === '' ? '' : Math.max(0, parseInt(value) || 0);
                                                                                                    updateRewardTier(tier.id, 'quantity_limit', numValue);
                                                                                                }}
                                                                                                placeholder="Unlimited"
                                                            />
                                                                                        </Box>
                                                                                        <Box minWidth="0" style={{ flex: 1 }}>
                                                                                            <TextField
                                                                                                label="Bundle Final Price"
                                                                                                type="number"
                                                                                                min="0"
                                                                                                value={tier.reward_price || ''}
                                                                                                onChange={(value) => {
                                                                                                    const numValue = value === '' ? '' : Math.max(0, parseFloat(value) || 0);
                                                                                                    updateRewardTier(tier.id, 'reward_price', numValue);
                                                                                                }}
                                                                                                placeholder="0"
                                                                                                prefix={currencyPrefix}
                                                            />
                                                                                        </Box>
                                                                                    </InlineStack>
                                                                                    
                                                                                    {/* Show Strikethrough Price Option */}
                                                                                    <Box>
                                                                                        <Checkbox
                                                                                            label="Show strikethrough price"
                                                                                            checked={tier.show_strikethrough_price || false}
                                                                                            onChange={(checked) => updateRewardTier(tier.id, 'show_strikethrough_price', checked)}
                                                                                        />
                                                                                        <Text variant="bodySm" tone="subdued" as="p" style={{ marginTop: '4px', marginLeft: '24px' }}>
                                                                                            Display the original price with a line through it next to the discounted price
                                                                                        </Text>
                                                                                    </Box>
                                                                                </BlockStack>
                                                                            </Card>
                                                                        ))}
                                                                    </BlockStack>
                                                                </div>
                                                            )}
                                                        </Box>
                                                    </BlockStack>
                                                </Card>
                                            </div>
                                        )}

                                        {/* PreSales Settings */}
                                        {formik.values.campaign_type === '0' && (
                                            <Card>
                                                <BlockStack gap="400">
                                                    <BlockStack gap="400">
                                                        <Text variant="headingMd" as="h3">
                                                            PreSales Settings
                                                        </Text>

                                                    </BlockStack>

                                                    <Box>
                                                        <BlockStack gap="300">
                                                            <Checkbox
                                                                label="Limit inventory for Preorder"
                                                                checked={formik.values.limitInventory}
                                                                onChange={(checked) => formik.setFieldValue('limitInventory', checked)}
                                                            />
                                                            {formik.values.limitInventory && (
                                                                <BlockStack gap="200">
                                                                    <TextField
                                                                        label="Available inventory"
                                                                        type="number"
                                                                        min="0"
                                                                        value={(formik.values.availableInventory).toString()}
                                                                        onChange={(value) => formik.setFieldValue('availableInventory', Math.max(0, parseInt(value) || 0))}
                                                                        // placeholder="100"
                                                                        helpText="Limit inventory per product in products tab"
                                                                        error={formik.touched.availableInventory && formik.errors.availableInventory}
                                                                    />
                                                                    <Box paddingBlockStart="100" paddingInlineStart="200">
                                                                        <InlineStack gap="200" align="start" blockAlign="start">
                                                                            <div style={{ display: 'flex' }}>
                                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '2px', flexShrink: 0, marginRight: '10px' }}>
                                                                                    <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm1 12H7v-2h2v2zm0-4H7V4h2v4z" fill="currentColor" opacity="0.6"/>
                                                                                </svg>
                                                                                <Text as="p" variant="bodySm" tone="subdued">
                                                                                    When the PreSales campaign is active, the app automatically disables <strong>"Track quantity"</strong> for the product to allow pre-orders.
                                                                                </Text>
                                                                            </div>
                                                                        </InlineStack>
                                                                    </Box>
                                                                    <TextField
                                                                        label="Initial Sold Quantity"
                                                                        type="number"
                                                                        min="0"
                                                                        value={(formik.values.initial_items_sold_preSale || 0).toString()}
                                                                        onChange={(value) => {
                                                                            const numValue = Math.max(0, parseInt(value) || 0);
                                                                            formik.setFieldValue('initial_items_sold_preSale', numValue);
                                                                        }}
                                                                        helpText="Enter the initial number of items already sold. This will be added to the progress calculation."
                                                                        error={formik.touched.initial_items_sold_preSale && formik.errors.initial_items_sold_preSale}
                                                                    />
                                                                </BlockStack>
                                                            )}
                                                        </BlockStack>
                                                    </Box>

                                                    <Box>
                                                        <BlockStack gap="300">
                                                            <Checkbox
                                                                label="Enable product limit per order"
                                                                checked={formik.values.enableProductLimitPerOrder}
                                                                onChange={(checked) => {
                                                                    formik.setFieldValue('enableProductLimitPerOrder', checked);
                                                                    if (!checked) {
                                                                        // Clear the value when unchecked
                                                                        formik.setFieldValue('productLimitPerOrder', null);
                                                                    }
                                                                }}
                                                            />
                                                            {formik.values.enableProductLimitPerOrder && (
                                                                <TextField
                                                                    label="Product limit per order"
                                                                    type="number"
                                                                    min="1"
                                                                    value={(formik.values.productLimitPerOrder || '').toString()}
                                                                    onChange={(value) => formik.setFieldValue('productLimitPerOrder', Math.max(1, parseInt(value) || 1))}
                                                                    helpText="Maximum quantity a customer can add to one order. If exceeded, customer needs to place a second order."
                                                                    error={formik.touched.productLimitPerOrder && formik.errors.productLimitPerOrder}
                                                                />
                                                            )}
                                                        </BlockStack>
                                                    </Box>

                                                    {formik.values.limitInventory && (
                                                        <Box>
                                                            <Checkbox
                                                                label="Show as Presales Target"
                                                                checked={formik.values.showPresalesTarget}
                                                                onChange={(checked) => formik.setFieldValue('showPresalesTarget', checked)}
                                                            />
                                                        </Box>
                                                    )}
                                                </BlockStack>
                                            </Card>
                                        )}

                                        {/* Product Drop Settings */}
                                        {formik.values.campaign_type === '2' && (
                                            <Card>
                                                <BlockStack gap="400">
                                                    <BlockStack gap="400">
                                                        <Text variant="headingMd" as="h3">
                                                            Product Drop Settings
                                                        </Text>
                                                    </BlockStack>

                                                    <Box>
                                                        <BlockStack gap="400">
                                                            <div style={{ paddingBottom: '10px' }}>
                                                                <Text variant="bodyMd" fontWeight="semibold" as="p">
                                                                    Drop Timing
                                                                </Text>
                                                            </div>
                                                        </BlockStack>
                                                        <BlockStack gap="300" paddingBlockStart="400">
                                                            {/* Start Date and Time */}
                                                            <InlineStack gap="300" align="start">
                                                                <Box minWidth="0" style={{ flex: 1 }}>
                                                                    <TextField
                                                                        label="Start date"
                                                                        type="date"
                                                                        min={minDateForTimezone}
                                                                        value={formik.values.campaign_start_datetime && formik.values.timeZone ? formatDateForTimezone(formik.values.campaign_start_datetime, formik.values.timeZone) : (formik.values.campaign_start_datetime ? formik.values.campaign_start_datetime.split('T')[0] : '')}
                                                                        onChange={(value) => {
                                                                            if (!value) {
                                                                                formik.setFieldValue('campaign_start_datetime', '');
                                                                                return;
                                                                            }
                                                                            // Get current time, preserving it if it exists
                                                                            let currentTime = '00:00';
                                                                            if (formik.values.campaign_start_datetime) {
                                                                                if (formik.values.timeZone) {
                                                                                    // Use timezone-aware formatting
                                                                                    const formattedTime = formatTimeForTimezone(formik.values.campaign_start_datetime, formik.values.timeZone);
                                                                                    if (formattedTime) {
                                                                                        currentTime = formattedTime;
                                                                                    }
                                                                                } else {
                                                                                    // Extract time part directly from datetime string
                                                                                    const timePart = formik.values.campaign_start_datetime.split('T')[1];
                                                                                    if (timePart) {
                                                                                        // Extract HH:mm from time part (ignore seconds if present)
                                                                                        const timeParts = timePart.split(':');
                                                                                        if (timeParts.length >= 2) {
                                                                                            currentTime = `${timeParts[0]}:${timeParts[1]}`;
                                                                                        }
                                                                                    }
                                                                                }
                                                                            }
                                                                            const dateTime = `${value}T${currentTime}`;
                                                                            formik.setFieldValue('campaign_start_datetime', dateTime);
                                                                        }}
                                                                        placeholder="Select start date"
                                                                        suffix={<CalendarIcon />}
                                                                    />
                                                                    <Text variant="bodyMd" tone="subdued">
                                                                        when the drop goes live
                                                                    </Text>
                                                                </Box>
                                                                <Box minWidth="0" style={{ flex: 1 }}>
                                                                    <TextField
                                                                        label="Start time"
                                                                        type="time"
                                                                        value={(() => {
                                                                            if (!formik.values.campaign_start_datetime) return '';
                                                                            if (formik.values.timeZone) {
                                                                                const formattedTime = formatTimeForTimezone(formik.values.campaign_start_datetime, formik.values.timeZone);
                                                                                return formattedTime || '';
                                                                            }
                                                                            // Extract time part directly
                                                                            const timePart = formik.values.campaign_start_datetime.split('T')[1];
                                                                            if (timePart) {
                                                                                // Extract HH:mm from time part (ignore seconds if present)
                                                                                const timeParts = timePart.split(':');
                                                                                if (timeParts.length >= 2) {
                                                                                    return `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}`;
                                                                                }
                                                                            }
                                                                            return '';
                                                                        })()}
                                                                        onChange={(value) => {
                                                                            if (!value) {
                                                                                const datePart = formik.values.campaign_start_datetime && formik.values.timeZone
                                                                                    ? formatDateForTimezone(formik.values.campaign_start_datetime, formik.values.timeZone)
                                                                                    : (formik.values.campaign_start_datetime ? formik.values.campaign_start_datetime.split('T')[0] : minDateForTimezone);
                                                                                formik.setFieldValue('campaign_start_datetime', datePart ? `${datePart}T00:00` : '');
                                                                                return;
                                                                            }
                                                                            const datePart = formik.values.campaign_start_datetime && formik.values.timeZone
                                                                                ? formatDateForTimezone(formik.values.campaign_start_datetime, formik.values.timeZone)
                                                                                : (formik.values.campaign_start_datetime ? formik.values.campaign_start_datetime.split('T')[0] : minDateForTimezone);
                                                                            const dateTime = datePart ? `${datePart}T${value}` : '';
                                                                            formik.setFieldValue('campaign_start_datetime', dateTime);
                                                                        }}
                                                                        suffix={<ClockIcon />}
                                                                    />
                                                                </Box>
                                                            </InlineStack>

                                                            {/* Set End Date Checkbox */}
                                                            <Checkbox
                                                                label="Set end date"
                                                                checked={formik.values.showEndDate}
                                                                onChange={(checked) => {
                                                                    formik.setFieldValue('showEndDate', checked);
                                                                    // Reset end date and time when unchecked
                                                                    if (!checked) {
                                                                        formik.setFieldValue('campaign_end_datetime', '');
                                                                    }
                                                                }}
                                                            />

                                                            {/* End Date and Time - Only show when checkbox is checked */}
                                                            {formik.values.showEndDate && (
                                                                <InlineStack gap="300" align="start">
                                                                    <Box minWidth="0" style={{ flex: 1 }}>
                                                                        <TextField
                                                                            label="End date"
                                                                            type="date"
                                                                            min={formik.values.campaign_start_datetime && formik.values.timeZone ? formatDateForTimezone(formik.values.campaign_start_datetime, formik.values.timeZone) : (formik.values.campaign_start_datetime ? formik.values.campaign_start_datetime.split('T')[0] : minDateForTimezone)}
                                                                            value={formik.values.campaign_end_datetime && formik.values.timeZone ? formatDateForTimezone(formik.values.campaign_end_datetime, formik.values.timeZone) : (formik.values.campaign_end_datetime ? formik.values.campaign_end_datetime.split('T')[0] : '')}
                                                                            onChange={(value) => {
                                                                                if (!value) {
                                                                                    formik.setFieldValue('campaign_end_datetime', '');
                                                                                    return;
                                                                                }
                                                                                const currentTime = formik.values.campaign_end_datetime && formik.values.timeZone
                                                                                    ? formatTimeForTimezone(formik.values.campaign_end_datetime, formik.values.timeZone)
                                                                                    : (formik.values.campaign_end_datetime ? formik.values.campaign_end_datetime.split('T')[1] : '23:59');
                                                                                const dateTime = `${value}T${currentTime}`;
                                                                                formik.setFieldValue('campaign_end_datetime', dateTime);
                                                                            }}
                                                                            placeholder="Select end date"
                                                                            suffix={<CalendarIcon />}
                                                                            error={formik.touched.campaign_end_datetime && formik.errors.campaign_end_datetime}
                                                                        />
                                                                        <Text variant="bodyMd" tone="subdued">
                                                                            when the drop ends
                                                                        </Text>
                                                                    </Box>
                                                                    <Box minWidth="0" style={{ flex: 1 }}>
                                                                        <TextField
                                                                            label="End time"
                                                                            type="time"
                                                                            value={formik.values.campaign_end_datetime && formik.values.timeZone ? formatTimeForTimezone(formik.values.campaign_end_datetime, formik.values.timeZone) : (formik.values.campaign_end_datetime ? formik.values.campaign_end_datetime.split('T')[1] : '')}
                                                                            onChange={(value) => {
                                                                                if (!value) {
                                                                                    const datePart = formik.values.campaign_end_datetime && formik.values.timeZone
                                                                                        ? formatDateForTimezone(formik.values.campaign_end_datetime, formik.values.timeZone)
                                                                                        : (formik.values.campaign_end_datetime ? formik.values.campaign_end_datetime.split('T')[0] : '');
                                                                                    formik.setFieldValue('campaign_end_datetime', datePart ? `${datePart}T23:59` : '');
                                                                                    return;
                                                                                }
                                                                                const datePart = formik.values.campaign_end_datetime && formik.values.timeZone
                                                                                    ? formatDateForTimezone(formik.values.campaign_end_datetime, formik.values.timeZone)
                                                                                    : (formik.values.campaign_end_datetime ? formik.values.campaign_end_datetime.split('T')[0] : minDateForTimezone);
                                                                                const dateTime = datePart ? `${datePart}T${value}` : '';
                                                                                formik.setFieldValue('campaign_end_datetime', dateTime);
                                                                            }}
                                                                            suffix={<ClockIcon />}
                                                                        />
                                                                    </Box>
                                                                </InlineStack>
                                                            )}

                                                            <Box minWidth="200px">
                                                                <Select
                                                                    label="Time Zone"
                                                                    options={TIME_ZONE_OPTIONS}
                                                                    value={formik.values.timeZone}
                                                                    onChange={(value) => formik.setFieldValue('timeZone', value)}
                                                                />
                                                            </Box>
                                                        </BlockStack>
                                                    </Box>

                                                    <Box>
                                                        <BlockStack gap="400">
                                                            <Divider />
                                                            <div style={{ paddingBottom: '10px' }}>
                                                                <Text variant="bodyMd" fontWeight="semibold" as="p">
                                                                    Purchase Limits
                                                                </Text>
                                                            </div>
                                                        </BlockStack>
                                                        <BlockStack gap="300" paddingBlockStart="400">
                                                            <TextField
                                                                label="Per Customer Limit"
                                                                type="number"
                                                                min="0"
                                                                value={formik.values.perCustomerLimit}
                                                                onChange={(value) => {
                                                                    const numValue = value === '' ? '' : Math.max(0, parseInt(value) || 0).toString();
                                                                    formik.setFieldValue('perCustomerLimit', numValue);
                                                                }}
                                                                placeholder="1"
                                                                helpText="Max quantity per customer"
                                                                error={formik.touched.perCustomerLimit && formik.errors.perCustomerLimit}
                                                            />
                                                            <TextField
                                                                label="Total Drop Limit"
                                                                type="number"
                                                                min="0"
                                                                value={formik.values.totalDropLimit}
                                                                onChange={(value) => {
                                                                    const numValue = value === '' ? '' : Math.max(0, parseInt(value) || 0).toString();
                                                                    formik.setFieldValue('totalDropLimit', numValue);
                                                                }}
                                                                placeholder="100"
                                                                helpText="Max total units sold in this drop"
                                                                error={formik.touched.totalDropLimit && formik.errors.totalDropLimit}
                                                            />
                                                            <Box paddingBlockStart="100" paddingInlineStart="200">
                                                                <InlineStack gap="200" align="start" blockAlign="start">
                                                                    <div style={{ display: 'flex' }}>
                                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '2px', flexShrink: 0, marginRight: '10px' }}>
                                                                            <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm1 12H7v-2h2v2zm0-4H7V4h2v4z" fill="currentColor" opacity="0.6"/>
                                                                        </svg>
                                                                        <Text as="p" variant="bodySm" tone="subdued">
                                                                            When the Product-drop campaign is active, the app automatically sets <strong>"Track quantity"</strong> to <strong>enable</strong> and the <strong>“Continue selling when out of stock”</strong> option will be <strong>disable</strong> for the product to allow product-drop.
                                                                        </Text>
                                                                    </div>
                                                                </InlineStack>
                                                            </Box>
                                                        </BlockStack>
                                                    </Box>
                                                </BlockStack>
                                            </Card>
                                        )}

                                        {/* Order Tags & Customer Tags - Unified Card for All Campaign Types */}
                                        {formik.values.campaign_type && (
                                            <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                                                <Card>
                                                    <BlockStack gap="400">

                                                        <Text variant="headingMd" as="h3">
                                                            Order Tags & Customer Tags
                                                        </Text>

                                                        <BlockStack gap="200">
                                                            <Box>
                                                                <div style={{ paddingBottom: '3px' }}>
                                                                    <Text variant="bodyMd" as="p">
                                                                        Order Tags
                                                                    </Text>
                                                                </div>

                                                                <div onKeyDown={handleOrderTagKeyPress}>
                                                                    <TextField
                                                                        value={formik.values.orderTagInput}
                                                                        onChange={handleOrderTagInputChange}
                                                                        onKeyPress={handleOrderTagKeyPress}
                                                                        onBlur={handleOrderTagSubmit}
                                                                        placeholder="Enter tag and press Enter"
                                                                        helpText={
                                                                            formik.values.campaign_type === '1'
                                                                                ? 'For orders containing crowdfunding items.'
                                                                                : formik.values.campaign_type === '0'
                                                                                    ? 'For orders containing preorder items.'
                                                                                    : 'For orders containing product drop items.'
                                                                        }
                                                                    />
                                                                </div>

                                                                <InlineStack gap="200" wrap>
                                                                    {(formik.values.orderTags || []).map(tag => (
                                                                        <Tag key={tag} onRemove={() => handleTagRemove('order', tag)}>
                                                                            {tag}
                                                                        </Tag>
                                                                    ))}
                                                                </InlineStack>
                                                            </Box>

                                                            <Box>
                                                                <div style={{ paddingBottom: '3px' }}>
                                                                    <Text variant="bodyMd" as="p">
                                                                        Customer Tags
                                                                    </Text>
                                                                </div>

                                                                <div onKeyDown={handleCustomerTagKeyPress}>
                                                                    <TextField
                                                                        value={formik.values.customerTagInput}
                                                                        onChange={handleCustomerTagInputChange}
                                                                        onKeyPress={handleCustomerTagKeyPress}
                                                                        onBlur={handleCustomerTagSubmit}
                                                                        placeholder="Enter tag and press Enter"
                                                                        helpText={
                                                                            formik.values.campaign_type === '1'
                                                                                ? 'For customers who participated in this crowdfunding campaign.'
                                                                                : formik.values.campaign_type === '0'
                                                                                    ? 'For customers who placed preorders.'
                                                                                    : 'For customers who participated in this product drop.'
                                                                        }
                                                                    />
                                                                </div>

                                                                <InlineStack gap="200" wrap>
                                                                    {(formik.values.customerTags || []).map(tag => (
                                                                        <Tag key={tag} onRemove={() => handleTagRemove('customer', tag)}>
                                                                            {tag}
                                                                        </Tag>
                                                                    ))}
                                                                </InlineStack>
                                                            </Box>
                                                        </BlockStack>
                                                    </BlockStack>
                                                </Card>
                                            </div>
                                        )}
                                    </div>
                                </BlockStack>
                            </Box>

                            {/* Right 40% - Preview (Sticky) */}
                            <Box style={{ flex: '0 0 40%', minWidth: 0 }}>
                                <Box
                                    style={{
                                        position: 'sticky',
                                        top: '20px',
                                        alignSelf: 'flex-start'
                                    }}
                                >
                                    <BlockStack gap="400">
                                        <div style={{ marginBottom: '1rem' }}>
                                            <Card>
                                                <BlockStack gap="400">
                                                    <Text variant="headingMd" as="h3">
                                                        Campaign Preview
                                                    </Text>

                                                    {/* Product Drop Preview Widget */}
                                                    {formik.values.campaign_type === '2' ? (
                                                        <div className="preview-widget" style={{
                                                            fontSize: '13px',
                                                            padding: '16px'
                                                        }}>
                                                            <p style={{
                                                                    fontSize: '12px',
                                                                    fontWeight: '600',
                                                                    margin: '0 0 12px 0',
                                                                }}>
                                                                    Before live
                                                                </p>
                                                            {/* Before Live State */}
                                                            <div style={{
                                                                backgroundColor: previewSettings.backgroundColor,
                                                                borderRadius: `${previewSettings.widgetBorderRadius}px`,
                                                                padding: '16px',
                                                                marginBottom: '16px',
                                                                border: `1px solid ${previewSettings.widgetBorderColor || '#e1e5e9'}`
                                                            }}>
                                                                

                                                                <h4 style={{
                                                                    fontSize: `${previewSettings.headerFontSize || 16}px`,
                                                                    fontWeight: 'bold',
                                                                    margin: '0 0 16px 0',
                                                                    color: previewSettings.headerColor
                                                                }}>
                                                                    {decodeForPreview(previewSettings.beforeLiveTitle || 'GOES LIVE IN:')}
                                                                </h4>

                                                                {/* Countdown Timer */}
                                                                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                                                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center', marginBottom: '4px' }}>
                                                                        <span style={{ fontSize: '24px', fontWeight: 'bold', color: previewSettings.timerTextColor || previewSettings.textColor || '#000000' }}>
                                                                            {String(countdown.days).padStart(2, '0')}
                                                                        </span>
                                                                        <span style={{ fontSize: '24px', fontWeight: 'bold', color: previewSettings.timerTextColor || previewSettings.textColor || '#000000', margin: '0 2px' }}>:</span>
                                                                        <span style={{ fontSize: '24px', fontWeight: 'bold', color: previewSettings.timerTextColor || previewSettings.textColor || '#000000' }}>
                                                                            {String(countdown.hours).padStart(2, '0')}
                                                                        </span>
                                                                        <span style={{ fontSize: '24px', fontWeight: 'bold', color: previewSettings.timerTextColor || previewSettings.textColor || '#000000', margin: '0 2px' }}>:</span>
                                                                        <span style={{ fontSize: '24px', fontWeight: 'bold', color: previewSettings.timerTextColor || previewSettings.textColor || '#000000' }}>
                                                                            {String(countdown.minutes).padStart(2, '0')}
                                                                        </span>
                                                                        <span style={{ fontSize: '24px', fontWeight: 'bold', color: previewSettings.timerTextColor || previewSettings.textColor || '#000000', margin: '0 2px' }}>:</span>
                                                                        <span style={{ fontSize: '24px', fontWeight: 'bold', color: previewSettings.timerTextColor || previewSettings.textColor || '#000000' }}>
                                                                            {String(countdown.seconds).padStart(2, '0')}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {/* Notify Button */}
                                                                <button 
                                                                    onClick={(e) => e.preventDefault()}
                                                                    style={{
                                                                        border: `2px solid ${previewSettings.buttonBackgroundColor || '#000000'}`,
                                                                        backgroundColor: previewSettings.buttonBackgroundColor || '#000000',
                                                                        color: previewSettings.buttonTextColor || '#ffffff',
                                                                        borderRadius: previewSettings.buttonStyle === 'pill' ? '50px' :
                                                                            previewSettings.buttonStyle === 'square' ? '0px' :
                                                                                `${previewSettings.widgetBorderRadius || 8}px`,
                                                                        padding: '5px 16px',
                                                                        fontSize: `${previewSettings.buttonFontSize || 14}px`,
                                                                        fontWeight: '600',
                                                                        cursor: 'default',
                                                                        width: '100%',
                                                                        marginBottom: '12px'
                                                                    }}>
                                                                    {decodeForPreview(previewSettings.beforeLiveButton || 'NOTIFY ME')}
                                                                </button>

                                                                {/* Drop Details */}
                                                                <p style={{
                                                                    margin: 0,
                                                                    color: previewSettings.subtitleColor,
                                                                    fontSize: `${previewSettings.secondaryFontSize || 13}px`
                                                                }}>
                                                                    {(() => {
                                                                        // Format date for preview
                                                                        let formattedDate = 'XX';
                                                                        if (formik.values.campaign_start_datetime) {
                                                                            try {
                                                                                if (formik.values.timeZone) {
                                                                                    // Use timezone-aware formatting
                                                                                    const utcDate = convertToUTC(formik.values.campaign_start_datetime, formik.values.timeZone);
                                                                                    if (utcDate) {
                                                                                        const formatter = new Intl.DateTimeFormat('en-US', {
                                                                                            timeZone: formik.values.timeZone,
                                                                                            month: 'short',
                                                                                            day: 'numeric',
                                                                                            year: 'numeric'
                                                                                        });
                                                                                        formattedDate = formatter.format(utcDate);
                                                                                    } else {
                                                                                        // Fallback to basic date parsing
                                                                                        const dateObj = new Date(formik.values.campaign_start_datetime);
                                                                                        formattedDate = dateObj.toLocaleDateString('en-US', {
                                                                                            month: 'short',
                                                                                            day: 'numeric',
                                                                                            year: 'numeric'
                                                                                        });
                                                                                    }
                                                                                } else {
                                                                                    // No timezone, use basic formatting
                                                                                    const dateObj = new Date(formik.values.campaign_start_datetime);
                                                                                    formattedDate = dateObj.toLocaleDateString('en-US', {
                                                                                        month: 'short',
                                                                                        day: 'numeric',
                                                                                        year: 'numeric'
                                                                                    });
                                                                                }
                                                                            } catch (error) {
                                                                                formattedDate = 'XX';
                                                                            }
                                                                        }

                                                                        // Format time for preview
                                                                        let formattedTime = 'XX:XX';
                                                                        if (formik.values.campaign_start_datetime) {
                                                                            try {
                                                                                if (formik.values.timeZone) {
                                                                                    // Use timezone-aware formatting
                                                                                    const utcDate = convertToUTC(formik.values.campaign_start_datetime, formik.values.timeZone);
                                                                                    if (utcDate) {
                                                                                        const formatter = new Intl.DateTimeFormat('en-US', {
                                                                                            timeZone: formik.values.timeZone,
                                                                                            hour: 'numeric',
                                                                                            minute: '2-digit',
                                                                                            hour12: true
                                                                                        });
                                                                                        formattedTime = formatter.format(utcDate);
                                                                                    } else {
                                                                                        // Fallback to basic time parsing
                                                                                        const dateObj = new Date(formik.values.campaign_start_datetime);
                                                                                        formattedTime = dateObj.toLocaleTimeString('en-US', {
                                                                                            hour: 'numeric',
                                                                                            minute: '2-digit',
                                                                                            hour12: true
                                                                                        });
                                                                                    }
                                                                                } else {
                                                                                    // No timezone, use basic formatting
                                                                                    const dateObj = new Date(formik.values.campaign_start_datetime);
                                                                                    formattedTime = dateObj.toLocaleTimeString('en-US', {
                                                                                        hour: 'numeric',
                                                                                        minute: '2-digit',
                                                                                        hour12: true
                                                                                    });
                                                                                }
                                                                            } catch (error) {
                                                                                formattedTime = 'XX:XX';
                                                                            }
                                                                        }

                                                                        // Format timezone for preview
                                                                        const formattedTimezone = formik.values.timeZone || 'XX';

                                                                        return formatTimeRemainingText(
                                                                            decodeForPreview(previewSettings.beforeLiveDetails || 'Time left: {days} day, {hours} hour, {minutes} minutes')
                                                                                .replace('{date}', formattedDate)
                                                                                .replace('{time}', formattedTime)
                                                                                .replace('{timezone}', formattedTimezone)
                                                                        );
                                                                    })()}
                                                                </p>
                                                            </div>

                                                            <p style={{
                                                                    fontSize: '12px',
                                                                    fontWeight: '600',
                                                                    margin: '0 0 12px 0',
                                                                }}>
                                                                    When live
                                                                </p>
                                                            {/* When Live State */}
                                                            <div style={{
                                                                backgroundColor: previewSettings.backgroundColor,
                                                                borderRadius: `${previewSettings.widgetBorderRadius}px`,
                                                                padding: '16px',
                                                                marginBottom: '16px',
                                                                border: `1px solid ${previewSettings.widgetBorderColor || '#e1e5e9'}`
                                                            }}>
                                                               

                                                                <h4 style={{
                                                                    fontSize: `${previewSettings.headerFontSize || 16}px`,
                                                                    fontWeight: 'bold',
                                                                    margin: '0 0 16px 0',
                                                                    color: previewSettings.headerColor
                                                                }}>
                                                                    {decodeForPreview(previewSettings.whenLiveTitle || 'DROP IS LIVE! SELLING FAST 🔥')}
                                                                </h4>

                                                                {/* Progress Bar */}
                                                                <div style={{ marginBottom: '16px' }}>
                                                                        {(() => {
                                                                            // Use live form values directly from campaign API response
                                                                            const sold = formik.values.current_items_sold || 0;
                                                                            const totalLimit = formik.values.totalDropLimit ? parseInt(formik.values.totalDropLimit) : 1000; // Default to 1000 if not set
                                                                            const progressPercent = totalLimit > 0 
                                                                                ? Math.min(Math.round((sold / totalLimit) * 100), 100) 
                                                                                : 0;
                                                                        
                                                                        return (
                                                                            <>
                                                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                                                                                    <div style={{ flex: 1 }}>
                                                                                        <div style={{
                                                                                            width: '100%',
                                                                                            height: '8px',
                                                                                            backgroundColor: previewSettings.progressBarBackgroundColor || '#e1e5e9',
                                                                                            borderRadius: '4px',
                                                                                            overflow: 'hidden'
                                                                                        }}>
                                                                                            <div style={{
                                                                                                width: `${Math.min(progressPercent, 100)}%`,
                                                                                                height: '100%',
                                                                                                backgroundColor: previewSettings.progressBarColor || '#000000',
                                                                                                transition: 'width 0.3s ease'
                                                                                            }} />
                                                                                        </div>
                                                                                    </div>
                                                                                    <span style={{ fontWeight: '600', fontSize: `${previewSettings.primaryFontSize || 14}px`, color: previewSettings.textColor }}>
                                                                                        {decodeForPreview(previewSettings.claimedPercentageText || '{percentage}% Claimed').replace('{percentage}', progressPercent.toString())}
                                                                                    </span>
                                                                                </div>
                                                                                <p style={{
                                                                                    margin: 0,
                                                                                    color: previewSettings.subtitleColor,
                                                                                    fontSize: `${previewSettings.primaryFontSize || 14}px`
                                                                                }}>
                                                                                    {decodeForPreview(previewSettings.raisedText || 'Sold: {raised}').replace('{raised}', sold.toString())} / {decodeForPreview(previewSettings.goalText || 'Limited: {goal}').replace('{goal}', totalLimit.toString())}
                                                                                </p>
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </div>

                                                                {/* Buy Now Button */}
                                                                <button 
                                                                    onClick={(e) => e.preventDefault()}
                                                                    style={{
                                                                        border: `2px solid ${previewSettings.buttonBackgroundColor || '#000000'}`,
                                                                        backgroundColor: previewSettings.buttonBackgroundColor || '#000000',
                                                                        color: previewSettings.buttonTextColor || '#ffffff',
                                                                        borderRadius: previewSettings.buttonStyle === 'pill' ? '50px' :
                                                                            previewSettings.buttonStyle === 'square' ? '0px' :
                                                                                `${previewSettings.widgetBorderRadius || 8}px`,
                                                                        padding: '5px 16px',
                                                                        fontSize: `${previewSettings.buttonFontSize || 14}px`,
                                                                        fontWeight: '600',
                                                                        cursor: 'default',
                                                                        width: '100%',
                                                                        marginBottom: '12px'
                                                                    }}>
                                                                    {decodeForPreview(previewSettings.whenLiveButton || 'BUY NOW ⚡')}
                                                                </button>

                                                                {/* Purchase Limit */}
                                                                <p style={{
                                                                    margin: 0,
                                                                    color: previewSettings.subtitleColor,
                                                                    fontSize: `${previewSettings.secondaryFontSize || 13}px`
                                                                }}>
                                                                    {decodeForPreview(previewSettings.whenLiveLimit || 'Limited to {limit} per customer.').replace('{limit}', formik.values.perCustomerLimit || '1')}
                                                                </p>
                                                            </div>

                                                            <p style={{
                                                                    fontSize: '12px',
                                                                    fontWeight: '600',
                                                                    margin: '0 0 12px 0',
                                                                }}>
                                                                    Sale ended
                                                                </p>
                                                            {/* After Live State */}
                                                            <div style={{
                                                                backgroundColor: previewSettings.backgroundColor,
                                                                borderRadius: `${previewSettings.widgetBorderRadius}px`,
                                                                padding: '16px',
                                                                marginBottom: '16px',
                                                                border: `1px solid ${previewSettings.widgetBorderColor || '#e1e5e9'}`
                                                            }}>
                                                                

                                                                <h4 style={{
                                                                    fontSize: `${previewSettings.headerFontSize || 16}px`,
                                                                    fontWeight: 'bold',
                                                                    margin: '0 0 12px 0',
                                                                    color: previewSettings.afterLiveTitleColor || '#ef4444'
                                                                }}>
                                                                    {decodeForPreview(previewSettings.afterLiveTitle || 'SOLD OUT!')}
                                                                </h4>

                                                                <p style={{
                                                                    margin: '0 0 12px 0',
                                                                    color: previewSettings.textColor,
                                                                    fontSize: `${previewSettings.secondaryFontSize || 13}px`
                                                                }}>
                                                                    {decodeForPreview(previewSettings.afterLiveMessage || 'All units are sold out. Thank you for the incredible response!')}
                                                                </p>

                                                                {/* Notify for Restock Button */}
                                                                <button 
                                                                    onClick={(e) => e.preventDefault()}
                                                                    style={{
                                                                        border: `2px solid ${previewSettings.buttonBackgroundColor || '#000000'}`,
                                                                        backgroundColor: previewSettings.buttonBackgroundColor || '#000000',
                                                                        color: previewSettings.buttonTextColor || '#ffffff',
                                                                        borderRadius: previewSettings.buttonStyle === 'pill' ? '50px' :
                                                                            previewSettings.buttonStyle === 'square' ? '0px' :
                                                                                `${previewSettings.widgetBorderRadius || 8}px`,
                                                                        padding: '5px 16px',
                                                                        fontSize: `${previewSettings.buttonFontSize || 14}px`,
                                                                        fontWeight: '600',
                                                                        cursor: 'default',
                                                                        width: '100%',
                                                                        marginBottom: '12px'
                                                                    }}>
                                                                    {decodeForPreview(previewSettings.afterLiveButton || 'NOTIFY ME FOR RESTOCK')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : formik.values.campaign_type === '1' ? (
                                                        <div className="preview-widget" style={{
                                                            fontSize: '13px',
                                                            backgroundColor: previewSettings.backgroundColor,
                                                            padding: '16px',
                                                            borderRadius: `${previewSettings.widgetBorderRadius}px`,
                                                            color: previewSettings.textColor,
                                                            border: `1px solid ${previewSettings.widgetBorderColor || '#e1e5e9'}`
                                                        }}>
                                                            {/* Campaign Title */}
                                                            <h3 style={{
                                                                color: previewSettings.headerColor,
                                                                fontSize: `${previewSettings.headerFontSize || 16}px`,
                                                                fontWeight: 'bold',
                                                                margin: '0 0 16px 0'
                                                            }}>
                                                                {formik.values.campaign_name || 'Demo Funding'}
                                                            </h3>

                                                            {/* Funding Progress */}
                                                            <div style={{ marginBottom: '16px' }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                                    <span style={{ fontWeight: '600', fontSize: `${previewSettings.primaryFontSize || 14}px` }}>
                                                                        {(() => {
                                                                            // Use live form values directly from campaign API response
                                                                            const raisedAmount = formik.values.goal_type === '1'
                                                                                ? (formik.values.addStartPoint ? (formik.values.initial_fund || 0) : 0)
                                                                                : (formik.values.addStartPoint ? (formik.values.initial_backers || 0) : 0);
                                                                            return formik.values.goal_type === '1'
                                                                                ? decodeForPreview(previewSettings.raisedText || 'Raised: {raised}').replace('{raised}', formatCurrency(raisedAmount))
                                                                                : decodeForPreview(previewSettings.raisedText || 'Raised: {raised}').replace('{raised}', raisedAmount.toString());
                                                                        })()}
                                                                    </span>
                                                                    <span style={{ fontWeight: '600', fontSize: `${previewSettings.primaryFontSize || 14}px` }}>
                                                                        {formik.values.goal_type === '1'
                                                                            ? decodeForPreview(previewSettings.goalText || 'Goal: {goal}').replace('{goal}', formatCurrency(formik.values.total_amount || 0))
                                                                            : decodeForPreview(previewSettings.goalText || 'Goal: {goal}').replace('{goal}', (formik.values.number_of_items || 0).toString())
                                                                        }
                                                                    </span>
                                                                </div>

                                                                {/* Progress Bar */}
                                                                <div style={{
                                                                    width: '100%',
                                                                    height: '8px',
                                                                    backgroundColor: previewSettings.progressBarBackgroundColor || '#e1e5e9',
                                                                    borderRadius: '4px',
                                                                    overflow: 'hidden',
                                                                    marginBottom: '12px'
                                                                }}>
                                                                    <div style={{
                                                                        width: `${Math.min(progressValue, 100)}%`,
                                                                        height: '100%',
                                                                        backgroundColor: previewSettings.progressBarColor || '#000000',
                                                                        transition: 'width 0.3s ease'
                                                                    }} />
                                                                </div>

                                                                <p style={{
                                                                    color: previewSettings.subtitleColor,
                                                                    fontSize: `${previewSettings.secondaryFontSize || 13}px`,
                                                                    margin: '0 0 12px 0'
                                                                }}>
                                                                    {decodeForPreview(previewSettings.progressMessage || "You've reached the target! Keep contributing to unlock even more amazing rewards!")}
                                                                </p>

                                                                {/* Progress Stats */}
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                                    <span style={{ fontWeight: '600', fontSize: `${previewSettings.primaryFontSize || 14}px` }}>
                                                                        {decodeForPreview(previewSettings.fundedText || '{percentage}% funded').replace('{percentage}', progressValue.toFixed(2))}
                                                                    </span>
                                                                    <span style={{ fontWeight: '600', fontSize: `${previewSettings.primaryFontSize || 14}px` }}>
                                                                        {decodeForPreview(previewSettings.backerText || 'Backers: {backers}').replace('{backers}', (backersValue ?? 0).toString())}
                                                                    </span>
                                                                </div>

                                                                {/* Time Remaining - Only show if end date is set */}
                                                                {formik.values.showEndDateCrowdfunding && formik.values.campaign_end_datetime && (
                                                                    <div style={{ marginBottom: '16px' }}>
                                                                        <span style={{
                                                                            color: previewSettings.subtitleColor,
                                                                            fontSize: `${previewSettings.secondaryFontSize || 13}px`
                                                                        }}>
                                                                            {formatTimeRemainingText(decodeForPreview(previewSettings.timeLeftText || 'Time left: {days} day, {hours} hour, {minutes} minutes'))}
                                                                        </span>
                                                                    </div>  
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : formik.values.campaign_type === '0' ? (
                                                        <div className="preview-widget" style={{
                                                            fontSize: '13px',
                                                            padding: '16px'
                                                        }}>

                                                            <p style={{
                                                                    fontSize: '12px',
                                                                    fontWeight: '600',
                                                                    margin: '0 0 12px 0',
                                                                }}>
                                                                    When live
                                                                </p>
                                                            {/* Pre-Sale Is Live State */}
                                                            <div style={{
                                                                backgroundColor: previewSettings.backgroundColor,
                                                                border: `1px solid ${previewSettings.widgetBorderColor || '#e1e5e9'}`,
                                                                borderRadius: `${previewSettings.widgetBorderRadius}px`,
                                                                padding: '16px',
                                                                marginBottom: '16px'
                                                            }}>
                                                                

                                                                <h4 style={{
                                                                    fontSize: `${previewSettings.headerFontSize || 16}px`,
                                                                    fontWeight: 'bold',
                                                                    margin: '0 0 16px 0',
                                                                    color: previewSettings.headerColor
                                                                }}>
                                                                    {decodeForPreview(previewSettings.whenLiveTitle || 'PRE-SALE IS LIVE! 🔥')}
                                                                </h4>

                                                                {/* Progress Bar - Only show when limitInventory is checked */}
                                                                {formik.values.limitInventory && formik.values.availableInventory > 0 && (
                                                                    <div style={{ marginBottom: '16px' }}>
                                                                        {(() => {
                                                                            // Use live form values directly from campaign API response
                                                                            const currentSold = formik.values.current_items_sold || 0;
                                                                            const initialSold = formik.values.initial_items_sold_preSale || 0;
                                                                            const sold = currentSold + initialSold;
                                                                            const available = formik.values.availableInventory || 1;
                                                                            const progressPercent = Math.min(Math.round((sold / available) * 100), 100);
                                                                            
                                                                            return (
                                                                                <>
                                                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                                                                                        <div style={{ flex: 1 }}>
                                                                                            <div style={{
                                                                                                width: '100%',
                                                                                                height: '8px',
                                                                                                backgroundColor: previewSettings.progressBarBackgroundColor || '#e1e5e9',
                                                                                                borderRadius: '4px',
                                                                                                overflow: 'hidden'
                                                                                            }}>
                                                                                                <div style={{
                                                                                                    width: `${Math.min(progressPercent, 100)}%`,
                                                                                                    height: '100%',
                                                                                                    backgroundColor: previewSettings.progressBarColor || '#000000',
                                                                                                    transition: 'width 0.3s ease'
                                                                                                }} />
                                                                                            </div>
                                                                                        </div>
                                                                                        <span style={{ fontWeight: '600', fontSize: `${previewSettings.primaryFontSize || 14}px`, color: previewSettings.textColor }}>
                                                                                            {decodeForPreview(previewSettings.claimedPercentageText || '{percentage}% Claimed').replace('{percentage}', progressPercent.toString())}
                                                                                        </span>
                                                                                    </div>
                                                                                    {formik.values.showPresalesTarget && (
                                                                                        <p style={{
                                                                                            margin: 0,
                                                                                            color: previewSettings.subtitleColor,
                                                                                            fontSize: `${previewSettings.primaryFontSize || 14}px`
                                                                                        }}>
                                                                                            {decodeForPreview(previewSettings.preOrderText || 'Pre-orders: {raised} / Target: {goal}').replace('{raised}', sold.toString()).replace('{goal}', available.toString())}
                                                                                        </p>
                                                                                    )}
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                )}

                                                                {/* Shop Pre-Sale Button */}
                                                                <button 
                                                                    onClick={(e) => e.preventDefault()}
                                                                    style={{
                                                                        border: `2px solid ${previewSettings.buttonBackgroundColor || '#000000'}`,
                                                                        backgroundColor: previewSettings.buttonBackgroundColor || '#000000',
                                                                        color: previewSettings.buttonTextColor || '#ffffff',
                                                                        borderRadius: previewSettings.buttonStyle === 'pill' ? '50px' :
                                                                            previewSettings.buttonStyle === 'square' ? '0px' :
                                                                                `${previewSettings.widgetBorderRadius || 8}px`,
                                                                        padding: '5px 16px',
                                                                        fontSize: `${previewSettings.buttonFontSize || 14}px`,
                                                                        fontWeight: '600',
                                                                        cursor: 'default',
                                                                        width: '100%',
                                                                        marginBottom: '12px'
                                                                    }}>
                                                                    {decodeForPreview(previewSettings.whenLiveButton || 'SHOP THE PRE-SALE')}
                                                                </button>
                                                            </div>

                                                            <p style={{
                                                                    fontSize: '12px',
                                                                    fontWeight: '600',
                                                                    margin: '0 0 12px 0',
                                                                }}>
                                                                    Sale ended
                                                                </p>
                                                            {/* Pre-Sale Has Ended State */}
                                                            <div style={{
                                                                backgroundColor: previewSettings.backgroundColor,
                                                                borderRadius: `${previewSettings.widgetBorderRadius}px`,
                                                                padding: '16px',
                                                                marginBottom: '16px',
                                                                border: `1px solid ${previewSettings.widgetBorderColor || '#e1e5e9'}`
                                                            }}>
                                                                

                                                                <h4 style={{
                                                                    fontSize: `${previewSettings.headerFontSize || 16}px`,
                                                                    fontWeight: 'bold',
                                                                    margin: '0 0 12px 0',
                                                                    color: previewSettings.afterLiveTitleColor || '#ef4444'
                                                                }}>
                                                                    {decodeForPreview(previewSettings.afterLiveTitle || 'SOLD OUT!')}
                                                                </h4>

                                                                <p style={{
                                                                    margin: '0 0 12px 0',
                                                                    color: previewSettings.textColor,
                                                                    fontSize: `${previewSettings.secondaryFontSize || 13}px`
                                                                }}>
                                                                    {decodeForPreview(previewSettings.afterLiveMessage || 'All units are sold out. Thank you for the incredible response!')}
                                                                </p>

                                                                {/* Notify for Restock Button */}
                                                                <button 
                                                                    onClick={(e) => e.preventDefault()}
                                                                    style={{
                                                                        border: `2px solid ${previewSettings.buttonBackgroundColor || '#000000'}`,
                                                                        backgroundColor: previewSettings.buttonBackgroundColor || '#000000',
                                                                        color: previewSettings.buttonTextColor || '#ffffff',
                                                                        borderRadius: previewSettings.buttonStyle === 'pill' ? '50px' :
                                                                            previewSettings.buttonStyle === 'square' ? '0px' :
                                                                                `${previewSettings.widgetBorderRadius || 8}px`,
                                                                        padding: '5px 16px',
                                                                        fontSize: `${previewSettings.buttonFontSize || 14}px`,
                                                                        fontWeight: '600',
                                                                        cursor: 'default',
                                                                        width: '100%',
                                                                        marginBottom: '12px'
                                                                    }}>
                                                                    {decodeForPreview(previewSettings.afterLiveButton || 'NOTIFY ME FOR RESTOCK')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="preview-widget" style={{
                                                            fontSize: '13px',
                                                            padding: '16px',
                                                            borderRadius: '8px',
                                                            border: `1px solid ${previewSettings.widgetBorderColor || '#e1e5e9'}`
                                                        }}>
                                                            <h4 style={{
                                                                fontSize: '13px',
                                                                fontWeight: '600',
                                                                margin: '0 0 16px 0',
                                                                color: previewSettings.headerColor
                                                            }}>
                                                                Campaign Details
                                                            </h4>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span style={{ fontSize: '12px', color: previewSettings.subtitleColor }}>Campaign Name:</span>
                                                                    <span style={{ fontSize: '13px', fontWeight: '600', color: previewSettings.textColor }}>
                                                                        {formik.values.campaign_name || 'Untitled Campaign'}
                                                                    </span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span style={{ fontSize: '12px', color: previewSettings.subtitleColor }}>Campaign Type:</span>
                                                                    <span style={{ fontSize: '13px', fontWeight: '600', color: previewSettings.textColor }}>
                                                                        {getCampaignType(formik.values.campaign_type)}
                                                                    </span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span style={{ fontSize: '12px', color: previewSettings.subtitleColor }}>Campaign Status:</span>
                                                                    <span style={{ fontSize: '13px', fontWeight: '600', color: previewSettings.textColor }}>
                                                                        {getCampaignStatus('1')}
                                                                    </span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span style={{ fontSize: '12px', color: previewSettings.subtitleColor }}>Goal Type:</span>
                                                                    <span style={{ fontSize: '13px', fontWeight: '600', color: previewSettings.textColor }}>
                                                                        {formik.values.goal_type === '0' ? 'Total Items Sold' : 'Funding Amount'}
                                                                    </span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span style={{ fontSize: '12px', color: previewSettings.subtitleColor }}>Initial Fund:</span>
                                                                    <span style={{ fontSize: '13px', fontWeight: '600', color: previewSettings.textColor }}>
                                                                        {formatCurrency(formik.values.initial_fund)}
                                                                    </span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span style={{ fontSize: '12px', color: previewSettings.subtitleColor }}>Products:</span>
                                                                    <span style={{ fontSize: '13px', fontWeight: '600', color: previewSettings.textColor }}>
                                                                        {formik.values.products?.length || 0} selected
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Rewards Widget Preview for Crowdfunding */}
                                                    {formik.values.campaign_type === '1' && (formik.values.rewards?.length || 0) > 0 && (
                                                        
                                                            <BlockStack gap="400">
                                                                <Text variant="headingMd" as="h4" fontWeight="semibold">
                                                                    Rewards Preview
                                                                </Text>

                                                                {(formik.values.rewards || []).map(tier => (
                                                                    <div key={tier.id} style={{
                                                                        backgroundColor: previewSettings.rewardCardBackgroundColor || '#ffffff',
                                                                        borderRadius: `${previewSettings.widgetBorderRadius || 8}px`,
                                                                        padding: '16px',
                                                                        marginBottom: '16px',
                                                                        border: `1px solid ${previewSettings.widgetBorderColor || '#e1e5e9'}`
                                                                    }}>
                                                                        <BlockStack gap="200">
                                                                            {/* Reward Image */}
                                                                            {tier.reward_image ? (
                                                                                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                                                                                    <img
                                                                                        src={tier.reward_image}
                                                                                        alt={tier.reward_title}
                                                                                        style={{
                                                                                            width: '120px',
                                                                                            height: '120px',
                                                                                            margin: '0 auto',
                                                                                            objectFit: 'cover',
                                                                                            borderRadius: '8px',
                                                                                            backgroundColor: '#f6f6f7',
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            ) : (
                                                                                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                                                                                    <div style={{
                                                                                        width: '120px',
                                                                                        height: '120px',
                                                                                        margin: '0 auto',
                                                                                        borderRadius: '8px',
                                                                                        backgroundColor: '#f6f6f7',
                                                                                        border: `1px solid ${previewSettings.rewardCardBorderColor || '#e1e5e9'}`,
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        fontSize: '12px',
                                                                                        color: previewSettings.rewardDescriptionColor || '#6b7280'
                                                                                    }}>
                                                                                        Reward Image
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {/* Reward Title */}
                                                                            <h4 style={{ 
                                                                                color: previewSettings.rewardTitleColor || '#000000',
                                                                                fontSize: `${previewSettings.headerFontSize || 16}px`,
                                                                                fontWeight: 'bold',
                                                                                margin: '0 0 8px 0'
                                                                            }}>
                                                                                {tier.reward_title || decodeForPreview(previewSettings.rewardTitle || 'Premium Bundle')}
                                                                            </h4>

                                                                            {/* Pricing */}
                                                                            {(() => {
                                                                                // Calculate total product price for strikethrough
                                                                                const calculateTotalProductPrice = () => {
                                                                                    if (!tier.products || tier.products.length === 0) return 0;
                                                                                    return tier.products.reduce((total, product) => {
                                                                                        if (product.variants && product.variants.length > 0) {
                                                                                            // Sum all variant prices
                                                                                            const variantTotal = product.variants.reduce((sum, variant) => {
                                                                                                return sum + parseFloat(variant.price || 0);
                                                                                            }, 0);
                                                                                            return total + variantTotal;
                                                                                        } else if (product.product_price) {
                                                                                            // Fallback to product_price if variants not available
                                                                                            return total + parseFloat(product.product_price || 0);
                                                                                        }
                                                                                        return total;
                                                                                    }, 0);
                                                                                };
                                                                                
                                                                                const totalProductPrice = calculateTotalProductPrice();
                                                                                const bundlePrice = tier.reward_price ? parseFloat(tier.reward_price) : 0;
                                                                                const showStrikethrough = tier.show_strikethrough_price && totalProductPrice > 0 && totalProductPrice > bundlePrice;
                                                                                
                                                                                return (
                                                                                    <div style={{ margin: '0 0 8px 0' }}>
                                                                                        {showStrikethrough ? (
                                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                                                                <span style={{ 
                                                                                                    color: previewSettings.rewardStrikethroughPriceColor || previewSettings.rewardPriceColor || '#000000',
                                                                                                    fontSize: `${previewSettings.primaryFontSize || 14}px`,
                                                                                                    fontWeight: 'bold',
                                                                                                    textDecoration: 'line-through',
                                                                                                    opacity: 0.6
                                                                                                }}>
                                                                                                    {formatCurrency(totalProductPrice)}
                                                                                                </span>
                                                                                                <span style={{ 
                                                                                                    color: previewSettings.rewardPriceColor || '#000000',
                                                                                                    fontSize: `${previewSettings.primaryFontSize || 14}px`,
                                                                                                    fontWeight: 'bold'
                                                                                                }}>
                                                                                                    {formatCurrency(bundlePrice)}
                                                                                                </span>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <p style={{ 
                                                                                                color: previewSettings.rewardPriceColor || '#000000',
                                                                                                fontSize: '15px',
                                                                                                fontWeight: 'bold',
                                                                                                margin: 0
                                                                                            }}>
                                                                                                {formatCurrency(bundlePrice)}
                                                                                            </p>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })()}

                                                                            {/* Description */}
                                                                            <p style={{ 
                                                                                color: previewSettings.rewardDescriptionColor || '#6b7280',
                                                                                fontSize: `${previewSettings.secondaryFontSize || 13}px`,
                                                                                margin: '0 0 12px 0',
                                                                                lineHeight: '1.5'
                                                                            }}>
                                                                                {tier.reward_description || decodeForPreview(previewSettings.rewardDescription || 'Perfect for professionals. Get more with less.')}
                                                                            </p>

                                                                            {/* Included Items */}
                                                                            <div style={{ marginBottom: '12px' }}>
                                                                                <p style={{ 
                                                                                    fontSize: '12px',
                                                                                    fontWeight: '600',
                                                                                    margin: '0 0 8px 0',
                                                                                    color: previewSettings.textColor
                                                                                }}>
                                                                                    {decodeForPreview(previewSettings.rewardIncludedLabel || 'Included')}
                                                                                </p>
                                                                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                                                    {tier.products && tier.products.length > 0 ? (
                                                                                        tier.products.filter(product => product && product.product_title).map((product, index) => (
                                                                                            <div
                                                                                                key={`${product.shopify_product_id || product.id}-${index}`}
                                                                                                style={{
                                                                                                    width: '32px',
                                                                                                    height: '32px',
                                                                                                    borderRadius: '6px',
                                                                                                    overflow: 'hidden',
                                                                                                    border: '1px solid #d1d5db',
                                                                                                    backgroundColor: '#e1e5e9',
                                                                                                    display: 'flex',
                                                                                                    alignItems: 'center',
                                                                                                    justifyContent: 'center'
                                                                                                }}
                                                                                            >
                                                                                                {product.product_image ? (
                                                                                                    <img
                                                                                                        src={product.product_image}
                                                                                                        alt={product.product_title || 'Product'}
                                                                                                        style={{
                                                                                                            width: '100%',
                                                                                                            height: '100%',
                                                                                                            objectFit: 'cover'
                                                                                                        }}
                                                                                                    />
                                                                                                ) : (
                                                                                                    <span style={{
                                                                                                        fontSize: '12px',
                                                                                                        color: '#6b7280'
                                                                                                    }}>
                                                                                                        ?
                                                                                                    </span>
                                                                                                )}
                                                                                            </div>
                                                                                        ))
                                                                                    ) : (
                                                                                        <span style={{ fontSize: '12px', color: previewSettings.subtitleColor }}>
                                                                                            No products selected
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>

                                                                            {/* Action Buttons */}
                                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                                <button 
                                                                                    onClick={(e) => e.preventDefault()}
                                                                                    style={{
                                                                                        backgroundColor: previewSettings.rewardSeeDetailsButtonBackgroundColor || '#ffffff',
                                                                                        color: previewSettings.rewardSeeDetailsButtonColor || '#000000',
                                                                                        border: 'none',
                                                                                        borderRadius: `${previewSettings.widgetBorderRadius || 8}px`,
                                                                                        padding: '6px 12px',
                                                                                        fontSize: `${previewSettings.buttonFontSize || 14}px`,
                                                                                        fontWeight: '600',
                                                                                        cursor: 'default',
                                                                                        flex: 1,
                                                                                        transition: 'all 0.2s ease'
                                                                                    }}>
                                                                                    {decodeForPreview(previewSettings.rewardSeeDetailsButton || 'See details')}
                                                                                </button>
                                                                                <button 
                                                                                    onClick={(e) => e.preventDefault()}
                                                                                    style={{
                                                                                        backgroundColor: previewSettings.rewardClaimButtonColor || '#000000',
                                                                                        color: previewSettings.rewardClaimButtonTextColor || '#ffffff',
                                                                                        border: 'none',
                                                                                        borderRadius: `${previewSettings.widgetBorderRadius || 8}px`,
                                                                                        padding: '6px 12px',
                                                                                        fontSize: `${previewSettings.buttonFontSize || 14}px`,
                                                                                        fontWeight: '600',
                                                                                        cursor: 'default',
                                                                                        flex: 1,
                                                                                        transition: 'all 0.2s ease'
                                                                                    }}>
                                                                                    {decodeForPreview(previewSettings.rewardClaimButton || 'Claim this perk')}
                                                                                </button>
                                                                            </div>
                                                                        </BlockStack>
                                                                    </div>
                                                                ))}
                                                            </BlockStack>
                                                    )}

                                                </BlockStack>
                                            </Card>
                                        </div>
                                    </BlockStack>
                                </Box>
                            </Box>
                        </InlineStack>
                    </BlockStack>
                </form>

                {showToast && (
                    <Toast
                        content={toastMessage}
                        onDismiss={() => setShowToast(false)}
                    />
                )}

                {/* Navigation Confirmation Modal */}
                <Modal
                    open={showNavigationModal}
                    onClose={handleCancelNavigation}
                    title="Unsaved changes"
                    primaryAction={{
                        content: 'Discard changes',
                        destructive: true,
                        onAction: handleConfirmNavigation,
                    }}
                    secondaryActions={[
                        {
                            content: 'Cancel',
                            onAction: handleCancelNavigation,
                        },
                    ]}
                >
                    <Modal.Section>
                        <Text as="p">
                            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
                        </Text>
                    </Modal.Section>
                </Modal>
                
                {/* SaveBar Component - Shows when form has unsaved changes */}
                <SaveBar
                    id="campaign-edit-save-bar"
                    open={saveBarVisible && dataLoaded}
                    discardConfirmation={true}
                >
                    <button
                        variant="primary"
                        onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            await handleSave(formik.values);
                        }}
                        disabled={saving || !dataLoaded}
                        loading={saving ? '' : undefined}
                    >
                        Save
                    </button>
                    <button
                        onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            await handleDiscard();
                        }}
                        disabled={discarding || !dataLoaded}
                    >
                        Discard
                    </button>
                </SaveBar>
            </Page>
        </>
    );
}

export default function CampaignEdit() {
    // Get install state from Redux for early return check
    const {
        checked: installChecked,
        success: installSuccess,
        loading: installLoading,
        installData
    } = useSelector((state) => state.install);

    // Don't render anything if install check failed (redirect is happening)
    if (installChecked && !installSuccess && !installData) {
        return null;
    }

    // OPTIMIZATION: Show UI immediately after install check completes
    // Don't wait for installLoading - show UI as soon as installChecked is true and we have data
    // This allows UI to render while campaign data and settings load in background
    if (!installChecked || (!installData && installLoading)) {
        return null;
    }

    // Show UI immediately - data will load in background via useEffect in CampaignEditContent
    return <CampaignEditContent />;
}

